import React, { useState, useRef, useEffect } from 'react';
import { useAuthContext } from '../../../context/AuthContext';
import apiService from '../../../services/api';
import { CATEGORIES, SUB_CATEGORIES } from '../../../utils/constants';
import { checkUserPromotion, consumePromotionSlot, calculatePromotionPrice } from '../../../utils/promotionApi'; // ✅ NEW
import styles from './PostAdModal.module.css';
import PaymentModal from './PaymentModal';

const ROOMS_CAT      = 'Rooms to Let';
const LOST_FOUND_CAT = 'Lost & Found';

// ── Location search via OpenStreetMap Nominatim ───────────────────────────
const searchKenyaPlaces = async (query) => {
  if (!query || query.trim().length < 2) return [];
  try {
    const url =
      `https://nominatim.openstreetmap.org/search` +
      `?q=${encodeURIComponent(query + ', Kenya')}` +
      `&countrycodes=ke&limit=12&format=json&addressdetails=1&accept-language=en`;
    const res = await fetch(url, {
      headers: { 'User-Agent': 'ImSellingKE/1.0 (marketplace)' }
    });
    const data = await res.json();
    return data.map(item => {
      const a = item.address;
      const label =
        a.suburb || a.neighbourhood || a.quarter || a.village ||
        a.hamlet || a.town || a.city_district || a.city ||
        item.display_name.split(',')[0];
      const parent =
        a.suburb        ? (a.town || a.city || a.county) :
        a.village       ? (a.town || a.city || a.county) :
        a.hamlet        ? (a.town || a.city || a.county) :
        a.neighbourhood ? (a.city || a.county)           : null;
      const display = parent ? `${label.trim()}, ${parent.trim()}` : label.trim();
      return { label: label.trim(), display, osm_id: item.osm_id };
    }).filter((v, i, arr) => arr.findIndex(x => x.display === v.display) === i);
  } catch (e) {
    console.error('Location search error:', e);
    return [];
  }
};

const Field = ({ label, required, children }) => (
  <div className={styles.formGroup}>
    <label>{label} {required && <span className={styles.req}>*</span>}</label>
    {children}
  </div>
);

// ── CityPicker ────────────────────────────────────────────────────────────
const CityPicker = ({ value, onChange, placeholder = 'Search village, estate, street, town...' }) => {
  const [query,     setQuery]     = useState(value || '');
  const [results,   setResults]   = useState([]);
  const [open,      setOpen]      = useState(false);
  const [active,    setActive]    = useState(-1);
  const [loading,   setLoading]   = useState(false);
  const [confirmed, setConfirmed] = useState(!!value);
  const wrapRef  = useRef(null);
  const inputRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    const fn = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', fn);
    return () => document.removeEventListener('mousedown', fn);
  }, []);

  const runSearch = (q) => {
    clearTimeout(timerRef.current);
    if (!q || q.trim().length < 2) { setResults([]); setLoading(false); return; }
    setLoading(true);
    timerRef.current = setTimeout(async () => {
      const found = await searchKenyaPlaces(q);
      setResults(found);
      setOpen(found.length > 0);
      setActive(0);
      setLoading(false);
    }, 400);
  };

  const select = (item) => {
    setQuery(item.display);
    onChange(item.display);
    setConfirmed(true);
    setOpen(false);
    setResults([]);
  };

  const clear = () => {
    setQuery(''); onChange('');
    setConfirmed(false); setResults([]); setOpen(false);
    clearTimeout(timerRef.current);
    inputRef.current?.focus();
  };

  const handleInput = (e) => {
    const v = e.target.value;
    setQuery(v);
    setConfirmed(false);
    onChange('');
    runSearch(v);
  };

  const handleKey = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive(i => Math.min(i + 1, results.length - 1)); }
    if (e.key === 'ArrowUp')   { e.preventDefault(); setActive(i => Math.max(i - 1, 0)); }
    if (e.key === 'Enter')     { e.preventDefault(); if (results[active]) select(results[active]); }
    if (e.key === 'Escape')    { setOpen(false); }
  };

  return (
    <div ref={wrapRef} className={styles.cityWrap}>
      <div className={styles.cityInputWrap}>
        <span className={styles.citySearchIcon}>🔍</span>
        <input
          ref={inputRef}
          type="text"
          className={`${styles.inputField} ${styles.cityInput} ${confirmed ? styles.cityInputConfirmed : ''}`}
          placeholder={placeholder}
          value={query}
          onChange={handleInput}
          onFocus={() => results.length > 0 && setOpen(true)}
          onKeyDown={handleKey}
          autoComplete="off"
          spellCheck="false"
        />
        {loading && <span className={styles.citySpinner} title="Searching...">⏳</span>}
        {!loading && query && (
          <button type="button" className={styles.cityClear} onClick={clear}>×</button>
        )}
      </div>
      {confirmed && <p className={styles.cityConfirmedMsg}>✅ Location set</p>}
      {!confirmed && query.length === 1 && (
        <p className={styles.cityHint}>Type one more character to search…</p>
      )}
      {open && results.length > 0 && (
        <ul className={styles.cityDropdown}>
          {results.map((item, i) => (
            <li
              key={`${item.osm_id}-${i}`}
              className={`${styles.cityOption} ${i === active ? styles.cityOptionActive : ''}`}
              onMouseDown={() => select(item)}
              onMouseEnter={() => setActive(i)}
            >
              <span className={styles.cityPin}>📍</span>
              <span>
                <span className={styles.cityOptionLabel}>{item.label}</span>
                {item.display !== item.label && (
                  <span className={styles.cityOptionSub}>
                    {', ' + item.display.replace(item.label + ', ', '')}
                  </span>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}
      {!loading && open && results.length === 0 && query.length >= 2 && (
        <div className={styles.cityNoResult}>
          No results for "<strong>{query}</strong>" — try a nearby street or area name
        </div>
      )}
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// ROOMS TO LET FORM
// ══════════════════════════════════════════════════════════════════════════════
const RoomsForm = ({ maxImages, maxVideos, planName, onSubmit, submitState = 'idle' }) => {
  const [title,        setTitle]        = useState('');
  const [location,     setLocation]     = useState('');
  const [price,        setPrice]        = useState('');
  const [priceDisplay, setPriceDisplay] = useState('');
  const [rentalPeriod, setRentalPeriod] = useState('month'); // ✅ NEW — rental period dropdown
  const [negotiable,   setNegotiable]   = useState('');
  const [propType,     setPropType]     = useState('');
  const [furnished,    setFurnished]    = useState('');
  const [availFrom,    setAvailFrom]    = useState('');
  const [description,  setDescription]  = useState('');
  const [contact,      setContact]      = useState('');
  const [images,       setImages]       = useState([]);
  const [videos,       setVideos]       = useState([]);

  const handleRoomPrice = (e) => {
    const raw = e.target.value.replace(/,/g, '').replace(/[^\d]/g, '');
    setPrice(raw);
    setPriceDisplay(raw ? Number(raw).toLocaleString('en-KE') : '');
  };

  const handleImages = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > maxImages)
      alert(`Only the first ${maxImages} photo${maxImages > 1 ? 's' : ''} will be uploaded. The rest will be ignored.`);
    setImages(files.slice(0, maxImages));
  };

  const handleVideos = (e) => {
    if (maxVideos === 0) return alert('Video upload requires Business plan');
    const all = Array.from(e.target.files);
    const valid = all.filter(f => f.size <= 50*1024*1024);
    const oversized = all.length - valid.length;
    const overCount = valid.length - maxVideos;
    if (oversized > 0) alert(`${oversized} video${oversized > 1 ? 's were' : ' was'} removed (exceeds 50MB limit).`);
    if (overCount > 0) alert(`Only ${maxVideos} video${maxVideos > 1 ? 's' : ''} allowed. The rest will be ignored.`);
    setVideos(valid.slice(0, maxVideos));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!title)            return alert('Please enter a title');
    if (title.length > 25) return alert('Title must be 25 characters or less');
    if (!location)         return alert('Please select a location');
    if (!price)            return alert('Please enter rental price');
    if (!negotiable)       return alert('Please select price type');
    if (!propType)         return alert('Please select property type');
    if (!furnished)        return alert('Please select furnished option');
    if (!availFrom)        return alert('Please select availability date');
    if (!description)      return alert('Please add a description');
    if (!contact)          return alert('Please enter contact info');

    const periodLabels = { hour: 'Hour', day: 'Day', week: 'Week', month: 'Month', year: 'Year' };
    const periodLabel = periodLabels[rentalPeriod] || 'Month';
    const fd = new FormData();
    fd.append('title',         title);
    fd.append('description',   `${description}\n\nProperty Type: ${propType}\nFurnished: ${furnished}\nAvailable From: ${availFrom}\nRental Period: Per ${periodLabel}`);
    fd.append('price',         price);
    fd.append('category_name', ROOMS_CAT);
    fd.append('condition',     'new');
    fd.append('price_type',    negotiable);
    fd.append('contact',       contact);
    fd.append('city',          location);
    fd.append('plan_name',     planName);
    fd.append('sub_category',  rentalPeriod); // ✅ store rental period for display
    images.forEach((img, i) => fd.append(`image_${i}`, img));
    videos.forEach((vid, i) => fd.append(`video_${i}`, vid));
    onSubmit(fd);
  };

  return (
    <form onSubmit={handleSubmit} className={styles.postAdForm}>
      <Field label={`Title (${title.length}/25)`} required>
        <input className={styles.inputField} placeholder="e.g., SPACIOUS 2 BEDROOM"
          value={title} maxLength={25}
          onChange={e => setTitle(e.target.value.toUpperCase())} />
      </Field>
      <Field label="Category" required>
        <div className={styles.lockedCategory}>🏠 Rooms to Let</div>
      </Field>
      <Field label="Location" required>
        <CityPicker value={location} onChange={setLocation} placeholder="Search city or town..." />
      </Field>
      <Field label="Rental Price (KES)" required>
        {/* ✅ Inline price + rental period row */}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input
            type="text" inputMode="numeric"
            className={styles.inputField}
            style={{ flex: 1 }}
            placeholder="e.g., 10,000"
            value={priceDisplay}
            onChange={handleRoomPrice}
          />
          <select
            className={styles.inputField}
            style={{ width: 110, flexShrink: 0 }}
            value={rentalPeriod}
            onChange={e => setRentalPeriod(e.target.value)}
            title="Rental period"
          >
            <option value="hour">/ Hour</option>
            <option value="day">/ Day</option>
            <option value="week">/ Week</option>
            <option value="month">/ Month</option>
            <option value="year">/ Year</option>
          </select>
        </div>
        {price && (
          <p style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
            Price: <strong>KES {priceDisplay} / {rentalPeriod.charAt(0).toUpperCase() + rentalPeriod.slice(1)}</strong>
          </p>
        )}
      </Field>
      <div className={styles.twoCol}>
        <Field label="Is Rent Negotiable?" required>
          <select className={styles.inputField} value={negotiable} onChange={e => setNegotiable(e.target.value)}>
            <option value="" disabled>Select price type...</option>
            <option value="fixed">🔒 Fixed Price</option>
            <option value="negotiable">🤝 Negotiable</option>
          </select>
        </Field>
        <Field label="Property Type" required>
          <select className={styles.inputField} value={propType} onChange={e => setPropType(e.target.value)}>
            <option value="" disabled>Select type...</option>
            <option value="bedsitter">Bedsitter</option>
            <option value="single_room">Single Room</option>
            <option value="1_bedroom">1 Bedroom</option>
            <option value="2_bedroom">2 Bedroom</option>
            <option value="3_bedroom">3 Bedroom</option>
            <option value="studio">Studio</option>
            <option value="mansion">Mansion</option>
            <option value="airbnb">AirBnB</option>
            <option value="warehouse">Warehouse</option>
            <option value="office">Office Space</option>
            <option value="shop">Shop / Stall</option>
          </select>
        </Field>
      </div>
      <div className={styles.twoCol}>
        <Field label="Furnished?" required>
          <select className={styles.inputField} value={furnished} onChange={e => setFurnished(e.target.value)}>
            <option value="" disabled>Select option...</option>
            <option value="fully_furnished">Fully Furnished</option>
            <option value="semi_furnished">Semi-Furnished</option>
            <option value="unfurnished">Unfurnished</option>
          </select>
        </Field>
        <Field label="Available From" required>
          <input type="date" className={styles.inputField}
            value={availFrom} onChange={e => setAvailFrom(e.target.value)}
            min={new Date().toISOString().split('T')[0]} />
        </Field>
      </div>
      <Field label="Description" required>
        <textarea className={`${styles.inputField} ${styles.textareaField}`}
          placeholder="Describe your room and amenities..." rows={4}
          value={description} onChange={e => setDescription(e.target.value)} />
      </Field>
      <Field label="Contact (Email or Phone)" required>
        <input className={styles.inputField}
          placeholder="e.g., +254712345678 or email@example.com"
          value={contact} onChange={e => setContact(e.target.value)} />
      </Field>
      {maxVideos > 0 && (
        <Field label="Upload Videos (Optional)">
          <input type="file" className={styles.fileInput}
            accept="video/mp4,video/webm,video/ogg,video/quicktime" multiple onChange={handleVideos} />
          <p className={styles.helperText}>🎥 Max {maxVideos} videos, 50MB each</p>
          {videos.length > 0 && <p className={styles.selectedFiles}>✅ {videos.length}/{maxVideos} selected</p>}
        </Field>
      )}
      <Field label="Upload Images (Optional)">
        <div className={styles.uploadBox}>
          <label className={styles.uploadLabel} htmlFor="rooms-images">
            <span>📷</span> Choose Files
          </label>
          <input id="rooms-images" type="file" className={styles.hiddenInput}
            accept="image/*" multiple onChange={handleImages} />
          <p className={styles.helperText}>Upload up to {maxImages} photos (Max 10 MB each)</p>
          {images.length > 0 && <p className={styles.selectedFiles}>✅ {images.length}/{maxImages} selected</p>}
        </div>
      </Field>
      {maxVideos === 0 && (
        <div className={styles.upgradePrompt}>
          <p>🎥 Want to add videos? Upgrade to <strong>Business Plan</strong> for up to 2 videos!</p>
        </div>
      )}
      <button
        type="submit"
        className={styles.submitAdBtn}
        disabled={submitState === 'posting' || submitState === 'posted'}
        style={submitState === 'posted' ? { background: '#16a34a', cursor: 'default' } : submitState === 'posting' ? { opacity: 0.7, cursor: 'not-allowed' } : {}}
      >
        {submitState === 'posting' ? '⏳ Posting Ad...' : submitState === 'posted' ? '✅ Ad Posted' : 'Post Listing'}
      </button>
    </form>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// LOST & FOUND FORM
// ══════════════════════════════════════════════════════════════════════════════
const LostFoundForm = ({ maxVideos, planName, onSubmit, submitState = 'idle' }) => {
  const [itemStatus,   setItemStatus]   = useState('lost');
  const [itemName,     setItemName]     = useState('');
  const [itemCategory, setItemCategory] = useState('');
  const [location,     setLocation]     = useState('');
  const [dateLost,     setDateLost]     = useState('');
  const [description,  setDescription]  = useState('');
  const [contact,      setContact]      = useState('');
  const [images,       setImages]       = useState([]);
  const [videos,       setVideos]       = useState([]);

  const LF_CATS = [
    'Electronics','Documents & ID','Pets','Bags & Luggage',
    'Clothing & Accessories','Keys','Wallets & Purses','Jewellery',
    'Vehicles','Books & Stationery','Other',
  ];

  const handleLFVideos = (e) => {
    if (maxVideos === 0) return alert('Video upload requires Business plan');
    const all = Array.from(e.target.files);
    const valid = all.filter(f => f.size <= 50*1024*1024);
    const oversized = all.length - valid.length;
    const overCount = valid.length - maxVideos;
    if (oversized > 0) alert(`${oversized} video${oversized > 1 ? 's were' : ' was'} removed (exceeds 50MB limit).`);
    if (overCount > 0) alert(`Only ${maxVideos} video${maxVideos > 1 ? 's' : ''} allowed. The rest will be ignored.`);
    setVideos(valid.slice(0, maxVideos));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!itemName)     return alert('Please enter item name');
    if (!itemCategory) return alert('Please select a category');
    if (!location)     return alert('Please enter a location');
    if (!dateLost)     return alert('Please select date lost/found');
    if (!description)  return alert('Please add a description');
    if (!contact)      return alert('Please enter contact info');

    const fd = new FormData();
    fd.append('title',         `[${itemStatus.toUpperCase()}] ${itemName}`);
    fd.append('description',   `Status: ${itemStatus}\nCategory: ${itemCategory}\nDate: ${dateLost}\n\n${description}`);
    fd.append('price',         '0');
    fd.append('category_name', LOST_FOUND_CAT);
    fd.append('condition',     'used');
    fd.append('price_type',    'fixed');
    fd.append('contact',       contact);
    fd.append('city',          location);
    fd.append('plan_name',     planName);
    images.forEach((img, i) => fd.append(`image_${i}`, img));
    videos.forEach((vid, i) => fd.append(`video_${i}`, vid));
    onSubmit(fd);
  };

  return (
    <form onSubmit={handleSubmit} className={styles.postAdForm}>
      <Field label="Status" required>
        <select className={styles.inputField} value={itemStatus} onChange={e => setItemStatus(e.target.value)}>
          <option value="lost">🔍 Lost</option>
          <option value="found">✅ Found</option>
        </select>
      </Field>
      <Field label={`Item Name (${itemName.length}/25)`} required>
        <input className={styles.inputField}
          placeholder="e.g., BLACK BACKPACK, CAR KEY"
          value={itemName} maxLength={25}
          onChange={e => setItemName(e.target.value.toUpperCase())} />
      </Field>
      <Field label="Category" required>
        <select className={styles.inputField} value={itemCategory} onChange={e => setItemCategory(e.target.value)}>
          <option value="" disabled>Select category...</option>
          {LF_CATS.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </Field>
      <Field label={itemStatus === 'lost' ? 'Where Was It Lost?' : 'Where Was It Found?'} required>
        <input className={styles.inputField}
          placeholder="e.g., Central Mall, Nairobi CBD, Uhuru Park"
          value={location} onChange={e => setLocation(e.target.value)} />
      </Field>
      <Field label="Date Lost/Found" required>
        <input type="date" className={styles.inputField}
          value={dateLost} onChange={e => setDateLost(e.target.value)}
          max={new Date().toISOString().split('T')[0]} />
      </Field>
      <Field label="Description" required>
        <textarea className={`${styles.inputField} ${styles.textareaField}`}
          placeholder="Describe the item, its appearance, and how to contact you..."
          rows={4} value={description} onChange={e => setDescription(e.target.value)} />
      </Field>
      <Field label="Contact (Email or Phone)" required>
        <input className={styles.inputField}
          placeholder="e.g., +254712345678 or email@example.com"
          value={contact} onChange={e => setContact(e.target.value)} />
      </Field>
      {maxVideos > 0 && (
        <Field label="Upload Videos (Optional)">
          <input type="file" className={styles.fileInput}
            accept="video/mp4,video/webm,video/ogg,video/quicktime" multiple onChange={handleLFVideos} />
          <p className={styles.helperText}>🎥 Max {maxVideos} videos, 50MB each</p>
          {videos.length > 0 && <p className={styles.selectedFiles}>✅ {videos.length}/{maxVideos} selected</p>}
        </Field>
      )}
      <Field label="Upload Images (Optional)">
        <div className={styles.uploadBox}>
          <label className={styles.uploadLabel} htmlFor="lf-images">
            <span>📷</span> Upload Photos <span className={styles.uploadChevron}>▾</span>
          </label>
          <input id="lf-images" type="file" className={styles.hiddenInput}
            accept="image/*" multiple onChange={e => {
              const files = Array.from(e.target.files);
              if (files.length > 6) alert('Only the first 6 photos will be uploaded. The rest will be ignored.');
              setImages(files.slice(0, 6));
            }} />
          <p className={styles.helperText}>🖼️ Upload up to 6 photos: JPG, PNG (Max 10 MB each)</p>
          {images.length > 0 && <p className={styles.selectedFiles}>✅ {images.length}/6 selected</p>}
        </div>
      </Field>
      {maxVideos === 0 && (
        <div className={styles.upgradePrompt}>
          <p>🎥 Want to add videos? Upgrade to <strong>Business Plan</strong> for up to 2 videos!</p>
        </div>
      )}
      <button
        type="submit"
        className={styles.submitAdBtn}
        disabled={submitState === 'posting' || submitState === 'posted'}
        style={submitState === 'posted' ? { background: '#16a34a', cursor: 'default' } : submitState === 'posting' ? { opacity: 0.7, cursor: 'not-allowed' } : {}}
      >
        {submitState === 'posting' ? '⏳ Posting Ad...' : submitState === 'posted' ? '✅ Ad Posted' : 'Post Listing'}
      </button>
    </form>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// STANDARD FORM
// ══════════════════════════════════════════════════════════════════════════════
const StandardForm = ({ initialCategory, maxImages, maxVideos, planName, onSubmit, submitState = 'idle' }) => {
  const [title,        setTitle]        = useState('');
  const [category,     setCategory]     = useState(initialCategory || 'Electronics');
  const [subCategory,  setSubCategory]  = useState('');
  const [condition,    setCondition]    = useState('');
  const [price,        setPrice]        = useState('');
  const [priceDisplay, setPriceDisplay] = useState('');
  const [negotiable,   setNegotiable]   = useState('');
  const [city,         setCity]         = useState('');
  const [description,  setDescription]  = useState('');
  const [contact,      setContact]      = useState('');
  const [images,       setImages]       = useState([]);
  const [videos,       setVideos]       = useState([]);

  const handlePriceChange = (e) => {
    const raw = e.target.value.replace(/,/g, '').replace(/[^\d]/g, '');
    setPrice(raw);
    setPriceDisplay(raw ? Number(raw).toLocaleString('en-KE') : '');
  };

  const handleImages = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > maxImages)
      alert(`Only the first ${maxImages} photo${maxImages > 1 ? 's' : ''} will be uploaded. The rest will be ignored.`);
    setImages(files.slice(0, maxImages));
  };

  const handleVideos = (e) => {
    if (maxVideos === 0) return alert('Video upload requires Business plan');
    const all = Array.from(e.target.files);
    const valid = all.filter(f => f.size <= 50*1024*1024);
    const oversized = all.length - valid.length;
    const overCount = valid.length - maxVideos;
    if (oversized > 0) alert(`${oversized} video${oversized > 1 ? 's were' : ' was'} removed (exceeds 50MB limit).`);
    if (overCount > 0) alert(`Only ${maxVideos} video${maxVideos > 1 ? 's' : ''} allowed. The rest will be ignored.`);
    setVideos(valid.slice(0, maxVideos));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (title.length < 5)        return alert('Title must be at least 5 characters');
    if (title.length > 25)       return alert('Title must be 25 characters or less');
    if (description.length < 10) return alert('Description must be at least 10 characters');
    if (!city)                   return alert('Please enter a city/town');
    if (!condition)              return alert('Please select product condition');
    if (!negotiable)             return alert('Please select price type');
    if (!contact)                return alert('Please enter contact info');
    if (!images.length && !videos.length) return alert('Please upload at least one image or video');

    const fd = new FormData();
    fd.append('title',         title);
    fd.append('description',   description);
    fd.append('price',         price);
    fd.append('category_name', category);
    if (subCategory) fd.append('sub_category', subCategory);
    fd.append('condition',     condition);
    fd.append('price_type',    negotiable);
    fd.append('contact',       contact);
    fd.append('city',          city);
    fd.append('plan_name',     planName);
    images.forEach((img, i) => fd.append(`image_${i}`, img));
    videos.forEach((vid, i) => fd.append(`video_${i}`, vid));
    onSubmit(fd);
  };

  return (
    <form onSubmit={handleSubmit} className={styles.postAdForm}>
      <Field label={`Title (${title.length}/25)`} required>
        <input className={styles.inputField} placeholder="e.g., IPHONE 13 PRO MAX"
          value={title} maxLength={25}
          onChange={e => setTitle(e.target.value.toUpperCase())} />
      </Field>

      <Field label="Category" required>
        <select className={styles.inputField} value={category}
          onChange={e => { setCategory(e.target.value); setSubCategory(''); }}>
          {CATEGORIES.filter(c => c !== 'All' && c !== ROOMS_CAT && c !== LOST_FOUND_CAT)
            .map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </Field>

      {/* ── Sub-category — optional, shown when category has defined sub-options ── */}
      {SUB_CATEGORIES[category] && (
        <Field label="Sub-Category (Optional)">
          <select className={`${styles.inputField} ${styles.subCategorySelect}`}
            value={subCategory}
            onChange={e => setSubCategory(e.target.value)}>
            <option value="">— Select sub-category —</option>
            {SUB_CATEGORIES[category].map(sub => (
              <option key={sub} value={sub}>{sub}</option>
            ))}
          </select>
          {subCategory && (
            <p className={styles.subCategoryChosen}>🏷️ {subCategory}</p>
          )}
        </Field>
      )}

      <Field label="Is Product Used?" required>
        <select className={styles.inputField} value={condition} onChange={e => setCondition(e.target.value)}>
          <option value="" disabled>Select condition...</option>
          <option value="new">✨ New</option>
          <option value="slightly_used">👌 Slightly Used</option>
          <option value="used">🔄 Used</option>
        </select>
      </Field>

      <Field label="Price (KES)" required>
        <input type="text" inputMode="numeric" className={styles.inputField}
          placeholder="e.g., 50,000" value={priceDisplay} onChange={handlePriceChange} />
      </Field>

      <Field label="Is Price Negotiable?" required>
        <select className={styles.inputField} value={negotiable} onChange={e => setNegotiable(e.target.value)}>
          <option value="" disabled>Select price type...</option>
          <option value="fixed">🔒 Fixed Price</option>
          <option value="negotiable">🤝 Slightly Negotiable</option>
        </select>
      </Field>

      <Field label="City/Town" required>
        <CityPicker value={city} onChange={setCity} placeholder="Search city or town..." />
      </Field>

      <Field label="Description" required>
        <textarea className={`${styles.inputField} ${styles.textareaField}`}
          placeholder="Describe your item in detail..." rows={4}
          value={description} onChange={e => setDescription(e.target.value)} />
      </Field>

      <Field label="Contact (Email or Phone)" required>
        <input className={styles.inputField}
          placeholder="e.g., +254712345678 or email@example.com"
          value={contact} onChange={e => setContact(e.target.value)} />
      </Field>

      {maxVideos > 0 && (
        <Field label="Upload Videos (Optional)">
          <input type="file" className={styles.fileInput}
            accept="video/mp4,video/webm,video/ogg,video/quicktime" multiple onChange={handleVideos} />
          <p className={styles.helperText}>🎥 Max {maxVideos} videos, 50MB each</p>
          {videos.length > 0 && <p className={styles.selectedFiles}>✅ {videos.length}/{maxVideos} selected</p>}
        </Field>
      )}

      <Field label="Upload Images *">
        <input type="file" className={styles.fileInput} accept="image/*" multiple onChange={handleImages} />
        <p className={styles.helperText}>📷 Max {maxImages} images ({planName} plan)</p>
        {images.length > 0 && <p className={styles.selectedFiles}>✅ {images.length}/{maxImages} selected</p>}
      </Field>

      {maxVideos === 0 && (
        <div className={styles.upgradePrompt}>
          <p>🎥 Want to add videos? Upgrade to <strong>Business Plan</strong> for up to 2 videos!</p>
        </div>
      )}

      <button
        type="submit"
        className={styles.submitAdBtn}
        disabled={submitState === 'posting' || submitState === 'posted'}
        style={submitState === 'posted' ? { background: '#16a34a', cursor: 'default' } : submitState === 'posting' ? { opacity: 0.7, cursor: 'not-allowed' } : {}}
      >
        {submitState === 'posting' ? '⏳ Posting Ad...' : submitState === 'posted' ? '✅ Ad Posted' : 'Post Ad'}
      </button>
    </form>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// MAIN MODAL
// ══════════════════════════════════════════════════════════════════════════════
const PostAdModal = ({ isOpen, onClose, onAdPosted, selectedPlan, onBackToPlan }) => {
  const { isAuthenticated, getToken } = useAuthContext();
  const [chosenCategory, setChosenCategory] = useState(null);
  
  // ✅ NEW: Submit state to prevent double-posting
  // 'idle' | 'posting' | 'posted'
  const [submitState, setSubmitState] = useState('idle');

  // ✅ NEW: Promotion state
  const [promotion, setPromotion] = useState(null);
  const [promotionLoading, setPromotionLoading] = useState(false);
  const [finalPrice, setFinalPrice] = useState(null);

  // ✅ Payment state — holds form data + ad_id while waiting for M-Pesa confirmation
  const [showPayment,     setShowPayment]     = useState(false);
  const [pendingFormData, setPendingFormData] = useState(null);
  const [pendingAdId,     setPendingAdId]     = useState(null);

  // ✅ Check for promotion when modal opens or plan changes.
  // ✅ FIX: If PlanSelectionModal already fetched + resolved the promo
  //    (passed via selectedPlan.promoApplied), reuse it — no re-fetch needed.
  //    Only fetch fresh if promoApplied is absent (e.g. direct plan selection).
  useEffect(() => {
    if (!isOpen || !selectedPlan || !isAuthenticated) return;

    if (selectedPlan.promoApplied) {
      // Reuse the already-resolved promo from PlanSelectionModal
      const pricingInfo = selectedPlan.promoApplied;
      // Reconstruct the promotion object from selectedPlan context
      // (we don't store the raw promo obj, but finalPrice + isFree is enough)
      setPromotion({ _fromPlan: true });   // truthy sentinel — real fields in pricingInfo
      setFinalPrice(pricingInfo);
    } else {
      checkPromotion();
    }
  }, [isOpen, selectedPlan?.planId, isAuthenticated]);

  // ✅ Fetch promotion from backend (used when promoApplied not pre-loaded)
  const checkPromotion = async () => {
    if (!selectedPlan) return;
    setPromotionLoading(true);
    try {
      const planParam = selectedPlan.planId === 'free' ? '' : (selectedPlan.planId || '');
      if (!planParam) { setPromotion(null); setFinalPrice(null); return; }

      const promo = await checkUserPromotion(planParam);

      if (promo.has_promotion && promo.promotion) {
        setPromotion(promo.promotion);
        // Use originalPrice for the discount calculation — not the already-discounted price
        const originalPrice = selectedPlan.originalPrice ?? selectedPlan.price ?? 0;
        const pricingInfo = calculatePromotionPrice(originalPrice, promo.promotion, planParam);
        setFinalPrice(pricingInfo.hasPromo ? pricingInfo : null);
        if (pricingInfo.hasPromo) console.log('✅ Promotion applied:', pricingInfo);
      } else {
        setPromotion(null);
        setFinalPrice(null);
      }
    } catch (error) {
      console.error('Promotion check failed:', error);
      setPromotion(null);
      setFinalPrice(null);
    } finally {
      setPromotionLoading(false);
    }
  };

  if (!isOpen) return null;

  const maxImages  = selectedPlan?.maxImages || 2;
  const maxVideos  = selectedPlan?.maxVideos || 0;
  const planName   = selectedPlan?.planName  || 'Free';
  const isBusiness = selectedPlan?.planId === 'business';
  
  // ✅ Use promotional price if available.
  // selectedPlan.originalPrice = full price before discount (set by PlanSelectionModal).
  // finalPrice.finalPrice = post-discount price calculated by calculatePromotionPrice.
  const originalPlanPrice = selectedPlan?.originalPrice ?? selectedPlan?.price ?? 0;
  const displayPrice = finalPrice?.isFree
    ? 0
    : (finalPrice?.hasPromo ? finalPrice.finalPrice : originalPlanPrice);

  const handleClose = () => {
    setChosenCategory(null);
    setPromotion(null);
    setFinalPrice(null);
    setShowPayment(false);
    setPendingFormData(null);
    setPendingAdId(null);
    setSubmitState('idle');
    onClose();
  };

  // ── Determine if this plan needs payment ─────────────────────────────────
  // Uses displayPrice (the actual amount the user owes) — not the original plan price.
  // This ensures a 100% promo (displayPrice=0) never triggers the payment modal,
  // and a 90% promo (displayPrice=15) correctly does.
  const needsPayment = () => {
    if (!selectedPlan) return false;
    if (selectedPlan.planId === 'free') return false;
    // displayPrice is already 0 when isFree, so this single check covers all cases
    if (!displayPrice || displayPrice === 0) return false;
    return true;
  };

  // ── Called by sub-forms (Rooms / LostFound / Standard) on submit ──────────
  const handleSubmit = async (formData) => {
    if (!isAuthenticated) return alert('Please log in to post an ad');
    if (selectedPlan) {
      formData.append('plan_id',   selectedPlan.planId   || '');
      formData.append('plan_name', selectedPlan.planName || 'Free');
      // ✅ Send the actual (possibly discounted) amount to the backend.
      // displayPrice already accounts for promos, so just use that.
      formData.append('plan_amount', String(displayPrice));
    }

    setSubmitState('posting');

    // ── If paid plan and no promo → create ad as pending, then show payment ─
    if (needsPayment()) {
      // Step 1: POST the ad to backend with status pending
      // This gives us an ad_id which M-Pesa callback needs to activate the plan
      let adId = null;
      try {
        const res = await apiService.postAd(formData, getToken());
        let data = {};
        try { data = await res.json(); } catch (_) {}
        if (!res.ok) {
          alert(data?.error || data?.detail || 'Failed to save ad. Please try again.');
          setSubmitState('idle');
          return;
        }
        adId = data?.id || data?.ad_id || null;
        console.log('✅ Ad created (pending payment), id=', adId);
      } catch (err) {
        console.error(err);
        alert('Network error. Please try again.');
        setSubmitState('idle');
        return;
      }

      // Step 2: Show payment modal with the real ad_id
      setPendingAdId(adId);
      setPendingFormData(formData);
      setShowPayment(true);
      setSubmitState('idle'); // reset — payment modal takes over
      return;   // wait for payment confirmation
    }

    // ── Free / promo → post immediately ──────────────────────────────────
    await postAdToBackend(formData);
  };

  // ── Actually POST the ad (called after payment OR directly for free) ──────
  const postAdToBackend = async (formData) => {
    try {
      const response = await apiService.postAd(formData, getToken());
      let data = {};
      try { data = await response.json(); } catch (_) {}

      if (response.ok) {
        // ✅ Consume promotion slot after successful free/promo post
        if (promotion && selectedPlan) {
          const planMap = {
            'free':     '',
            'basic':    'basic',
            'pro':      'pro',      // ✅ FIX: 'pro' was missing — Pro plan never decremented
            'business': 'business',
          };
          const planParam = planMap[selectedPlan.planId] || '';
          if (planParam) {
            try {
              await consumePromotionSlot(planParam);
              console.log('✅ Promotion slot consumed for plan:', planParam);
            } catch (err) {
              console.error('⚠️ Slot consume failed (non-fatal):', err);
            }
          }
        }
        setSubmitState('posted');
        alert('Ad posted successfully!');
        handleClose();
        if (typeof onAdPosted === 'function') onAdPosted();
      } else {
        setSubmitState('idle');
        alert(data?.error || data?.detail || 'Failed to post ad. Please try again.');
      }
    } catch (err) {
      console.error(err);
      setSubmitState('idle');
      alert('Network error. Please try again.');
    }
  };

  // ── Payment confirmed → ad already exists, just notify parent ──────────────
  // The Django M-Pesa callback already activated AdPlan when payment was confirmed.
  // ✅ FIX: We MUST consume the promotion slot here too.
  //    Previously this was only called in postAdToBackend (the free/promo path),
  //    but discounted plans (e.g. Basic at KES 35) still go through the payment
  //    path because needsPayment() returns true (price > 0).
  //    Result: slot was NEVER decremented for discounted paid plans.
  const handlePaymentSuccess = async (paymentData) => {
    // ✅ Consume the promotion slot if the user had an active promo on this plan
    if (promotion && selectedPlan) {
      const planMap = {
        'free':     '',
        'basic':    'basic',
        'pro':      'pro',
        'business': 'business',
      };
      const planParam = planMap[selectedPlan.planId] || '';
      if (planParam) {
        try {
          await consumePromotionSlot(planParam);
          console.log('✅ Promotion slot consumed after payment for plan:', planParam);
        } catch (err) {
          console.error('⚠️ Slot consume after payment failed (non-fatal):', err);
        }
      }
    }

    setShowPayment(false);
    setPendingFormData(null);
    setPendingAdId(null);
    alert('Ad posted successfully! Your plan is now active.');
    handleClose();
    if (typeof onAdPosted === 'function') onAdPosted();
  };

  const titles = {
    [ROOMS_CAT]:      { title: 'Post Your Listing',  sub: 'Fill in the details to list your room' },
    [LOST_FOUND_CAT]: { title: 'Post Lost & Found',  sub: 'Fill in the details to report the item' },
    default:          { title: 'Post Your Ad',        sub: 'Fill in the details to list your item' },
  };
  const { title, sub } = titles[chosenCategory] || titles.default;

  return (
    <>
    <div className={styles.modalOverlay} onClick={handleClose}>
      <div className={styles.postAdModal} onClick={e => e.stopPropagation()}>
        <button className={styles.closeBtn} onClick={handleClose}>×</button>
        <h2 className={styles.modalTitle}>{title}</h2>
        <p className={styles.modalSubtitle}>{sub}</p>

        {/* ── Plan banner ── */}
        {selectedPlan && (
          <div className={`${styles.selectedPlanBanner} ${isBusiness ? styles.selectedPlanBannerBusiness : ''}`}>
            <div className={styles.planInfo}>
              <span className={`${styles.planBadge} ${isBusiness ? styles.planBadgeBusiness : ''}`}>
                {isBusiness ? '⭐ ' : ''}{planName}
              </span>
              <div className={styles.planLimits}>
                <span>📷 Up to {maxImages} images</span>
                {maxVideos > 0 && <span>🎥 Up to {maxVideos} videos</span>}
                {selectedPlan.displayDays && <span>⏱️ {selectedPlan.displayDays} days</span>}
              </div>
            </div>
            
            {/* ← Change Plan button */}
            {onBackToPlan && (
              <button
                onClick={onBackToPlan}
                style={{
                  background: 'none', border: '1.5px solid rgba(255,255,255,0.5)',
                  borderRadius: 8, color: 'inherit', fontSize: 12, fontWeight: 600,
                  padding: '4px 12px', cursor: 'pointer', whiteSpace: 'nowrap',
                  opacity: 0.85, transition: 'opacity .15s',
                }}
                onMouseEnter={e => e.target.style.opacity = 1}
                onMouseLeave={e => e.target.style.opacity = 0.85}
              >
                ← Change Plan
              </button>
            )}

          {/* ✅ NEW: Show promotional or regular price */}
            {displayPrice > 0 ? (
              <div className={`${styles.planPrice} ${isBusiness ? styles.planPriceBusiness : ''}`}>
                {finalPrice && finalPrice.discountAmount > 0 && (
                  <div className={styles.originalPrice}>
                    <del>KES {selectedPlan.price}</del>
                  </div>
                )}
                <div>KES {displayPrice}</div>
              </div>
            ) : displayPrice === 0 && finalPrice?.isFree ? (
              <div className={`${styles.planPrice} ${styles.planPriceFree}`}>
                FREE
              </div>
            ) : null}
          </div>
        )}

        {/* ✅ NEW: Promotion label */}
        {finalPrice && finalPrice.label && (
          <div className={styles.promotionBanner}>
            {finalPrice.label}
          </div>
        )}

        {/* ── Business slot notice ── */}
        {isBusiness && (
          <div className={styles.slotBanner}>
            🔄 <strong>Slot-based plan:</strong> Pay once &amp; post freely — delete and re-post
            your ad any time within the 3-month window at no extra charge.
          </div>
        )}

        {/* ── Category picker ── */}
        {chosenCategory === null && (
          <div className={styles.categoryPicker}>
            <div className={styles.formGroup}>
              <label>Category <span className={styles.req}>*</span></label>
              <select
                className={styles.inputField}
                defaultValue=""
                onChange={e => { if (e.target.value) setChosenCategory(e.target.value); }}
              >
                <option value="" disabled>Select a category...</option>
                {CATEGORIES.filter(c => c !== 'All').map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
          </div>
        )}

        {chosenCategory === ROOMS_CAT && (
          <RoomsForm maxImages={maxImages} maxVideos={maxVideos} planName={planName} onSubmit={handleSubmit} submitState={submitState} />
        )}
        {chosenCategory === LOST_FOUND_CAT && (
          <LostFoundForm maxVideos={maxVideos} planName={planName} onSubmit={handleSubmit} submitState={submitState} />
        )}
        {chosenCategory !== null && chosenCategory !== ROOMS_CAT && chosenCategory !== LOST_FOUND_CAT && (
          <StandardForm initialCategory={chosenCategory} maxImages={maxImages}
            maxVideos={maxVideos} planName={planName} onSubmit={handleSubmit} submitState={submitState} />
        )}

        {chosenCategory !== null && (
          <button className={styles.backBtn} onClick={() => setChosenCategory(null)}>
            ← Change category
          </button>
        )}
      </div>
    </div>

    {/* ── M-Pesa Payment Modal — shown after user fills form and clicks submit ── */}
    {/* Always use displayPrice: it handles isFree (0), partial discount, and full price */}
    <PaymentModal
      isOpen={showPayment}
      plan={{
        ...selectedPlan,
        // displayPrice already resolves correctly for every case:
        //   100% promo (isFree)  → 0       — PaymentModal skips STK push
        //   partial promo        → reduced  — e.g. KES 15 for 90% off Pro
        //   no promo             → original — e.g. KES 150 for Pro
        price: displayPrice,
      }}
      adId={pendingAdId}
      onSuccess={handlePaymentSuccess}
      onClose={() => { setShowPayment(false); setPendingFormData(null); setPendingAdId(null); }}
    />
    </>
  );
};

export default PostAdModal;
































// import React, { useState, useRef, useEffect } from 'react';
// import { useAuthContext } from '../../../context/AuthContext';
// import apiService from '../../../services/api';
// import { CATEGORIES, SUB_CATEGORIES } from '../../../utils/constants';
// import { checkUserPromotion, consumePromotionSlot, calculatePromotionPrice } from '../../../utils/promotionApi'; // ✅ NEW
// import styles from './PostAdModal.module.css';
// import PaymentModal from './PaymentModal';

// const ROOMS_CAT      = 'Rooms to Let';
// const LOST_FOUND_CAT = 'Lost & Found';

// // ── Location search via OpenStreetMap Nominatim ───────────────────────────
// const searchKenyaPlaces = async (query) => {
//   if (!query || query.trim().length < 2) return [];
//   try {
//     const url =
//       `https://nominatim.openstreetmap.org/search` +
//       `?q=${encodeURIComponent(query + ', Kenya')}` +
//       `&countrycodes=ke&limit=12&format=json&addressdetails=1&accept-language=en`;
//     const res = await fetch(url, {
//       headers: { 'User-Agent': 'ImSellingKE/1.0 (marketplace)' }
//     });
//     const data = await res.json();
//     return data.map(item => {
//       const a = item.address;
//       const label =
//         a.suburb || a.neighbourhood || a.quarter || a.village ||
//         a.hamlet || a.town || a.city_district || a.city ||
//         item.display_name.split(',')[0];
//       const parent =
//         a.suburb        ? (a.town || a.city || a.county) :
//         a.village       ? (a.town || a.city || a.county) :
//         a.hamlet        ? (a.town || a.city || a.county) :
//         a.neighbourhood ? (a.city || a.county)           : null;
//       const display = parent ? `${label.trim()}, ${parent.trim()}` : label.trim();
//       return { label: label.trim(), display, osm_id: item.osm_id };
//     }).filter((v, i, arr) => arr.findIndex(x => x.display === v.display) === i);
//   } catch (e) {
//     console.error('Location search error:', e);
//     return [];
//   }
// };

// const Field = ({ label, required, children }) => (
//   <div className={styles.formGroup}>
//     <label>{label} {required && <span className={styles.req}>*</span>}</label>
//     {children}
//   </div>
// );

// // ── CityPicker ────────────────────────────────────────────────────────────
// const CityPicker = ({ value, onChange, placeholder = 'Search village, estate, street, town...' }) => {
//   const [query,     setQuery]     = useState(value || '');
//   const [results,   setResults]   = useState([]);
//   const [open,      setOpen]      = useState(false);
//   const [active,    setActive]    = useState(-1);
//   const [loading,   setLoading]   = useState(false);
//   const [confirmed, setConfirmed] = useState(!!value);
//   const wrapRef  = useRef(null);
//   const inputRef = useRef(null);
//   const timerRef = useRef(null);

//   useEffect(() => {
//     const fn = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
//     document.addEventListener('mousedown', fn);
//     return () => document.removeEventListener('mousedown', fn);
//   }, []);

//   const runSearch = (q) => {
//     clearTimeout(timerRef.current);
//     if (!q || q.trim().length < 2) { setResults([]); setLoading(false); return; }
//     setLoading(true);
//     timerRef.current = setTimeout(async () => {
//       const found = await searchKenyaPlaces(q);
//       setResults(found);
//       setOpen(found.length > 0);
//       setActive(0);
//       setLoading(false);
//     }, 400);
//   };

//   const select = (item) => {
//     setQuery(item.display);
//     onChange(item.display);
//     setConfirmed(true);
//     setOpen(false);
//     setResults([]);
//   };

//   const clear = () => {
//     setQuery(''); onChange('');
//     setConfirmed(false); setResults([]); setOpen(false);
//     clearTimeout(timerRef.current);
//     inputRef.current?.focus();
//   };

//   const handleInput = (e) => {
//     const v = e.target.value;
//     setQuery(v);
//     setConfirmed(false);
//     onChange('');
//     runSearch(v);
//   };

//   const handleKey = (e) => {
//     if (e.key === 'ArrowDown') { e.preventDefault(); setActive(i => Math.min(i + 1, results.length - 1)); }
//     if (e.key === 'ArrowUp')   { e.preventDefault(); setActive(i => Math.max(i - 1, 0)); }
//     if (e.key === 'Enter')     { e.preventDefault(); if (results[active]) select(results[active]); }
//     if (e.key === 'Escape')    { setOpen(false); }
//   };

//   return (
//     <div ref={wrapRef} className={styles.cityWrap}>
//       <div className={styles.cityInputWrap}>
//         <span className={styles.citySearchIcon}>🔍</span>
//         <input
//           ref={inputRef}
//           type="text"
//           className={`${styles.inputField} ${styles.cityInput} ${confirmed ? styles.cityInputConfirmed : ''}`}
//           placeholder={placeholder}
//           value={query}
//           onChange={handleInput}
//           onFocus={() => results.length > 0 && setOpen(true)}
//           onKeyDown={handleKey}
//           autoComplete="off"
//           spellCheck="false"
//         />
//         {loading && <span className={styles.citySpinner} title="Searching...">⏳</span>}
//         {!loading && query && (
//           <button type="button" className={styles.cityClear} onClick={clear}>×</button>
//         )}
//       </div>
//       {confirmed && <p className={styles.cityConfirmedMsg}>✅ Location set</p>}
//       {!confirmed && query.length === 1 && (
//         <p className={styles.cityHint}>Type one more character to search…</p>
//       )}
//       {open && results.length > 0 && (
//         <ul className={styles.cityDropdown}>
//           {results.map((item, i) => (
//             <li
//               key={`${item.osm_id}-${i}`}
//               className={`${styles.cityOption} ${i === active ? styles.cityOptionActive : ''}`}
//               onMouseDown={() => select(item)}
//               onMouseEnter={() => setActive(i)}
//             >
//               <span className={styles.cityPin}>📍</span>
//               <span>
//                 <span className={styles.cityOptionLabel}>{item.label}</span>
//                 {item.display !== item.label && (
//                   <span className={styles.cityOptionSub}>
//                     {', ' + item.display.replace(item.label + ', ', '')}
//                   </span>
//                 )}
//               </span>
//             </li>
//           ))}
//         </ul>
//       )}
//       {!loading && open && results.length === 0 && query.length >= 2 && (
//         <div className={styles.cityNoResult}>
//           No results for "<strong>{query}</strong>" — try a nearby street or area name
//         </div>
//       )}
//     </div>
//   );
// };

// // ══════════════════════════════════════════════════════════════════════════════
// // ROOMS TO LET FORM
// // ══════════════════════════════════════════════════════════════════════════════
// const RoomsForm = ({ maxImages, maxVideos, planName, onSubmit }) => {
//   const [title,        setTitle]        = useState('');
//   const [location,     setLocation]     = useState('');
//   const [price,        setPrice]        = useState('');
//   const [priceDisplay, setPriceDisplay] = useState('');
//   const [rentalPeriod, setRentalPeriod] = useState('month'); // ✅ NEW — rental period dropdown
//   const [negotiable,   setNegotiable]   = useState('');
//   const [propType,     setPropType]     = useState('');
//   const [furnished,    setFurnished]    = useState('');
//   const [availFrom,    setAvailFrom]    = useState('');
//   const [description,  setDescription]  = useState('');
//   const [contact,      setContact]      = useState('');
//   const [images,       setImages]       = useState([]);
//   const [videos,       setVideos]       = useState([]);

//   const handleRoomPrice = (e) => {
//     const raw = e.target.value.replace(/,/g, '').replace(/[^\d]/g, '');
//     setPrice(raw);
//     setPriceDisplay(raw ? Number(raw).toLocaleString('en-KE') : '');
//   };

//   const handleImages = (e) => {
//     const files = Array.from(e.target.files);
//     if (files.length > maxImages)
//       alert(`Only the first ${maxImages} photo${maxImages > 1 ? 's' : ''} will be uploaded. The rest will be ignored.`);
//     setImages(files.slice(0, maxImages));
//   };

//   const handleVideos = (e) => {
//     if (maxVideos === 0) return alert('Video upload requires Business plan');
//     const all = Array.from(e.target.files);
//     const valid = all.filter(f => f.size <= 50*1024*1024);
//     const oversized = all.length - valid.length;
//     const overCount = valid.length - maxVideos;
//     if (oversized > 0) alert(`${oversized} video${oversized > 1 ? 's were' : ' was'} removed (exceeds 50MB limit).`);
//     if (overCount > 0) alert(`Only ${maxVideos} video${maxVideos > 1 ? 's' : ''} allowed. The rest will be ignored.`);
//     setVideos(valid.slice(0, maxVideos));
//   };

//   const handleSubmit = (e) => {
//     e.preventDefault();
//     if (!title)            return alert('Please enter a title');
//     if (title.length > 25) return alert('Title must be 25 characters or less');
//     if (!location)         return alert('Please select a location');
//     if (!price)            return alert('Please enter rental price');
//     if (!negotiable)       return alert('Please select price type');
//     if (!propType)         return alert('Please select property type');
//     if (!furnished)        return alert('Please select furnished option');
//     if (!availFrom)        return alert('Please select availability date');
//     if (!description)      return alert('Please add a description');
//     if (!contact)          return alert('Please enter contact info');

//     const periodLabels = { hour: 'Hour', day: 'Day', week: 'Week', month: 'Month', year: 'Year' };
//     const periodLabel = periodLabels[rentalPeriod] || 'Month';
//     const fd = new FormData();
//     fd.append('title',         title);
//     fd.append('description',   `${description}\n\nProperty Type: ${propType}\nFurnished: ${furnished}\nAvailable From: ${availFrom}\nRental Period: Per ${periodLabel}`);
//     fd.append('price',         price);
//     fd.append('category_name', ROOMS_CAT);
//     fd.append('condition',     'new');
//     fd.append('price_type',    negotiable);
//     fd.append('contact',       contact);
//     fd.append('city',          location);
//     fd.append('plan_name',     planName);
//     fd.append('sub_category',  rentalPeriod); // ✅ store rental period for display
//     images.forEach((img, i) => fd.append(`image_${i}`, img));
//     videos.forEach((vid, i) => fd.append(`video_${i}`, vid));
//     onSubmit(fd);
//   };

//   return (
//     <form onSubmit={handleSubmit} className={styles.postAdForm}>
//       <Field label={`Title (${title.length}/25)`} required>
//         <input className={styles.inputField} placeholder="e.g., SPACIOUS 2 BEDROOM"
//           value={title} maxLength={25}
//           onChange={e => setTitle(e.target.value.toUpperCase())} />
//       </Field>
//       <Field label="Category" required>
//         <div className={styles.lockedCategory}>🏠 Rooms to Let</div>
//       </Field>
//       <Field label="Location" required>
//         <CityPicker value={location} onChange={setLocation} placeholder="Search city or town..." />
//       </Field>
//       <Field label="Rental Price (KES)" required>
//         {/* ✅ Inline price + rental period row */}
//         <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
//           <input
//             type="text" inputMode="numeric"
//             className={styles.inputField}
//             style={{ flex: 1 }}
//             placeholder="e.g., 10,000"
//             value={priceDisplay}
//             onChange={handleRoomPrice}
//           />
//           <select
//             className={styles.inputField}
//             style={{ width: 110, flexShrink: 0 }}
//             value={rentalPeriod}
//             onChange={e => setRentalPeriod(e.target.value)}
//             title="Rental period"
//           >
//             <option value="hour">/ Hour</option>
//             <option value="day">/ Day</option>
//             <option value="week">/ Week</option>
//             <option value="month">/ Month</option>
//             <option value="year">/ Year</option>
//           </select>
//         </div>
//         {price && (
//           <p style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
//             Price: <strong>KES {priceDisplay} / {rentalPeriod.charAt(0).toUpperCase() + rentalPeriod.slice(1)}</strong>
//           </p>
//         )}
//       </Field>
//       <div className={styles.twoCol}>
//         <Field label="Is Rent Negotiable?" required>
//           <select className={styles.inputField} value={negotiable} onChange={e => setNegotiable(e.target.value)}>
//             <option value="" disabled>Select price type...</option>
//             <option value="fixed">🔒 Fixed Price</option>
//             <option value="negotiable">🤝 Negotiable</option>
//           </select>
//         </Field>
//         <Field label="Property Type" required>
//           <select className={styles.inputField} value={propType} onChange={e => setPropType(e.target.value)}>
//             <option value="" disabled>Select type...</option>
//             <option value="bedsitter">Bedsitter</option>
//             <option value="single_room">Single Room</option>
//             <option value="1_bedroom">1 Bedroom</option>
//             <option value="2_bedroom">2 Bedroom</option>
//             <option value="3_bedroom">3 Bedroom</option>
//             <option value="studio">Studio</option>
//             <option value="mansion">Mansion</option>
//             <option value="airbnb">AirBnB</option>
//             <option value="warehouse">Warehouse</option>
//             <option value="office">Office Space</option>
//             <option value="shop">Shop / Stall</option>
//           </select>
//         </Field>
//       </div>
//       <div className={styles.twoCol}>
//         <Field label="Furnished?" required>
//           <select className={styles.inputField} value={furnished} onChange={e => setFurnished(e.target.value)}>
//             <option value="" disabled>Select option...</option>
//             <option value="fully_furnished">Fully Furnished</option>
//             <option value="semi_furnished">Semi-Furnished</option>
//             <option value="unfurnished">Unfurnished</option>
//           </select>
//         </Field>
//         <Field label="Available From" required>
//           <input type="date" className={styles.inputField}
//             value={availFrom} onChange={e => setAvailFrom(e.target.value)}
//             min={new Date().toISOString().split('T')[0]} />
//         </Field>
//       </div>
//       <Field label="Description" required>
//         <textarea className={`${styles.inputField} ${styles.textareaField}`}
//           placeholder="Describe your room and amenities..." rows={4}
//           value={description} onChange={e => setDescription(e.target.value)} />
//       </Field>
//       <Field label="Contact (Email or Phone)" required>
//         <input className={styles.inputField}
//           placeholder="e.g., +254712345678 or email@example.com"
//           value={contact} onChange={e => setContact(e.target.value)} />
//       </Field>
//       {maxVideos > 0 && (
//         <Field label="Upload Videos (Optional)">
//           <input type="file" className={styles.fileInput}
//             accept="video/mp4,video/webm,video/ogg,video/quicktime" multiple onChange={handleVideos} />
//           <p className={styles.helperText}>🎥 Max {maxVideos} videos, 50MB each</p>
//           {videos.length > 0 && <p className={styles.selectedFiles}>✅ {videos.length}/{maxVideos} selected</p>}
//         </Field>
//       )}
//       <Field label="Upload Images (Optional)">
//         <div className={styles.uploadBox}>
//           <label className={styles.uploadLabel} htmlFor="rooms-images">
//             <span>📷</span> Choose Files
//           </label>
//           <input id="rooms-images" type="file" className={styles.hiddenInput}
//             accept="image/*" multiple onChange={handleImages} />
//           <p className={styles.helperText}>Upload up to {maxImages} photos (Max 10 MB each)</p>
//           {images.length > 0 && <p className={styles.selectedFiles}>✅ {images.length}/{maxImages} selected</p>}
//         </div>
//       </Field>
//       {maxVideos === 0 && (
//         <div className={styles.upgradePrompt}>
//           <p>🎥 Want to add videos? Upgrade to <strong>Business Plan</strong> for up to 2 videos!</p>
//         </div>
//       )}
//       <button type="submit" className={styles.submitAdBtn}>Post Listing</button>
//     </form>
//   );
// };

// // ══════════════════════════════════════════════════════════════════════════════
// // LOST & FOUND FORM
// // ══════════════════════════════════════════════════════════════════════════════
// const LostFoundForm = ({ maxVideos, planName, onSubmit }) => {
//   const [itemStatus,   setItemStatus]   = useState('lost');
//   const [itemName,     setItemName]     = useState('');
//   const [itemCategory, setItemCategory] = useState('');
//   const [location,     setLocation]     = useState('');
//   const [dateLost,     setDateLost]     = useState('');
//   const [description,  setDescription]  = useState('');
//   const [contact,      setContact]      = useState('');
//   const [images,       setImages]       = useState([]);
//   const [videos,       setVideos]       = useState([]);

//   const LF_CATS = [
//     'Electronics','Documents & ID','Pets','Bags & Luggage',
//     'Clothing & Accessories','Keys','Wallets & Purses','Jewellery',
//     'Vehicles','Books & Stationery','Other',
//   ];

//   const handleLFVideos = (e) => {
//     if (maxVideos === 0) return alert('Video upload requires Business plan');
//     const all = Array.from(e.target.files);
//     const valid = all.filter(f => f.size <= 50*1024*1024);
//     const oversized = all.length - valid.length;
//     const overCount = valid.length - maxVideos;
//     if (oversized > 0) alert(`${oversized} video${oversized > 1 ? 's were' : ' was'} removed (exceeds 50MB limit).`);
//     if (overCount > 0) alert(`Only ${maxVideos} video${maxVideos > 1 ? 's' : ''} allowed. The rest will be ignored.`);
//     setVideos(valid.slice(0, maxVideos));
//   };

//   const handleSubmit = (e) => {
//     e.preventDefault();
//     if (!itemName)     return alert('Please enter item name');
//     if (!itemCategory) return alert('Please select a category');
//     if (!location)     return alert('Please enter a location');
//     if (!dateLost)     return alert('Please select date lost/found');
//     if (!description)  return alert('Please add a description');
//     if (!contact)      return alert('Please enter contact info');

//     const fd = new FormData();
//     fd.append('title',         `[${itemStatus.toUpperCase()}] ${itemName}`);
//     fd.append('description',   `Status: ${itemStatus}\nCategory: ${itemCategory}\nDate: ${dateLost}\n\n${description}`);
//     fd.append('price',         '0');
//     fd.append('category_name', LOST_FOUND_CAT);
//     fd.append('condition',     'used');
//     fd.append('price_type',    'fixed');
//     fd.append('contact',       contact);
//     fd.append('city',          location);
//     fd.append('plan_name',     planName);
//     images.forEach((img, i) => fd.append(`image_${i}`, img));
//     videos.forEach((vid, i) => fd.append(`video_${i}`, vid));
//     onSubmit(fd);
//   };

//   return (
//     <form onSubmit={handleSubmit} className={styles.postAdForm}>
//       <Field label="Status" required>
//         <select className={styles.inputField} value={itemStatus} onChange={e => setItemStatus(e.target.value)}>
//           <option value="lost">🔍 Lost</option>
//           <option value="found">✅ Found</option>
//         </select>
//       </Field>
//       <Field label={`Item Name (${itemName.length}/25)`} required>
//         <input className={styles.inputField}
//           placeholder="e.g., BLACK BACKPACK, CAR KEY"
//           value={itemName} maxLength={25}
//           onChange={e => setItemName(e.target.value.toUpperCase())} />
//       </Field>
//       <Field label="Category" required>
//         <select className={styles.inputField} value={itemCategory} onChange={e => setItemCategory(e.target.value)}>
//           <option value="" disabled>Select category...</option>
//           {LF_CATS.map(c => <option key={c} value={c}>{c}</option>)}
//         </select>
//       </Field>
//       <Field label={itemStatus === 'lost' ? 'Where Was It Lost?' : 'Where Was It Found?'} required>
//         <input className={styles.inputField}
//           placeholder="e.g., Central Mall, Nairobi CBD, Uhuru Park"
//           value={location} onChange={e => setLocation(e.target.value)} />
//       </Field>
//       <Field label="Date Lost/Found" required>
//         <input type="date" className={styles.inputField}
//           value={dateLost} onChange={e => setDateLost(e.target.value)}
//           max={new Date().toISOString().split('T')[0]} />
//       </Field>
//       <Field label="Description" required>
//         <textarea className={`${styles.inputField} ${styles.textareaField}`}
//           placeholder="Describe the item, its appearance, and how to contact you..."
//           rows={4} value={description} onChange={e => setDescription(e.target.value)} />
//       </Field>
//       <Field label="Contact (Email or Phone)" required>
//         <input className={styles.inputField}
//           placeholder="e.g., +254712345678 or email@example.com"
//           value={contact} onChange={e => setContact(e.target.value)} />
//       </Field>
//       {maxVideos > 0 && (
//         <Field label="Upload Videos (Optional)">
//           <input type="file" className={styles.fileInput}
//             accept="video/mp4,video/webm,video/ogg,video/quicktime" multiple onChange={handleLFVideos} />
//           <p className={styles.helperText}>🎥 Max {maxVideos} videos, 50MB each</p>
//           {videos.length > 0 && <p className={styles.selectedFiles}>✅ {videos.length}/{maxVideos} selected</p>}
//         </Field>
//       )}
//       <Field label="Upload Images (Optional)">
//         <div className={styles.uploadBox}>
//           <label className={styles.uploadLabel} htmlFor="lf-images">
//             <span>📷</span> Upload Photos <span className={styles.uploadChevron}>▾</span>
//           </label>
//           <input id="lf-images" type="file" className={styles.hiddenInput}
//             accept="image/*" multiple onChange={e => {
//               const files = Array.from(e.target.files);
//               if (files.length > 6) alert('Only the first 6 photos will be uploaded. The rest will be ignored.');
//               setImages(files.slice(0, 6));
//             }} />
//           <p className={styles.helperText}>🖼️ Upload up to 6 photos: JPG, PNG (Max 10 MB each)</p>
//           {images.length > 0 && <p className={styles.selectedFiles}>✅ {images.length}/6 selected</p>}
//         </div>
//       </Field>
//       {maxVideos === 0 && (
//         <div className={styles.upgradePrompt}>
//           <p>🎥 Want to add videos? Upgrade to <strong>Business Plan</strong> for up to 2 videos!</p>
//         </div>
//       )}
//       <button type="submit" className={styles.submitAdBtn}>Post Listing</button>
//     </form>
//   );
// };

// // ══════════════════════════════════════════════════════════════════════════════
// // STANDARD FORM
// // ══════════════════════════════════════════════════════════════════════════════
// const StandardForm = ({ initialCategory, maxImages, maxVideos, planName, onSubmit }) => {
//   const [title,        setTitle]        = useState('');
//   const [category,     setCategory]     = useState(initialCategory || 'Electronics');
//   const [subCategory,  setSubCategory]  = useState('');
//   const [condition,    setCondition]    = useState('');
//   const [price,        setPrice]        = useState('');
//   const [priceDisplay, setPriceDisplay] = useState('');
//   const [negotiable,   setNegotiable]   = useState('');
//   const [city,         setCity]         = useState('');
//   const [description,  setDescription]  = useState('');
//   const [contact,      setContact]      = useState('');
//   const [images,       setImages]       = useState([]);
//   const [videos,       setVideos]       = useState([]);

//   const handlePriceChange = (e) => {
//     const raw = e.target.value.replace(/,/g, '').replace(/[^\d]/g, '');
//     setPrice(raw);
//     setPriceDisplay(raw ? Number(raw).toLocaleString('en-KE') : '');
//   };

//   const handleImages = (e) => {
//     const files = Array.from(e.target.files);
//     if (files.length > maxImages)
//       alert(`Only the first ${maxImages} photo${maxImages > 1 ? 's' : ''} will be uploaded. The rest will be ignored.`);
//     setImages(files.slice(0, maxImages));
//   };

//   const handleVideos = (e) => {
//     if (maxVideos === 0) return alert('Video upload requires Business plan');
//     const all = Array.from(e.target.files);
//     const valid = all.filter(f => f.size <= 50*1024*1024);
//     const oversized = all.length - valid.length;
//     const overCount = valid.length - maxVideos;
//     if (oversized > 0) alert(`${oversized} video${oversized > 1 ? 's were' : ' was'} removed (exceeds 50MB limit).`);
//     if (overCount > 0) alert(`Only ${maxVideos} video${maxVideos > 1 ? 's' : ''} allowed. The rest will be ignored.`);
//     setVideos(valid.slice(0, maxVideos));
//   };

//   const handleSubmit = (e) => {
//     e.preventDefault();
//     if (title.length < 5)        return alert('Title must be at least 5 characters');
//     if (title.length > 25)       return alert('Title must be 25 characters or less');
//     if (description.length < 10) return alert('Description must be at least 10 characters');
//     if (!city)                   return alert('Please enter a city/town');
//     if (!condition)              return alert('Please select product condition');
//     if (!negotiable)             return alert('Please select price type');
//     if (!contact)                return alert('Please enter contact info');
//     if (!images.length && !videos.length) return alert('Please upload at least one image or video');

//     const fd = new FormData();
//     fd.append('title',         title);
//     fd.append('description',   description);
//     fd.append('price',         price);
//     fd.append('category_name', category);
//     if (subCategory) fd.append('sub_category', subCategory);
//     fd.append('condition',     condition);
//     fd.append('price_type',    negotiable);
//     fd.append('contact',       contact);
//     fd.append('city',          city);
//     fd.append('plan_name',     planName);
//     images.forEach((img, i) => fd.append(`image_${i}`, img));
//     videos.forEach((vid, i) => fd.append(`video_${i}`, vid));
//     onSubmit(fd);
//   };

//   return (
//     <form onSubmit={handleSubmit} className={styles.postAdForm}>
//       <Field label={`Title (${title.length}/25)`} required>
//         <input className={styles.inputField} placeholder="e.g., IPHONE 13 PRO MAX"
//           value={title} maxLength={25}
//           onChange={e => setTitle(e.target.value.toUpperCase())} />
//       </Field>

//       <Field label="Category" required>
//         <select className={styles.inputField} value={category}
//           onChange={e => { setCategory(e.target.value); setSubCategory(''); }}>
//           {CATEGORIES.filter(c => c !== 'All' && c !== ROOMS_CAT && c !== LOST_FOUND_CAT)
//             .map(c => <option key={c} value={c}>{c}</option>)}
//         </select>
//       </Field>

//       {/* ── Sub-category — optional, shown when category has defined sub-options ── */}
//       {SUB_CATEGORIES[category] && (
//         <Field label="Sub-Category (Optional)">
//           <select className={`${styles.inputField} ${styles.subCategorySelect}`}
//             value={subCategory}
//             onChange={e => setSubCategory(e.target.value)}>
//             <option value="">— Select sub-category —</option>
//             {SUB_CATEGORIES[category].map(sub => (
//               <option key={sub} value={sub}>{sub}</option>
//             ))}
//           </select>
//           {subCategory && (
//             <p className={styles.subCategoryChosen}>🏷️ {subCategory}</p>
//           )}
//         </Field>
//       )}

//       <Field label="Is Product Used?" required>
//         <select className={styles.inputField} value={condition} onChange={e => setCondition(e.target.value)}>
//           <option value="" disabled>Select condition...</option>
//           <option value="new">✨ New</option>
//           <option value="slightly_used">👌 Slightly Used</option>
//           <option value="used">🔄 Used</option>
//         </select>
//       </Field>

//       <Field label="Price (KES)" required>
//         <input type="text" inputMode="numeric" className={styles.inputField}
//           placeholder="e.g., 50,000" value={priceDisplay} onChange={handlePriceChange} />
//       </Field>

//       <Field label="Is Price Negotiable?" required>
//         <select className={styles.inputField} value={negotiable} onChange={e => setNegotiable(e.target.value)}>
//           <option value="" disabled>Select price type...</option>
//           <option value="fixed">🔒 Fixed Price</option>
//           <option value="negotiable">🤝 Slightly Negotiable</option>
//         </select>
//       </Field>

//       <Field label="City/Town" required>
//         <CityPicker value={city} onChange={setCity} placeholder="Search city or town..." />
//       </Field>

//       <Field label="Description" required>
//         <textarea className={`${styles.inputField} ${styles.textareaField}`}
//           placeholder="Describe your item in detail..." rows={4}
//           value={description} onChange={e => setDescription(e.target.value)} />
//       </Field>

//       <Field label="Contact (Email or Phone)" required>
//         <input className={styles.inputField}
//           placeholder="e.g., +254712345678 or email@example.com"
//           value={contact} onChange={e => setContact(e.target.value)} />
//       </Field>

//       {maxVideos > 0 && (
//         <Field label="Upload Videos (Optional)">
//           <input type="file" className={styles.fileInput}
//             accept="video/mp4,video/webm,video/ogg,video/quicktime" multiple onChange={handleVideos} />
//           <p className={styles.helperText}>🎥 Max {maxVideos} videos, 50MB each</p>
//           {videos.length > 0 && <p className={styles.selectedFiles}>✅ {videos.length}/{maxVideos} selected</p>}
//         </Field>
//       )}

//       <Field label="Upload Images *">
//         <input type="file" className={styles.fileInput} accept="image/*" multiple onChange={handleImages} />
//         <p className={styles.helperText}>📷 Max {maxImages} images ({planName} plan)</p>
//         {images.length > 0 && <p className={styles.selectedFiles}>✅ {images.length}/{maxImages} selected</p>}
//       </Field>

//       {maxVideos === 0 && (
//         <div className={styles.upgradePrompt}>
//           <p>🎥 Want to add videos? Upgrade to <strong>Business Plan</strong> for up to 2 videos!</p>
//         </div>
//       )}

//       <button type="submit" className={styles.submitAdBtn}>Post Ad</button>
//     </form>
//   );
// };

// // ══════════════════════════════════════════════════════════════════════════════
// // MAIN MODAL
// // ══════════════════════════════════════════════════════════════════════════════
// const PostAdModal = ({ isOpen, onClose, onAdPosted, selectedPlan, onBackToPlan }) => {
//   const { isAuthenticated, getToken } = useAuthContext();
//   const [chosenCategory, setChosenCategory] = useState(null);
  
//   // ✅ NEW: Promotion state
//   const [promotion, setPromotion] = useState(null);
//   const [promotionLoading, setPromotionLoading] = useState(false);
//   const [finalPrice, setFinalPrice] = useState(null);

//   // ✅ Payment state — holds form data + ad_id while waiting for M-Pesa confirmation
//   const [showPayment,     setShowPayment]     = useState(false);
//   const [pendingFormData, setPendingFormData] = useState(null);
//   const [pendingAdId,     setPendingAdId]     = useState(null);

//   // ✅ NEW: Check for promotion when modal opens or plan changes
//   useEffect(() => {
//     if (isOpen && selectedPlan && isAuthenticated) {
//       checkPromotion();
//     }
//   }, [isOpen, selectedPlan, isAuthenticated]);

//   // ✅ NEW: Check user promotion
//   const checkPromotion = async () => {
//     if (!selectedPlan) return;
    
//     setPromotionLoading(true);
//     try {
//       const planMap = {
//         'free':     '',
//         'basic':    'basic',
//         'pro':      'pro',      // ✅ FIX: 'pro' was missing — Pro plan never got a promo
//         'business': 'business',
//       };
//       const planParam = planMap[selectedPlan.planId] || '';
      
//       const promo = await checkUserPromotion(planParam);
      
//       if (promo.has_promotion && promo.promotion) {
//         setPromotion(promo.promotion);
        
//         // Calculate final price
//         const originalPrice = selectedPlan.price || 0;
//         const pricingInfo = calculatePromotionPrice(
//           originalPrice, 
//           promo.promotion, 
//           planParam
//         );
        
//         setFinalPrice(pricingInfo);
        
//         console.log('✅ Promotion applied:', pricingInfo);
//       } else {
//         setPromotion(null);
//         setFinalPrice(null);
//       }
//     } catch (error) {
//       console.error('Promotion check failed:', error);
//       setPromotion(null);
//       setFinalPrice(null);
//     } finally {
//       setPromotionLoading(false);
//     }
//   };

//   if (!isOpen) return null;

//   const maxImages  = selectedPlan?.maxImages || 2;
//   const maxVideos  = selectedPlan?.maxVideos || 0;
//   const planName   = selectedPlan?.planName  || 'Free';
//   const isBusiness = selectedPlan?.planId === 'business';
  
//   // ✅ NEW: Use promotional price if available
//   const displayPrice = finalPrice?.isFree 
//     ? 0 
//     : finalPrice?.finalPrice ?? selectedPlan?.price ?? 0;

//   const handleClose = () => {
//     setChosenCategory(null);
//     setPromotion(null);
//     setFinalPrice(null);
//     setShowPayment(false);
//     setPendingFormData(null);
//     setPendingAdId(null);
//     onClose();
//   };

//   // ── Determine if this plan needs payment ─────────────────────────────────
//   const needsPayment = () => {
//     if (!selectedPlan || !selectedPlan.price || selectedPlan.price === 0) return false;
//     if (selectedPlan.planId === 'free') return false;
//     // If user has an active promo that makes it free — skip payment
//     if (finalPrice?.isFree) return false;
//     return true;
//   };

//   // ── Called by sub-forms (Rooms / LostFound / Standard) on submit ──────────
//   const handleSubmit = async (formData) => {
//     if (!isAuthenticated) return alert('Please log in to post an ad');
//     if (selectedPlan) {
//       formData.append('plan_id',   selectedPlan.planId   || '');
//       formData.append('plan_name', selectedPlan.planName || 'Free');
//       // ✅ FIX: send the actual (possibly discounted) amount so ad_views.py
//       //    stores it in AdPlan.amount_paid instead of the hardcoded full price.
//       const _displayPrice = (finalPrice?.isFree)
//         ? 0
//         : (finalPrice?.finalPrice ?? selectedPlan?.price ?? 0);
//       formData.append('plan_amount', String(_displayPrice));
//     }

//     // ── If paid plan and no promo → create ad as pending, then show payment ─
//     if (needsPayment()) {
//       // Step 1: POST the ad to backend with status pending
//       // This gives us an ad_id which M-Pesa callback needs to activate the plan
//       let adId = null;
//       try {
//         const res = await apiService.postAd(formData, getToken());
//         let data = {};
//         try { data = await res.json(); } catch (_) {}
//         if (!res.ok) {
//           alert(data?.error || data?.detail || 'Failed to save ad. Please try again.');
//           return;
//         }
//         adId = data?.id || data?.ad_id || null;
//         console.log('✅ Ad created (pending payment), id=', adId);
//       } catch (err) {
//         console.error(err);
//         alert('Network error. Please try again.');
//         return;
//       }

//       // Step 2: Show payment modal with the real ad_id
//       setPendingAdId(adId);
//       setPendingFormData(formData);
//       setShowPayment(true);
//       return;   // wait for payment confirmation
//     }

//     // ── Free / promo → post immediately ──────────────────────────────────
//     await postAdToBackend(formData);
//   };

//   // ── Actually POST the ad (called after payment OR directly for free) ──────
//   const postAdToBackend = async (formData) => {
//     try {
//       const response = await apiService.postAd(formData, getToken());
//       let data = {};
//       try { data = await response.json(); } catch (_) {}

//       if (response.ok) {
//         // ✅ Consume promotion slot after successful free/promo post
//         if (promotion && selectedPlan) {
//           const planMap = {
//             'free':     '',
//             'basic':    'basic',
//             'pro':      'pro',      // ✅ FIX: 'pro' was missing — Pro plan never decremented
//             'business': 'business',
//           };
//           const planParam = planMap[selectedPlan.planId] || '';
//           if (planParam) {
//             try {
//               await consumePromotionSlot(planParam);
//               console.log('✅ Promotion slot consumed for plan:', planParam);
//             } catch (err) {
//               console.error('⚠️ Slot consume failed (non-fatal):', err);
//             }
//           }
//         }
//         alert('Ad posted successfully!');
//         handleClose();
//         if (typeof onAdPosted === 'function') onAdPosted();
//       } else {
//         alert(data?.error || data?.detail || 'Failed to post ad. Please try again.');
//       }
//     } catch (err) {
//       console.error(err);
//       alert('Network error. Please try again.');
//     }
//   };

//   // ── Payment confirmed → ad already exists, just notify parent ──────────────
//   // The Django M-Pesa callback already activated AdPlan when payment was confirmed.
//   // ✅ FIX: We MUST consume the promotion slot here too.
//   //    Previously this was only called in postAdToBackend (the free/promo path),
//   //    but discounted plans (e.g. Basic at KES 35) still go through the payment
//   //    path because needsPayment() returns true (price > 0).
//   //    Result: slot was NEVER decremented for discounted paid plans.
//   const handlePaymentSuccess = async (paymentData) => {
//     // ✅ Consume the promotion slot if the user had an active promo on this plan
//     if (promotion && selectedPlan) {
//       const planMap = {
//         'free':     '',
//         'basic':    'basic',
//         'pro':      'pro',
//         'business': 'business',
//       };
//       const planParam = planMap[selectedPlan.planId] || '';
//       if (planParam) {
//         try {
//           await consumePromotionSlot(planParam);
//           console.log('✅ Promotion slot consumed after payment for plan:', planParam);
//         } catch (err) {
//           console.error('⚠️ Slot consume after payment failed (non-fatal):', err);
//         }
//       }
//     }

//     setShowPayment(false);
//     setPendingFormData(null);
//     setPendingAdId(null);
//     alert('Ad posted successfully! Your plan is now active.');
//     handleClose();
//     if (typeof onAdPosted === 'function') onAdPosted();
//   };

//   const titles = {
//     [ROOMS_CAT]:      { title: 'Post Your Listing',  sub: 'Fill in the details to list your room' },
//     [LOST_FOUND_CAT]: { title: 'Post Lost & Found',  sub: 'Fill in the details to report the item' },
//     default:          { title: 'Post Your Ad',        sub: 'Fill in the details to list your item' },
//   };
//   const { title, sub } = titles[chosenCategory] || titles.default;

//   return (
//     <>
//     <div className={styles.modalOverlay} onClick={handleClose}>
//       <div className={styles.postAdModal} onClick={e => e.stopPropagation()}>
//         <button className={styles.closeBtn} onClick={handleClose}>×</button>
//         <h2 className={styles.modalTitle}>{title}</h2>
//         <p className={styles.modalSubtitle}>{sub}</p>

//         {/* ── Plan banner ── */}
//         {selectedPlan && (
//           <div className={`${styles.selectedPlanBanner} ${isBusiness ? styles.selectedPlanBannerBusiness : ''}`}>
//             <div className={styles.planInfo}>
//               <span className={`${styles.planBadge} ${isBusiness ? styles.planBadgeBusiness : ''}`}>
//                 {isBusiness ? '⭐ ' : ''}{planName}
//               </span>
//               <div className={styles.planLimits}>
//                 <span>📷 Up to {maxImages} images</span>
//                 {maxVideos > 0 && <span>🎥 Up to {maxVideos} videos</span>}
//                 {selectedPlan.displayDays && <span>⏱️ {selectedPlan.displayDays} days</span>}
//               </div>
//             </div>
            
//             {/* ← Change Plan button */}
//             {onBackToPlan && (
//               <button
//                 onClick={onBackToPlan}
//                 style={{
//                   background: 'none', border: '1.5px solid rgba(255,255,255,0.5)',
//                   borderRadius: 8, color: 'inherit', fontSize: 12, fontWeight: 600,
//                   padding: '4px 12px', cursor: 'pointer', whiteSpace: 'nowrap',
//                   opacity: 0.85, transition: 'opacity .15s',
//                 }}
//                 onMouseEnter={e => e.target.style.opacity = 1}
//                 onMouseLeave={e => e.target.style.opacity = 0.85}
//               >
//                 ← Change Plan
//               </button>
//             )}

//           {/* ✅ NEW: Show promotional or regular price */}
//             {displayPrice > 0 ? (
//               <div className={`${styles.planPrice} ${isBusiness ? styles.planPriceBusiness : ''}`}>
//                 {finalPrice && finalPrice.discountAmount > 0 && (
//                   <div className={styles.originalPrice}>
//                     <del>KES {selectedPlan.price}</del>
//                   </div>
//                 )}
//                 <div>KES {displayPrice}</div>
//               </div>
//             ) : displayPrice === 0 && finalPrice?.isFree ? (
//               <div className={`${styles.planPrice} ${styles.planPriceFree}`}>
//                 FREE
//               </div>
//             ) : null}
//           </div>
//         )}

//         {/* ✅ NEW: Promotion label */}
//         {finalPrice && finalPrice.label && (
//           <div className={styles.promotionBanner}>
//             {finalPrice.label}
//           </div>
//         )}

//         {/* ── Business slot notice ── */}
//         {isBusiness && (
//           <div className={styles.slotBanner}>
//             🔄 <strong>Slot-based plan:</strong> Pay once &amp; post freely — delete and re-post
//             your ad any time within the 3-month window at no extra charge.
//           </div>
//         )}

//         {/* ── Category picker ── */}
//         {chosenCategory === null && (
//           <div className={styles.categoryPicker}>
//             <div className={styles.formGroup}>
//               <label>Category <span className={styles.req}>*</span></label>
//               <select
//                 className={styles.inputField}
//                 defaultValue=""
//                 onChange={e => { if (e.target.value) setChosenCategory(e.target.value); }}
//               >
//                 <option value="" disabled>Select a category...</option>
//                 {CATEGORIES.filter(c => c !== 'All').map(c => (
//                   <option key={c} value={c}>{c}</option>
//                 ))}
//               </select>
//             </div>
//           </div>
//         )}

//         {chosenCategory === ROOMS_CAT && (
//           <RoomsForm maxImages={maxImages} maxVideos={maxVideos} planName={planName} onSubmit={handleSubmit} />
//         )}
//         {chosenCategory === LOST_FOUND_CAT && (
//           <LostFoundForm maxVideos={maxVideos} planName={planName} onSubmit={handleSubmit} />
//         )}
//         {chosenCategory !== null && chosenCategory !== ROOMS_CAT && chosenCategory !== LOST_FOUND_CAT && (
//           <StandardForm initialCategory={chosenCategory} maxImages={maxImages}
//             maxVideos={maxVideos} planName={planName} onSubmit={handleSubmit} />
//         )}

//         {chosenCategory !== null && (
//           <button className={styles.backBtn} onClick={() => setChosenCategory(null)}>
//             ← Change category
//           </button>
//         )}
//       </div>
//     </div>

//     {/* ── M-Pesa Payment Modal — shown after user fills form and clicks submit ── */}
//     {/* ✅ FIX: pass the discounted price to PaymentModal, not the original plan price */}
//     <PaymentModal
//       isOpen={showPayment}
//       plan={{
//         ...selectedPlan,
//         // If a promotion reduced the price, use finalPrice.finalPrice; otherwise keep original
//         price: (finalPrice && !finalPrice.isFree && finalPrice.finalPrice != null)
//           ? finalPrice.finalPrice
//           : selectedPlan?.price ?? 0,
//       }}
//       adId={pendingAdId}
//       onSuccess={handlePaymentSuccess}
//       onClose={() => { setShowPayment(false); setPendingFormData(null); setPendingAdId(null); }}
//     />
//     </>
//   );
// };

// export default PostAdModal;














































































// // import React, { useState, useRef, useEffect } from 'react';
// // import { useAuthContext } from '../../../context/AuthContext';
// // import apiService from '../../../services/api';
// // import { CATEGORIES, SUB_CATEGORIES } from '../../../utils/constants';
// // import { checkUserPromotion, consumePromotionSlot, calculatePromotionPrice } from '../../../utils/promotionApi'; // ✅ NEW
// // import styles from './PostAdModal.module.css';
// // import PaymentModal from './PaymentModal';

// // const ROOMS_CAT      = 'Rooms to Let';
// // const LOST_FOUND_CAT = 'Lost & Found';

// // // ── Location search via OpenStreetMap Nominatim ───────────────────────────
// // const searchKenyaPlaces = async (query) => {
// //   if (!query || query.trim().length < 2) return [];
// //   try {
// //     const url =
// //       `https://nominatim.openstreetmap.org/search` +
// //       `?q=${encodeURIComponent(query + ', Kenya')}` +
// //       `&countrycodes=ke&limit=12&format=json&addressdetails=1&accept-language=en`;
// //     const res = await fetch(url, {
// //       headers: { 'User-Agent': 'ImSellingKE/1.0 (marketplace)' }
// //     });
// //     const data = await res.json();
// //     return data.map(item => {
// //       const a = item.address;
// //       const label =
// //         a.suburb || a.neighbourhood || a.quarter || a.village ||
// //         a.hamlet || a.town || a.city_district || a.city ||
// //         item.display_name.split(',')[0];
// //       const parent =
// //         a.suburb        ? (a.town || a.city || a.county) :
// //         a.village       ? (a.town || a.city || a.county) :
// //         a.hamlet        ? (a.town || a.city || a.county) :
// //         a.neighbourhood ? (a.city || a.county)           : null;
// //       const display = parent ? `${label.trim()}, ${parent.trim()}` : label.trim();
// //       return { label: label.trim(), display, osm_id: item.osm_id };
// //     }).filter((v, i, arr) => arr.findIndex(x => x.display === v.display) === i);
// //   } catch (e) {
// //     console.error('Location search error:', e);
// //     return [];
// //   }
// // };

// // const Field = ({ label, required, children }) => (
// //   <div className={styles.formGroup}>
// //     <label>{label} {required && <span className={styles.req}>*</span>}</label>
// //     {children}
// //   </div>
// // );

// // // ── CityPicker ────────────────────────────────────────────────────────────
// // const CityPicker = ({ value, onChange, placeholder = 'Search village, estate, street, town...' }) => {
// //   const [query,     setQuery]     = useState(value || '');
// //   const [results,   setResults]   = useState([]);
// //   const [open,      setOpen]      = useState(false);
// //   const [active,    setActive]    = useState(-1);
// //   const [loading,   setLoading]   = useState(false);
// //   const [confirmed, setConfirmed] = useState(!!value);
// //   const wrapRef  = useRef(null);
// //   const inputRef = useRef(null);
// //   const timerRef = useRef(null);

// //   useEffect(() => {
// //     const fn = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
// //     document.addEventListener('mousedown', fn);
// //     return () => document.removeEventListener('mousedown', fn);
// //   }, []);

// //   const runSearch = (q) => {
// //     clearTimeout(timerRef.current);
// //     if (!q || q.trim().length < 2) { setResults([]); setLoading(false); return; }
// //     setLoading(true);
// //     timerRef.current = setTimeout(async () => {
// //       const found = await searchKenyaPlaces(q);
// //       setResults(found);
// //       setOpen(found.length > 0);
// //       setActive(0);
// //       setLoading(false);
// //     }, 400);
// //   };

// //   const select = (item) => {
// //     setQuery(item.display);
// //     onChange(item.display);
// //     setConfirmed(true);
// //     setOpen(false);
// //     setResults([]);
// //   };

// //   const clear = () => {
// //     setQuery(''); onChange('');
// //     setConfirmed(false); setResults([]); setOpen(false);
// //     clearTimeout(timerRef.current);
// //     inputRef.current?.focus();
// //   };

// //   const handleInput = (e) => {
// //     const v = e.target.value;
// //     setQuery(v);
// //     setConfirmed(false);
// //     onChange('');
// //     runSearch(v);
// //   };

// //   const handleKey = (e) => {
// //     if (e.key === 'ArrowDown') { e.preventDefault(); setActive(i => Math.min(i + 1, results.length - 1)); }
// //     if (e.key === 'ArrowUp')   { e.preventDefault(); setActive(i => Math.max(i - 1, 0)); }
// //     if (e.key === 'Enter')     { e.preventDefault(); if (results[active]) select(results[active]); }
// //     if (e.key === 'Escape')    { setOpen(false); }
// //   };

// //   return (
// //     <div ref={wrapRef} className={styles.cityWrap}>
// //       <div className={styles.cityInputWrap}>
// //         <span className={styles.citySearchIcon}>🔍</span>
// //         <input
// //           ref={inputRef}
// //           type="text"
// //           className={`${styles.inputField} ${styles.cityInput} ${confirmed ? styles.cityInputConfirmed : ''}`}
// //           placeholder={placeholder}
// //           value={query}
// //           onChange={handleInput}
// //           onFocus={() => results.length > 0 && setOpen(true)}
// //           onKeyDown={handleKey}
// //           autoComplete="off"
// //           spellCheck="false"
// //         />
// //         {loading && <span className={styles.citySpinner} title="Searching...">⏳</span>}
// //         {!loading && query && (
// //           <button type="button" className={styles.cityClear} onClick={clear}>×</button>
// //         )}
// //       </div>
// //       {confirmed && <p className={styles.cityConfirmedMsg}>✅ Location set</p>}
// //       {!confirmed && query.length === 1 && (
// //         <p className={styles.cityHint}>Type one more character to search…</p>
// //       )}
// //       {open && results.length > 0 && (
// //         <ul className={styles.cityDropdown}>
// //           {results.map((item, i) => (
// //             <li
// //               key={`${item.osm_id}-${i}`}
// //               className={`${styles.cityOption} ${i === active ? styles.cityOptionActive : ''}`}
// //               onMouseDown={() => select(item)}
// //               onMouseEnter={() => setActive(i)}
// //             >
// //               <span className={styles.cityPin}>📍</span>
// //               <span>
// //                 <span className={styles.cityOptionLabel}>{item.label}</span>
// //                 {item.display !== item.label && (
// //                   <span className={styles.cityOptionSub}>
// //                     {', ' + item.display.replace(item.label + ', ', '')}
// //                   </span>
// //                 )}
// //               </span>
// //             </li>
// //           ))}
// //         </ul>
// //       )}
// //       {!loading && open && results.length === 0 && query.length >= 2 && (
// //         <div className={styles.cityNoResult}>
// //           No results for "<strong>{query}</strong>" — try a nearby street or area name
// //         </div>
// //       )}
// //     </div>
// //   );
// // };

// // // ══════════════════════════════════════════════════════════════════════════════
// // // ROOMS TO LET FORM
// // // ══════════════════════════════════════════════════════════════════════════════
// // const RoomsForm = ({ maxImages, maxVideos, planName, onSubmit }) => {
// //   const [title,        setTitle]        = useState('');
// //   const [location,     setLocation]     = useState('');
// //   const [price,        setPrice]        = useState('');
// //   const [priceDisplay, setPriceDisplay] = useState('');
// //   const [rentalPeriod, setRentalPeriod] = useState('month'); // ✅ NEW — rental period dropdown
// //   const [negotiable,   setNegotiable]   = useState('');
// //   const [propType,     setPropType]     = useState('');
// //   const [furnished,    setFurnished]    = useState('');
// //   const [availFrom,    setAvailFrom]    = useState('');
// //   const [description,  setDescription]  = useState('');
// //   const [contact,      setContact]      = useState('');
// //   const [images,       setImages]       = useState([]);
// //   const [videos,       setVideos]       = useState([]);

// //   const handleRoomPrice = (e) => {
// //     const raw = e.target.value.replace(/,/g, '').replace(/[^\d]/g, '');
// //     setPrice(raw);
// //     setPriceDisplay(raw ? Number(raw).toLocaleString('en-KE') : '');
// //   };

// //   const handleImages = (e) => {
// //     const files = Array.from(e.target.files);
// //     if (files.length > maxImages)
// //       alert(`Only the first ${maxImages} photo${maxImages > 1 ? 's' : ''} will be uploaded. The rest will be ignored.`);
// //     setImages(files.slice(0, maxImages));
// //   };

// //   const handleVideos = (e) => {
// //     if (maxVideos === 0) return alert('Video upload requires Business plan');
// //     const all = Array.from(e.target.files);
// //     const valid = all.filter(f => f.size <= 50*1024*1024);
// //     const oversized = all.length - valid.length;
// //     const overCount = valid.length - maxVideos;
// //     if (oversized > 0) alert(`${oversized} video${oversized > 1 ? 's were' : ' was'} removed (exceeds 50MB limit).`);
// //     if (overCount > 0) alert(`Only ${maxVideos} video${maxVideos > 1 ? 's' : ''} allowed. The rest will be ignored.`);
// //     setVideos(valid.slice(0, maxVideos));
// //   };

// //   const handleSubmit = (e) => {
// //     e.preventDefault();
// //     if (!title)            return alert('Please enter a title');
// //     if (title.length > 25) return alert('Title must be 25 characters or less');
// //     if (!location)         return alert('Please select a location');
// //     if (!price)            return alert('Please enter rental price');
// //     if (!negotiable)       return alert('Please select price type');
// //     if (!propType)         return alert('Please select property type');
// //     if (!furnished)        return alert('Please select furnished option');
// //     if (!availFrom)        return alert('Please select availability date');
// //     if (!description)      return alert('Please add a description');
// //     if (!contact)          return alert('Please enter contact info');

// //     const periodLabels = { hour: 'Hour', day: 'Day', week: 'Week', month: 'Month', year: 'Year' };
// //     const periodLabel = periodLabels[rentalPeriod] || 'Month';
// //     const fd = new FormData();
// //     fd.append('title',         title);
// //     fd.append('description',   `${description}\n\nProperty Type: ${propType}\nFurnished: ${furnished}\nAvailable From: ${availFrom}\nRental Period: Per ${periodLabel}`);
// //     fd.append('price',         price);
// //     fd.append('category_name', ROOMS_CAT);
// //     fd.append('condition',     'new');
// //     fd.append('price_type',    negotiable);
// //     fd.append('contact',       contact);
// //     fd.append('city',          location);
// //     fd.append('plan_name',     planName);
// //     fd.append('sub_category',  rentalPeriod); // ✅ store rental period for display
// //     images.forEach((img, i) => fd.append(`image_${i}`, img));
// //     videos.forEach((vid, i) => fd.append(`video_${i}`, vid));
// //     onSubmit(fd);
// //   };

// //   return (
// //     <form onSubmit={handleSubmit} className={styles.postAdForm}>
// //       <Field label={`Title (${title.length}/25)`} required>
// //         <input className={styles.inputField} placeholder="e.g., SPACIOUS 2 BEDROOM"
// //           value={title} maxLength={25}
// //           onChange={e => setTitle(e.target.value.toUpperCase())} />
// //       </Field>
// //       <Field label="Category" required>
// //         <div className={styles.lockedCategory}>🏠 Rooms to Let</div>
// //       </Field>
// //       <Field label="Location" required>
// //         <CityPicker value={location} onChange={setLocation} placeholder="Search city or town..." />
// //       </Field>
// //       <Field label="Rental Price (KES)" required>
// //         {/* ✅ Inline price + rental period row */}
// //         <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
// //           <input
// //             type="text" inputMode="numeric"
// //             className={styles.inputField}
// //             style={{ flex: 1 }}
// //             placeholder="e.g., 10,000"
// //             value={priceDisplay}
// //             onChange={handleRoomPrice}
// //           />
// //           <select
// //             className={styles.inputField}
// //             style={{ width: 110, flexShrink: 0 }}
// //             value={rentalPeriod}
// //             onChange={e => setRentalPeriod(e.target.value)}
// //             title="Rental period"
// //           >
// //             <option value="hour">/ Hour</option>
// //             <option value="day">/ Day</option>
// //             <option value="week">/ Week</option>
// //             <option value="month">/ Month</option>
// //             <option value="year">/ Year</option>
// //           </select>
// //         </div>
// //         {price && (
// //           <p style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
// //             Price: <strong>KES {priceDisplay} / {rentalPeriod.charAt(0).toUpperCase() + rentalPeriod.slice(1)}</strong>
// //           </p>
// //         )}
// //       </Field>
// //       <div className={styles.twoCol}>
// //         <Field label="Is Rent Negotiable?" required>
// //           <select className={styles.inputField} value={negotiable} onChange={e => setNegotiable(e.target.value)}>
// //             <option value="" disabled>Select price type...</option>
// //             <option value="fixed">🔒 Fixed Price</option>
// //             <option value="negotiable">🤝 Negotiable</option>
// //           </select>
// //         </Field>
// //         <Field label="Property Type" required>
// //           <select className={styles.inputField} value={propType} onChange={e => setPropType(e.target.value)}>
// //             <option value="" disabled>Select type...</option>
// //             <option value="bedsitter">Bedsitter</option>
// //             <option value="single_room">Single Room</option>
// //             <option value="1_bedroom">1 Bedroom</option>
// //             <option value="2_bedroom">2 Bedroom</option>
// //             <option value="3_bedroom">3 Bedroom</option>
// //             <option value="studio">Studio</option>
// //             <option value="mansion">Mansion</option>
// //             <option value="airbnb">AirBnB</option>
// //             <option value="warehouse">Warehouse</option>
// //             <option value="office">Office Space</option>
// //             <option value="shop">Shop / Stall</option>
// //           </select>
// //         </Field>
// //       </div>
// //       <div className={styles.twoCol}>
// //         <Field label="Furnished?" required>
// //           <select className={styles.inputField} value={furnished} onChange={e => setFurnished(e.target.value)}>
// //             <option value="" disabled>Select option...</option>
// //             <option value="fully_furnished">Fully Furnished</option>
// //             <option value="semi_furnished">Semi-Furnished</option>
// //             <option value="unfurnished">Unfurnished</option>
// //           </select>
// //         </Field>
// //         <Field label="Available From" required>
// //           <input type="date" className={styles.inputField}
// //             value={availFrom} onChange={e => setAvailFrom(e.target.value)}
// //             min={new Date().toISOString().split('T')[0]} />
// //         </Field>
// //       </div>
// //       <Field label="Description" required>
// //         <textarea className={`${styles.inputField} ${styles.textareaField}`}
// //           placeholder="Describe your room and amenities..." rows={4}
// //           value={description} onChange={e => setDescription(e.target.value)} />
// //       </Field>
// //       <Field label="Contact (Email or Phone)" required>
// //         <input className={styles.inputField}
// //           placeholder="e.g., +254712345678 or email@example.com"
// //           value={contact} onChange={e => setContact(e.target.value)} />
// //       </Field>
// //       {maxVideos > 0 && (
// //         <Field label="Upload Videos (Optional)">
// //           <input type="file" className={styles.fileInput}
// //             accept="video/mp4,video/webm,video/ogg,video/quicktime" multiple onChange={handleVideos} />
// //           <p className={styles.helperText}>🎥 Max {maxVideos} videos, 50MB each</p>
// //           {videos.length > 0 && <p className={styles.selectedFiles}>✅ {videos.length}/{maxVideos} selected</p>}
// //         </Field>
// //       )}
// //       <Field label="Upload Images (Optional)">
// //         <div className={styles.uploadBox}>
// //           <label className={styles.uploadLabel} htmlFor="rooms-images">
// //             <span>📷</span> Choose Files
// //           </label>
// //           <input id="rooms-images" type="file" className={styles.hiddenInput}
// //             accept="image/*" multiple onChange={handleImages} />
// //           <p className={styles.helperText}>Upload up to {maxImages} photos (Max 10 MB each)</p>
// //           {images.length > 0 && <p className={styles.selectedFiles}>✅ {images.length}/{maxImages} selected</p>}
// //         </div>
// //       </Field>
// //       {maxVideos === 0 && (
// //         <div className={styles.upgradePrompt}>
// //           <p>🎥 Want to add videos? Upgrade to <strong>Business Plan</strong> for up to 2 videos!</p>
// //         </div>
// //       )}
// //       <button type="submit" className={styles.submitAdBtn}>Post Listing</button>
// //     </form>
// //   );
// // };

// // // ══════════════════════════════════════════════════════════════════════════════
// // // LOST & FOUND FORM
// // // ══════════════════════════════════════════════════════════════════════════════
// // const LostFoundForm = ({ maxVideos, planName, onSubmit }) => {
// //   const [itemStatus,   setItemStatus]   = useState('lost');
// //   const [itemName,     setItemName]     = useState('');
// //   const [itemCategory, setItemCategory] = useState('');
// //   const [location,     setLocation]     = useState('');
// //   const [dateLost,     setDateLost]     = useState('');
// //   const [description,  setDescription]  = useState('');
// //   const [contact,      setContact]      = useState('');
// //   const [images,       setImages]       = useState([]);
// //   const [videos,       setVideos]       = useState([]);

// //   const LF_CATS = [
// //     'Electronics','Documents & ID','Pets','Bags & Luggage',
// //     'Clothing & Accessories','Keys','Wallets & Purses','Jewellery',
// //     'Vehicles','Books & Stationery','Other',
// //   ];

// //   const handleLFVideos = (e) => {
// //     if (maxVideos === 0) return alert('Video upload requires Business plan');
// //     const all = Array.from(e.target.files);
// //     const valid = all.filter(f => f.size <= 50*1024*1024);
// //     const oversized = all.length - valid.length;
// //     const overCount = valid.length - maxVideos;
// //     if (oversized > 0) alert(`${oversized} video${oversized > 1 ? 's were' : ' was'} removed (exceeds 50MB limit).`);
// //     if (overCount > 0) alert(`Only ${maxVideos} video${maxVideos > 1 ? 's' : ''} allowed. The rest will be ignored.`);
// //     setVideos(valid.slice(0, maxVideos));
// //   };

// //   const handleSubmit = (e) => {
// //     e.preventDefault();
// //     if (!itemName)     return alert('Please enter item name');
// //     if (!itemCategory) return alert('Please select a category');
// //     if (!location)     return alert('Please enter a location');
// //     if (!dateLost)     return alert('Please select date lost/found');
// //     if (!description)  return alert('Please add a description');
// //     if (!contact)      return alert('Please enter contact info');

// //     const fd = new FormData();
// //     fd.append('title',         `[${itemStatus.toUpperCase()}] ${itemName}`);
// //     fd.append('description',   `Status: ${itemStatus}\nCategory: ${itemCategory}\nDate: ${dateLost}\n\n${description}`);
// //     fd.append('price',         '0');
// //     fd.append('category_name', LOST_FOUND_CAT);
// //     fd.append('condition',     'used');
// //     fd.append('price_type',    'fixed');
// //     fd.append('contact',       contact);
// //     fd.append('city',          location);
// //     fd.append('plan_name',     planName);
// //     images.forEach((img, i) => fd.append(`image_${i}`, img));
// //     videos.forEach((vid, i) => fd.append(`video_${i}`, vid));
// //     onSubmit(fd);
// //   };

// //   return (
// //     <form onSubmit={handleSubmit} className={styles.postAdForm}>
// //       <Field label="Status" required>
// //         <select className={styles.inputField} value={itemStatus} onChange={e => setItemStatus(e.target.value)}>
// //           <option value="lost">🔍 Lost</option>
// //           <option value="found">✅ Found</option>
// //         </select>
// //       </Field>
// //       <Field label={`Item Name (${itemName.length}/25)`} required>
// //         <input className={styles.inputField}
// //           placeholder="e.g., BLACK BACKPACK, CAR KEY"
// //           value={itemName} maxLength={25}
// //           onChange={e => setItemName(e.target.value.toUpperCase())} />
// //       </Field>
// //       <Field label="Category" required>
// //         <select className={styles.inputField} value={itemCategory} onChange={e => setItemCategory(e.target.value)}>
// //           <option value="" disabled>Select category...</option>
// //           {LF_CATS.map(c => <option key={c} value={c}>{c}</option>)}
// //         </select>
// //       </Field>
// //       <Field label={itemStatus === 'lost' ? 'Where Was It Lost?' : 'Where Was It Found?'} required>
// //         <input className={styles.inputField}
// //           placeholder="e.g., Central Mall, Nairobi CBD, Uhuru Park"
// //           value={location} onChange={e => setLocation(e.target.value)} />
// //       </Field>
// //       <Field label="Date Lost/Found" required>
// //         <input type="date" className={styles.inputField}
// //           value={dateLost} onChange={e => setDateLost(e.target.value)}
// //           max={new Date().toISOString().split('T')[0]} />
// //       </Field>
// //       <Field label="Description" required>
// //         <textarea className={`${styles.inputField} ${styles.textareaField}`}
// //           placeholder="Describe the item, its appearance, and how to contact you..."
// //           rows={4} value={description} onChange={e => setDescription(e.target.value)} />
// //       </Field>
// //       <Field label="Contact (Email or Phone)" required>
// //         <input className={styles.inputField}
// //           placeholder="e.g., +254712345678 or email@example.com"
// //           value={contact} onChange={e => setContact(e.target.value)} />
// //       </Field>
// //       {maxVideos > 0 && (
// //         <Field label="Upload Videos (Optional)">
// //           <input type="file" className={styles.fileInput}
// //             accept="video/mp4,video/webm,video/ogg,video/quicktime" multiple onChange={handleLFVideos} />
// //           <p className={styles.helperText}>🎥 Max {maxVideos} videos, 50MB each</p>
// //           {videos.length > 0 && <p className={styles.selectedFiles}>✅ {videos.length}/{maxVideos} selected</p>}
// //         </Field>
// //       )}
// //       <Field label="Upload Images (Optional)">
// //         <div className={styles.uploadBox}>
// //           <label className={styles.uploadLabel} htmlFor="lf-images">
// //             <span>📷</span> Upload Photos <span className={styles.uploadChevron}>▾</span>
// //           </label>
// //           <input id="lf-images" type="file" className={styles.hiddenInput}
// //             accept="image/*" multiple onChange={e => {
// //               const files = Array.from(e.target.files);
// //               if (files.length > 6) alert('Only the first 6 photos will be uploaded. The rest will be ignored.');
// //               setImages(files.slice(0, 6));
// //             }} />
// //           <p className={styles.helperText}>🖼️ Upload up to 6 photos: JPG, PNG (Max 10 MB each)</p>
// //           {images.length > 0 && <p className={styles.selectedFiles}>✅ {images.length}/6 selected</p>}
// //         </div>
// //       </Field>
// //       {maxVideos === 0 && (
// //         <div className={styles.upgradePrompt}>
// //           <p>🎥 Want to add videos? Upgrade to <strong>Business Plan</strong> for up to 2 videos!</p>
// //         </div>
// //       )}
// //       <button type="submit" className={styles.submitAdBtn}>Post Listing</button>
// //     </form>
// //   );
// // };

// // // ══════════════════════════════════════════════════════════════════════════════
// // // STANDARD FORM
// // // ══════════════════════════════════════════════════════════════════════════════
// // const StandardForm = ({ initialCategory, maxImages, maxVideos, planName, onSubmit }) => {
// //   const [title,        setTitle]        = useState('');
// //   const [category,     setCategory]     = useState(initialCategory || 'Electronics');
// //   const [subCategory,  setSubCategory]  = useState('');
// //   const [condition,    setCondition]    = useState('');
// //   const [price,        setPrice]        = useState('');
// //   const [priceDisplay, setPriceDisplay] = useState('');
// //   const [negotiable,   setNegotiable]   = useState('');
// //   const [city,         setCity]         = useState('');
// //   const [description,  setDescription]  = useState('');
// //   const [contact,      setContact]      = useState('');
// //   const [images,       setImages]       = useState([]);
// //   const [videos,       setVideos]       = useState([]);

// //   const handlePriceChange = (e) => {
// //     const raw = e.target.value.replace(/,/g, '').replace(/[^\d]/g, '');
// //     setPrice(raw);
// //     setPriceDisplay(raw ? Number(raw).toLocaleString('en-KE') : '');
// //   };

// //   const handleImages = (e) => {
// //     const files = Array.from(e.target.files);
// //     if (files.length > maxImages)
// //       alert(`Only the first ${maxImages} photo${maxImages > 1 ? 's' : ''} will be uploaded. The rest will be ignored.`);
// //     setImages(files.slice(0, maxImages));
// //   };

// //   const handleVideos = (e) => {
// //     if (maxVideos === 0) return alert('Video upload requires Business plan');
// //     const all = Array.from(e.target.files);
// //     const valid = all.filter(f => f.size <= 50*1024*1024);
// //     const oversized = all.length - valid.length;
// //     const overCount = valid.length - maxVideos;
// //     if (oversized > 0) alert(`${oversized} video${oversized > 1 ? 's were' : ' was'} removed (exceeds 50MB limit).`);
// //     if (overCount > 0) alert(`Only ${maxVideos} video${maxVideos > 1 ? 's' : ''} allowed. The rest will be ignored.`);
// //     setVideos(valid.slice(0, maxVideos));
// //   };

// //   const handleSubmit = (e) => {
// //     e.preventDefault();
// //     if (title.length < 5)        return alert('Title must be at least 5 characters');
// //     if (title.length > 25)       return alert('Title must be 25 characters or less');
// //     if (description.length < 10) return alert('Description must be at least 10 characters');
// //     if (!city)                   return alert('Please enter a city/town');
// //     if (!condition)              return alert('Please select product condition');
// //     if (!negotiable)             return alert('Please select price type');
// //     if (!contact)                return alert('Please enter contact info');
// //     if (!images.length && !videos.length) return alert('Please upload at least one image or video');

// //     const fd = new FormData();
// //     fd.append('title',         title);
// //     fd.append('description',   description);
// //     fd.append('price',         price);
// //     fd.append('category_name', category);
// //     if (subCategory) fd.append('sub_category', subCategory);
// //     fd.append('condition',     condition);
// //     fd.append('price_type',    negotiable);
// //     fd.append('contact',       contact);
// //     fd.append('city',          city);
// //     fd.append('plan_name',     planName);
// //     images.forEach((img, i) => fd.append(`image_${i}`, img));
// //     videos.forEach((vid, i) => fd.append(`video_${i}`, vid));
// //     onSubmit(fd);
// //   };

// //   return (
// //     <form onSubmit={handleSubmit} className={styles.postAdForm}>
// //       <Field label={`Title (${title.length}/25)`} required>
// //         <input className={styles.inputField} placeholder="e.g., IPHONE 13 PRO MAX"
// //           value={title} maxLength={25}
// //           onChange={e => setTitle(e.target.value.toUpperCase())} />
// //       </Field>

// //       <Field label="Category" required>
// //         <select className={styles.inputField} value={category}
// //           onChange={e => { setCategory(e.target.value); setSubCategory(''); }}>
// //           {CATEGORIES.filter(c => c !== 'All' && c !== ROOMS_CAT && c !== LOST_FOUND_CAT)
// //             .map(c => <option key={c} value={c}>{c}</option>)}
// //         </select>
// //       </Field>

// //       {/* ── Sub-category — optional, shown when category has defined sub-options ── */}
// //       {SUB_CATEGORIES[category] && (
// //         <Field label="Sub-Category (Optional)">
// //           <select className={`${styles.inputField} ${styles.subCategorySelect}`}
// //             value={subCategory}
// //             onChange={e => setSubCategory(e.target.value)}>
// //             <option value="">— Select sub-category —</option>
// //             {SUB_CATEGORIES[category].map(sub => (
// //               <option key={sub} value={sub}>{sub}</option>
// //             ))}
// //           </select>
// //           {subCategory && (
// //             <p className={styles.subCategoryChosen}>🏷️ {subCategory}</p>
// //           )}
// //         </Field>
// //       )}

// //       <Field label="Is Product Used?" required>
// //         <select className={styles.inputField} value={condition} onChange={e => setCondition(e.target.value)}>
// //           <option value="" disabled>Select condition...</option>
// //           <option value="new">✨ New</option>
// //           <option value="slightly_used">👌 Slightly Used</option>
// //           <option value="used">🔄 Used</option>
// //         </select>
// //       </Field>

// //       <Field label="Price (KES)" required>
// //         <input type="text" inputMode="numeric" className={styles.inputField}
// //           placeholder="e.g., 50,000" value={priceDisplay} onChange={handlePriceChange} />
// //       </Field>

// //       <Field label="Is Price Negotiable?" required>
// //         <select className={styles.inputField} value={negotiable} onChange={e => setNegotiable(e.target.value)}>
// //           <option value="" disabled>Select price type...</option>
// //           <option value="fixed">🔒 Fixed Price</option>
// //           <option value="negotiable">🤝 Slightly Negotiable</option>
// //         </select>
// //       </Field>

// //       <Field label="City/Town" required>
// //         <CityPicker value={city} onChange={setCity} placeholder="Search city or town..." />
// //       </Field>

// //       <Field label="Description" required>
// //         <textarea className={`${styles.inputField} ${styles.textareaField}`}
// //           placeholder="Describe your item in detail..." rows={4}
// //           value={description} onChange={e => setDescription(e.target.value)} />
// //       </Field>

// //       <Field label="Contact (Email or Phone)" required>
// //         <input className={styles.inputField}
// //           placeholder="e.g., +254712345678 or email@example.com"
// //           value={contact} onChange={e => setContact(e.target.value)} />
// //       </Field>

// //       {maxVideos > 0 && (
// //         <Field label="Upload Videos (Optional)">
// //           <input type="file" className={styles.fileInput}
// //             accept="video/mp4,video/webm,video/ogg,video/quicktime" multiple onChange={handleVideos} />
// //           <p className={styles.helperText}>🎥 Max {maxVideos} videos, 50MB each</p>
// //           {videos.length > 0 && <p className={styles.selectedFiles}>✅ {videos.length}/{maxVideos} selected</p>}
// //         </Field>
// //       )}

// //       <Field label="Upload Images *">
// //         <input type="file" className={styles.fileInput} accept="image/*" multiple onChange={handleImages} />
// //         <p className={styles.helperText}>📷 Max {maxImages} images ({planName} plan)</p>
// //         {images.length > 0 && <p className={styles.selectedFiles}>✅ {images.length}/{maxImages} selected</p>}
// //       </Field>

// //       {maxVideos === 0 && (
// //         <div className={styles.upgradePrompt}>
// //           <p>🎥 Want to add videos? Upgrade to <strong>Business Plan</strong> for up to 2 videos!</p>
// //         </div>
// //       )}

// //       <button type="submit" className={styles.submitAdBtn}>Post Ad</button>
// //     </form>
// //   );
// // };

// // // ══════════════════════════════════════════════════════════════════════════════
// // // MAIN MODAL
// // // ══════════════════════════════════════════════════════════════════════════════
// // const PostAdModal = ({ isOpen, onClose, onAdPosted, selectedPlan, onBackToPlan }) => {
// //   const { isAuthenticated, getToken } = useAuthContext();
// //   const [chosenCategory, setChosenCategory] = useState(null);
  
// //   // ✅ NEW: Promotion state
// //   const [promotion, setPromotion] = useState(null);
// //   const [promotionLoading, setPromotionLoading] = useState(false);
// //   const [finalPrice, setFinalPrice] = useState(null);

// //   // ✅ Payment state — holds form data + ad_id while waiting for M-Pesa confirmation
// //   const [showPayment,     setShowPayment]     = useState(false);
// //   const [pendingFormData, setPendingFormData] = useState(null);
// //   const [pendingAdId,     setPendingAdId]     = useState(null);

// //   // ✅ NEW: Check for promotion when modal opens or plan changes
// //   useEffect(() => {
// //     if (isOpen && selectedPlan && isAuthenticated) {
// //       checkPromotion();
// //     }
// //   }, [isOpen, selectedPlan, isAuthenticated]);

// //   // ✅ NEW: Check user promotion
// //   const checkPromotion = async () => {
// //     if (!selectedPlan) return;
    
// //     setPromotionLoading(true);
// //     try {
// //       const planMap = {
// //         'free': '',
// //         'basic': 'basic',
// //         'business': 'business'
// //       };
// //       const planParam = planMap[selectedPlan.planId] || '';
      
// //       const promo = await checkUserPromotion(planParam);
      
// //       if (promo.has_promotion && promo.promotion) {
// //         setPromotion(promo.promotion);
        
// //         // Calculate final price
// //         const originalPrice = selectedPlan.price || 0;
// //         const pricingInfo = calculatePromotionPrice(
// //           originalPrice, 
// //           promo.promotion, 
// //           planParam
// //         );
        
// //         setFinalPrice(pricingInfo);
        
// //         console.log('✅ Promotion applied:', pricingInfo);
// //       } else {
// //         setPromotion(null);
// //         setFinalPrice(null);
// //       }
// //     } catch (error) {
// //       console.error('Promotion check failed:', error);
// //       setPromotion(null);
// //       setFinalPrice(null);
// //     } finally {
// //       setPromotionLoading(false);
// //     }
// //   };

// //   if (!isOpen) return null;

// //   const maxImages  = selectedPlan?.maxImages || 2;
// //   const maxVideos  = selectedPlan?.maxVideos || 0;
// //   const planName   = selectedPlan?.planName  || 'Free';
// //   const isBusiness = selectedPlan?.planId === 'business';
  
// //   // ✅ NEW: Use promotional price if available
// //   const displayPrice = finalPrice?.isFree 
// //     ? 0 
// //     : finalPrice?.finalPrice ?? selectedPlan?.price ?? 0;

// //   const handleClose = () => {
// //     setChosenCategory(null);
// //     setPromotion(null);
// //     setFinalPrice(null);
// //     setShowPayment(false);
// //     setPendingFormData(null);
// //     setPendingAdId(null);
// //     onClose();
// //   };

// //   // ── Determine if this plan needs payment ─────────────────────────────────
// //   const needsPayment = () => {
// //     if (!selectedPlan || !selectedPlan.price || selectedPlan.price === 0) return false;
// //     if (selectedPlan.planId === 'free') return false;
// //     // If user has an active promo that makes it free — skip payment
// //     if (finalPrice?.isFree) return false;
// //     return true;
// //   };

// //   // ── Called by sub-forms (Rooms / LostFound / Standard) on submit ──────────
// //   const handleSubmit = async (formData) => {
// //     if (!isAuthenticated) return alert('Please log in to post an ad');
// //     if (selectedPlan) {
// //       formData.append('plan_id',   selectedPlan.planId   || '');
// //       formData.append('plan_name', selectedPlan.planName || 'Free');
// //       // ✅ FIX: send the actual (possibly discounted) amount so ad_views.py
// //       //    stores it in AdPlan.amount_paid instead of the hardcoded full price.
// //       const _displayPrice = (finalPrice?.isFree)
// //         ? 0
// //         : (finalPrice?.finalPrice ?? selectedPlan?.price ?? 0);
// //       formData.append('plan_amount', String(_displayPrice));
// //     }

// //     // ── If paid plan and no promo → create ad as pending, then show payment ─
// //     if (needsPayment()) {
// //       // Step 1: POST the ad to backend with status pending
// //       // This gives us an ad_id which M-Pesa callback needs to activate the plan
// //       let adId = null;
// //       try {
// //         const res = await apiService.postAd(formData, getToken());
// //         let data = {};
// //         try { data = await res.json(); } catch (_) {}
// //         if (!res.ok) {
// //           alert(data?.error || data?.detail || 'Failed to save ad. Please try again.');
// //           return;
// //         }
// //         adId = data?.id || data?.ad_id || null;
// //         console.log('✅ Ad created (pending payment), id=', adId);
// //       } catch (err) {
// //         console.error(err);
// //         alert('Network error. Please try again.');
// //         return;
// //       }

// //       // Step 2: Show payment modal with the real ad_id
// //       setPendingAdId(adId);
// //       setPendingFormData(formData);
// //       setShowPayment(true);
// //       return;   // wait for payment confirmation
// //     }

// //     // ── Free / promo → post immediately ──────────────────────────────────
// //     await postAdToBackend(formData);
// //   };

// //   // ── Actually POST the ad (called after payment OR directly for free) ──────
// //   const postAdToBackend = async (formData) => {
// //     try {
// //       const response = await apiService.postAd(formData, getToken());
// //       let data = {};
// //       try { data = await response.json(); } catch (_) {}

// //       if (response.ok) {
// //         // Use promotion slot after successful post
// //         if (promotion && selectedPlan) {
// //           const planMap = { 'free': '', 'basic': 'basic', 'pro': 'pro', 'business': 'business' };
// //           const planParam = planMap[selectedPlan.planId] || '';
// //           if (planParam) {
// //             try { await consumePromotionSlot(planParam); } catch (_) {}
// //           }
// //         }
// //         alert('Ad posted successfully!');
// //         handleClose();
// //         if (typeof onAdPosted === 'function') onAdPosted();
// //       } else {
// //         alert(data?.error || data?.detail || 'Failed to post ad. Please try again.');
// //       }
// //     } catch (err) {
// //       console.error(err);
// //       alert('Network error. Please try again.');
// //     }
// //   };

// //   // ── Payment confirmed → ad already exists, just notify parent ──────────────
// //   // The Django callback already activated AdPlan when payment was confirmed.
// //   const handlePaymentSuccess = async (paymentData) => {
// //     setShowPayment(false);
// //     setPendingFormData(null);
// //     setPendingAdId(null);
// //     alert('Ad posted successfully! Your plan is now active.');
// //     handleClose();
// //     if (typeof onAdPosted === 'function') onAdPosted();
// //   };

// //   const titles = {
// //     [ROOMS_CAT]:      { title: 'Post Your Listing',  sub: 'Fill in the details to list your room' },
// //     [LOST_FOUND_CAT]: { title: 'Post Lost & Found',  sub: 'Fill in the details to report the item' },
// //     default:          { title: 'Post Your Ad',        sub: 'Fill in the details to list your item' },
// //   };
// //   const { title, sub } = titles[chosenCategory] || titles.default;

// //   return (
// //     <>
// //     <div className={styles.modalOverlay} onClick={handleClose}>
// //       <div className={styles.postAdModal} onClick={e => e.stopPropagation()}>
// //         <button className={styles.closeBtn} onClick={handleClose}>×</button>
// //         <h2 className={styles.modalTitle}>{title}</h2>
// //         <p className={styles.modalSubtitle}>{sub}</p>

// //         {/* ── Plan banner ── */}
// //         {selectedPlan && (
// //           <div className={`${styles.selectedPlanBanner} ${isBusiness ? styles.selectedPlanBannerBusiness : ''}`}>
// //             <div className={styles.planInfo}>
// //               <span className={`${styles.planBadge} ${isBusiness ? styles.planBadgeBusiness : ''}`}>
// //                 {isBusiness ? '⭐ ' : ''}{planName}
// //               </span>
// //               <div className={styles.planLimits}>
// //                 <span>📷 Up to {maxImages} images</span>
// //                 {maxVideos > 0 && <span>🎥 Up to {maxVideos} videos</span>}
// //                 {selectedPlan.displayDays && <span>⏱️ {selectedPlan.displayDays} days</span>}
// //               </div>
// //             </div>
            
// //             {/* ← Change Plan button */}
// //             {onBackToPlan && (
// //               <button
// //                 onClick={onBackToPlan}
// //                 style={{
// //                   background: 'none', border: '1.5px solid rgba(255,255,255,0.5)',
// //                   borderRadius: 8, color: 'inherit', fontSize: 12, fontWeight: 600,
// //                   padding: '4px 12px', cursor: 'pointer', whiteSpace: 'nowrap',
// //                   opacity: 0.85, transition: 'opacity .15s',
// //                 }}
// //                 onMouseEnter={e => e.target.style.opacity = 1}
// //                 onMouseLeave={e => e.target.style.opacity = 0.85}
// //               >
// //                 ← Change Plan
// //               </button>
// //             )}

// //           {/* ✅ NEW: Show promotional or regular price */}
// //             {displayPrice > 0 ? (
// //               <div className={`${styles.planPrice} ${isBusiness ? styles.planPriceBusiness : ''}`}>
// //                 {finalPrice && finalPrice.discountAmount > 0 && (
// //                   <div className={styles.originalPrice}>
// //                     <del>KES {selectedPlan.price}</del>
// //                   </div>
// //                 )}
// //                 <div>KES {displayPrice}</div>
// //               </div>
// //             ) : displayPrice === 0 && finalPrice?.isFree ? (
// //               <div className={`${styles.planPrice} ${styles.planPriceFree}`}>
// //                 FREE
// //               </div>
// //             ) : null}
// //           </div>
// //         )}

// //         {/* ✅ NEW: Promotion label */}
// //         {finalPrice && finalPrice.label && (
// //           <div className={styles.promotionBanner}>
// //             {finalPrice.label}
// //           </div>
// //         )}

// //         {/* ── Business slot notice ── */}
// //         {isBusiness && (
// //           <div className={styles.slotBanner}>
// //             🔄 <strong>Slot-based plan:</strong> Pay once &amp; post freely — delete and re-post
// //             your ad any time within the 3-month window at no extra charge.
// //           </div>
// //         )}

// //         {/* ── Category picker ── */}
// //         {chosenCategory === null && (
// //           <div className={styles.categoryPicker}>
// //             <div className={styles.formGroup}>
// //               <label>Category <span className={styles.req}>*</span></label>
// //               <select
// //                 className={styles.inputField}
// //                 defaultValue=""
// //                 onChange={e => { if (e.target.value) setChosenCategory(e.target.value); }}
// //               >
// //                 <option value="" disabled>Select a category...</option>
// //                 {CATEGORIES.filter(c => c !== 'All').map(c => (
// //                   <option key={c} value={c}>{c}</option>
// //                 ))}
// //               </select>
// //             </div>
// //           </div>
// //         )}

// //         {chosenCategory === ROOMS_CAT && (
// //           <RoomsForm maxImages={maxImages} maxVideos={maxVideos} planName={planName} onSubmit={handleSubmit} />
// //         )}
// //         {chosenCategory === LOST_FOUND_CAT && (
// //           <LostFoundForm maxVideos={maxVideos} planName={planName} onSubmit={handleSubmit} />
// //         )}
// //         {chosenCategory !== null && chosenCategory !== ROOMS_CAT && chosenCategory !== LOST_FOUND_CAT && (
// //           <StandardForm initialCategory={chosenCategory} maxImages={maxImages}
// //             maxVideos={maxVideos} planName={planName} onSubmit={handleSubmit} />
// //         )}

// //         {chosenCategory !== null && (
// //           <button className={styles.backBtn} onClick={() => setChosenCategory(null)}>
// //             ← Change category
// //           </button>
// //         )}
// //       </div>
// //     </div>

// //     {/* ── M-Pesa Payment Modal — shown after user fills form and clicks submit ── */}
// //     {/* ✅ FIX: pass the discounted price to PaymentModal, not the original plan price */}
// //     <PaymentModal
// //       isOpen={showPayment}
// //       plan={{
// //         ...selectedPlan,
// //         // If a promotion reduced the price, use finalPrice.finalPrice; otherwise keep original
// //         price: (finalPrice && !finalPrice.isFree && finalPrice.finalPrice != null)
// //           ? finalPrice.finalPrice
// //           : selectedPlan?.price ?? 0,
// //       }}
// //       adId={pendingAdId}
// //       onSuccess={handlePaymentSuccess}
// //       onClose={() => { setShowPayment(false); setPendingFormData(null); setPendingAdId(null); }}
// //     />
// //     </>
// //   );
// // };

// // export default PostAdModal;

















// // // import React, { useState, useRef, useEffect } from 'react';
// // // import { useAuthContext } from '../../../context/AuthContext';
// // // import apiService from '../../../services/api';
// // // import { CATEGORIES, SUB_CATEGORIES } from '../../../utils/constants';
// // // import { checkUserPromotion, consumePromotionSlot, calculatePromotionPrice } from '../../../utils/promotionApi'; // ✅ NEW
// // // import styles from './PostAdModal.module.css';
// // // import PaymentModal from './PaymentModal';

// // // const ROOMS_CAT      = 'Rooms to Let';
// // // const LOST_FOUND_CAT = 'Lost & Found';

// // // // ── Location search via OpenStreetMap Nominatim ───────────────────────────
// // // const searchKenyaPlaces = async (query) => {
// // //   if (!query || query.trim().length < 2) return [];
// // //   try {
// // //     const url =
// // //       `https://nominatim.openstreetmap.org/search` +
// // //       `?q=${encodeURIComponent(query + ', Kenya')}` +
// // //       `&countrycodes=ke&limit=12&format=json&addressdetails=1&accept-language=en`;
// // //     const res = await fetch(url, {
// // //       headers: { 'User-Agent': 'ImSellingKE/1.0 (marketplace)' }
// // //     });
// // //     const data = await res.json();
// // //     return data.map(item => {
// // //       const a = item.address;
// // //       const label =
// // //         a.suburb || a.neighbourhood || a.quarter || a.village ||
// // //         a.hamlet || a.town || a.city_district || a.city ||
// // //         item.display_name.split(',')[0];
// // //       const parent =
// // //         a.suburb        ? (a.town || a.city || a.county) :
// // //         a.village       ? (a.town || a.city || a.county) :
// // //         a.hamlet        ? (a.town || a.city || a.county) :
// // //         a.neighbourhood ? (a.city || a.county)           : null;
// // //       const display = parent ? `${label.trim()}, ${parent.trim()}` : label.trim();
// // //       return { label: label.trim(), display, osm_id: item.osm_id };
// // //     }).filter((v, i, arr) => arr.findIndex(x => x.display === v.display) === i);
// // //   } catch (e) {
// // //     console.error('Location search error:', e);
// // //     return [];
// // //   }
// // // };

// // // const Field = ({ label, required, children }) => (
// // //   <div className={styles.formGroup}>
// // //     <label>{label} {required && <span className={styles.req}>*</span>}</label>
// // //     {children}
// // //   </div>
// // // );

// // // // ── CityPicker ────────────────────────────────────────────────────────────
// // // const CityPicker = ({ value, onChange, placeholder = 'Search village, estate, street, town...' }) => {
// // //   const [query,     setQuery]     = useState(value || '');
// // //   const [results,   setResults]   = useState([]);
// // //   const [open,      setOpen]      = useState(false);
// // //   const [active,    setActive]    = useState(-1);
// // //   const [loading,   setLoading]   = useState(false);
// // //   const [confirmed, setConfirmed] = useState(!!value);
// // //   const wrapRef  = useRef(null);
// // //   const inputRef = useRef(null);
// // //   const timerRef = useRef(null);

// // //   useEffect(() => {
// // //     const fn = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
// // //     document.addEventListener('mousedown', fn);
// // //     return () => document.removeEventListener('mousedown', fn);
// // //   }, []);

// // //   const runSearch = (q) => {
// // //     clearTimeout(timerRef.current);
// // //     if (!q || q.trim().length < 2) { setResults([]); setLoading(false); return; }
// // //     setLoading(true);
// // //     timerRef.current = setTimeout(async () => {
// // //       const found = await searchKenyaPlaces(q);
// // //       setResults(found);
// // //       setOpen(found.length > 0);
// // //       setActive(0);
// // //       setLoading(false);
// // //     }, 400);
// // //   };

// // //   const select = (item) => {
// // //     setQuery(item.display);
// // //     onChange(item.display);
// // //     setConfirmed(true);
// // //     setOpen(false);
// // //     setResults([]);
// // //   };

// // //   const clear = () => {
// // //     setQuery(''); onChange('');
// // //     setConfirmed(false); setResults([]); setOpen(false);
// // //     clearTimeout(timerRef.current);
// // //     inputRef.current?.focus();
// // //   };

// // //   const handleInput = (e) => {
// // //     const v = e.target.value;
// // //     setQuery(v);
// // //     setConfirmed(false);
// // //     onChange('');
// // //     runSearch(v);
// // //   };

// // //   const handleKey = (e) => {
// // //     if (e.key === 'ArrowDown') { e.preventDefault(); setActive(i => Math.min(i + 1, results.length - 1)); }
// // //     if (e.key === 'ArrowUp')   { e.preventDefault(); setActive(i => Math.max(i - 1, 0)); }
// // //     if (e.key === 'Enter')     { e.preventDefault(); if (results[active]) select(results[active]); }
// // //     if (e.key === 'Escape')    { setOpen(false); }
// // //   };

// // //   return (
// // //     <div ref={wrapRef} className={styles.cityWrap}>
// // //       <div className={styles.cityInputWrap}>
// // //         <span className={styles.citySearchIcon}>🔍</span>
// // //         <input
// // //           ref={inputRef}
// // //           type="text"
// // //           className={`${styles.inputField} ${styles.cityInput} ${confirmed ? styles.cityInputConfirmed : ''}`}
// // //           placeholder={placeholder}
// // //           value={query}
// // //           onChange={handleInput}
// // //           onFocus={() => results.length > 0 && setOpen(true)}
// // //           onKeyDown={handleKey}
// // //           autoComplete="off"
// // //           spellCheck="false"
// // //         />
// // //         {loading && <span className={styles.citySpinner} title="Searching...">⏳</span>}
// // //         {!loading && query && (
// // //           <button type="button" className={styles.cityClear} onClick={clear}>×</button>
// // //         )}
// // //       </div>
// // //       {confirmed && <p className={styles.cityConfirmedMsg}>✅ Location set</p>}
// // //       {!confirmed && query.length === 1 && (
// // //         <p className={styles.cityHint}>Type one more character to search…</p>
// // //       )}
// // //       {open && results.length > 0 && (
// // //         <ul className={styles.cityDropdown}>
// // //           {results.map((item, i) => (
// // //             <li
// // //               key={`${item.osm_id}-${i}`}
// // //               className={`${styles.cityOption} ${i === active ? styles.cityOptionActive : ''}`}
// // //               onMouseDown={() => select(item)}
// // //               onMouseEnter={() => setActive(i)}
// // //             >
// // //               <span className={styles.cityPin}>📍</span>
// // //               <span>
// // //                 <span className={styles.cityOptionLabel}>{item.label}</span>
// // //                 {item.display !== item.label && (
// // //                   <span className={styles.cityOptionSub}>
// // //                     {', ' + item.display.replace(item.label + ', ', '')}
// // //                   </span>
// // //                 )}
// // //               </span>
// // //             </li>
// // //           ))}
// // //         </ul>
// // //       )}
// // //       {!loading && open && results.length === 0 && query.length >= 2 && (
// // //         <div className={styles.cityNoResult}>
// // //           No results for "<strong>{query}</strong>" — try a nearby street or area name
// // //         </div>
// // //       )}
// // //     </div>
// // //   );
// // // };

// // // // ══════════════════════════════════════════════════════════════════════════════
// // // // ROOMS TO LET FORM
// // // // ══════════════════════════════════════════════════════════════════════════════
// // // const RoomsForm = ({ maxImages, maxVideos, planName, onSubmit }) => {
// // //   const [title,         setTitle]         = useState('');
// // //   const [location,      setLocation]      = useState('');
// // //   const [price,         setPrice]         = useState('');
// // //   const [priceDisplay,  setPriceDisplay]  = useState('');
// // //   const [rentalPeriod,  setRentalPeriod]  = useState('month'); // ✅ NEW
// // //   const [negotiable,    setNegotiable]    = useState('');
// // //   const [propType,      setPropType]      = useState('');
// // //   const [furnished,     setFurnished]     = useState('');
// // //   const [availFrom,     setAvailFrom]     = useState('');
// // //   const [description,   setDescription]   = useState('');
// // //   const [contact,       setContact]       = useState('');
// // //   const [images,        setImages]        = useState([]);
// // //   const [videos,        setVideos]        = useState([]);

// // //   const handleRoomPrice = (e) => {
// // //     const raw = e.target.value.replace(/,/g, '').replace(/[^\d]/g, '');
// // //     setPrice(raw);
// // //     setPriceDisplay(raw ? Number(raw).toLocaleString('en-KE') : '');
// // //   };

// // //   const handleImages = (e) => {
// // //     const files = Array.from(e.target.files);
// // //     if (files.length > maxImages)
// // //       alert(`Only the first ${maxImages} photo${maxImages > 1 ? 's' : ''} will be uploaded. The rest will be ignored.`);
// // //     setImages(files.slice(0, maxImages));
// // //   };

// // //   const handleVideos = (e) => {
// // //     if (maxVideos === 0) return alert('Video upload requires Business plan');
// // //     const all = Array.from(e.target.files);
// // //     const valid = all.filter(f => f.size <= 50*1024*1024);
// // //     const oversized = all.length - valid.length;
// // //     const overCount = valid.length - maxVideos;
// // //     if (oversized > 0) alert(`${oversized} video${oversized > 1 ? 's were' : ' was'} removed (exceeds 50MB limit).`);
// // //     if (overCount > 0) alert(`Only ${maxVideos} video${maxVideos > 1 ? 's' : ''} allowed. The rest will be ignored.`);
// // //     setVideos(valid.slice(0, maxVideos));
// // //   };

// // //   const handleSubmit = (e) => {
// // //     e.preventDefault();
// // //     if (!title)            return alert('Please enter a title');
// // //     if (title.length > 25) return alert('Title must be 25 characters or less');
// // //     if (!location)         return alert('Please select a location');
// // //     if (!price)            return alert('Please enter rental price');
// // //     if (!negotiable)       return alert('Please select price type');
// // //     if (!propType)         return alert('Please select property type');
// // //     if (!furnished)        return alert('Please select furnished option');
// // //     if (!availFrom)        return alert('Please select availability date');
// // //     if (!description)      return alert('Please add a description');
// // //     if (!contact)          return alert('Please enter contact info');

// // //     const fd = new FormData();
// // //     fd.append('title',         title);
// // //     const periodLabels = { hour: 'Hour', day: 'Day', week: 'Week', month: 'Month', year: 'Year' };
// // //     const periodLabel = periodLabels[rentalPeriod] || 'Month';
// // //     fd.append('description',   `${description}\n\nProperty Type: ${propType}\nFurnished: ${furnished}\nAvailable From: ${availFrom}\nRental Period: Per ${periodLabel}`);
// // //     fd.append('sub_category',  rentalPeriod); // store period as sub_category for display
// // //     fd.append('price',         price);
// // //     fd.append('category_name', ROOMS_CAT);
// // //     fd.append('condition',     'new');
// // //     fd.append('price_type',    negotiable);
// // //     fd.append('contact',       contact);
// // //     fd.append('city',          location);
// // //     fd.append('plan_name',     planName);
// // //     images.forEach((img, i) => fd.append(`image_${i}`, img));
// // //     videos.forEach((vid, i) => fd.append(`video_${i}`, vid));
// // //     onSubmit(fd);
// // //   };

// // //   return (
// // //     <form onSubmit={handleSubmit} className={styles.postAdForm}>
// // //       <Field label={`Title (${title.length}/25)`} required>
// // //         <input className={styles.inputField} placeholder="e.g., SPACIOUS 2 BEDROOM"
// // //           value={title} maxLength={25}
// // //           onChange={e => setTitle(e.target.value.toUpperCase())} />
// // //       </Field>
// // //       <Field label="Category" required>
// // //         <div className={styles.lockedCategory}>🏠 Rooms to Let</div>
// // //       </Field>
// // //       <Field label="Location" required>
// // //         <CityPicker value={location} onChange={setLocation} placeholder="Search city or town..." />
// // //       </Field>
// // //       <Field label="Rental Price (KES)" required>
// // //         {/* ✅ Inline price + period row */}
// // //         <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
// // //           <input
// // //             type="text" inputMode="numeric"
// // //             className={styles.inputField}
// // //             style={{ flex: 1 }}
// // //             placeholder="e.g., 10,000"
// // //             value={priceDisplay}
// // //             onChange={handleRoomPrice}
// // //           />
// // //           <select
// // //             className={styles.inputField}
// // //             style={{ width: 110, flexShrink: 0 }}
// // //             value={rentalPeriod}
// // //             onChange={e => setRentalPeriod(e.target.value)}
// // //             title="Rental period"
// // //           >
// // //             <option value="hour">/ Hour</option>
// // //             <option value="day">/ Day</option>
// // //             <option value="week">/ Week</option>
// // //             <option value="month">/ Month</option>
// // //             <option value="year">/ Year</option>
// // //           </select>
// // //         </div>
// // //         {price && (
// // //           <p style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
// // //             Price: <strong>KES {priceDisplay} / {rentalPeriod.charAt(0).toUpperCase() + rentalPeriod.slice(1)}</strong>
// // //           </p>
// // //         )}
// // //       </Field>
// // //       <div className={styles.twoCol}>
// // //         <Field label="Is Rent Negotiable?" required>
// // //           <select className={styles.inputField} value={negotiable} onChange={e => setNegotiable(e.target.value)}>
// // //             <option value="" disabled>Select price type...</option>
// // //             <option value="fixed">🔒 Fixed Price</option>
// // //             <option value="negotiable">🤝 Negotiable</option>
// // //           </select>
// // //         </Field>
// // //         <Field label="Property Type" required>
// // //           <select className={styles.inputField} value={propType} onChange={e => setPropType(e.target.value)}>
// // //             <option value="" disabled>Select type...</option>
// // //             <option value="bedsitter">Bedsitter</option>
// // //             <option value="single_room">Single Room</option>
// // //             <option value="1_bedroom">1 Bedroom</option>
// // //             <option value="2_bedroom">2 Bedroom</option>
// // //             <option value="3_bedroom">3 Bedroom</option>
// // //             <option value="studio">Studio</option>
// // //             <option value="mansion">Mansion</option>
// // //             <option value="airbnb">AirBnB</option>
// // //             <option value="warehouse">Warehouse</option>
// // //             <option value="office">Office Space</option>
// // //             <option value="shop">Shop / Stall</option>
// // //           </select>
// // //         </Field>
// // //       </div>
// // //       <div className={styles.twoCol}>
// // //         <Field label="Furnished?" required>
// // //           <select className={styles.inputField} value={furnished} onChange={e => setFurnished(e.target.value)}>
// // //             <option value="" disabled>Select option...</option>
// // //             <option value="fully_furnished">Fully Furnished</option>
// // //             <option value="semi_furnished">Semi-Furnished</option>
// // //             <option value="unfurnished">Unfurnished</option>
// // //           </select>
// // //         </Field>
// // //         <Field label="Available From" required>
// // //           <input type="date" className={styles.inputField}
// // //             value={availFrom} onChange={e => setAvailFrom(e.target.value)}
// // //             min={new Date().toISOString().split('T')[0]} />
// // //         </Field>
// // //       </div>
// // //       <Field label="Description" required>
// // //         <textarea className={`${styles.inputField} ${styles.textareaField}`}
// // //           placeholder="Describe your room and amenities..." rows={4}
// // //           value={description} onChange={e => setDescription(e.target.value)} />
// // //       </Field>
// // //       <Field label="Contact (Email or Phone)" required>
// // //         <input className={styles.inputField}
// // //           placeholder="e.g., +254712345678 or email@example.com"
// // //           value={contact} onChange={e => setContact(e.target.value)} />
// // //       </Field>
// // //       {maxVideos > 0 && (
// // //         <Field label="Upload Videos (Optional)">
// // //           <input type="file" className={styles.fileInput}
// // //             accept="video/mp4,video/webm,video/ogg,video/quicktime" multiple onChange={handleVideos} />
// // //           <p className={styles.helperText}>🎥 Max {maxVideos} videos, 50MB each</p>
// // //           {videos.length > 0 && <p className={styles.selectedFiles}>✅ {videos.length}/{maxVideos} selected</p>}
// // //         </Field>
// // //       )}
// // //       <Field label="Upload Images (Optional)">
// // //         <div className={styles.uploadBox}>
// // //           <label className={styles.uploadLabel} htmlFor="rooms-images">
// // //             <span>📷</span> Choose Files
// // //           </label>
// // //           <input id="rooms-images" type="file" className={styles.hiddenInput}
// // //             accept="image/*" multiple onChange={handleImages} />
// // //           <p className={styles.helperText}>Upload up to {maxImages} photos (Max 10 MB each)</p>
// // //           {images.length > 0 && <p className={styles.selectedFiles}>✅ {images.length}/{maxImages} selected</p>}
// // //         </div>
// // //       </Field>
// // //       {maxVideos === 0 && (
// // //         <div className={styles.upgradePrompt}>
// // //           <p>🎥 Want to add videos? Upgrade to <strong>Business Plan</strong> for up to 2 videos!</p>
// // //         </div>
// // //       )}
// // //       <button type="submit" className={styles.submitAdBtn}>Post Listing</button>
// // //     </form>
// // //   );
// // // };

// // // // ══════════════════════════════════════════════════════════════════════════════
// // // // LOST & FOUND FORM
// // // // ══════════════════════════════════════════════════════════════════════════════
// // // const LostFoundForm = ({ maxVideos, planName, onSubmit }) => {
// // //   const [itemStatus,   setItemStatus]   = useState('lost');
// // //   const [itemName,     setItemName]     = useState('');
// // //   const [itemCategory, setItemCategory] = useState('');
// // //   const [location,     setLocation]     = useState('');
// // //   const [dateLost,     setDateLost]     = useState('');
// // //   const [description,  setDescription]  = useState('');
// // //   const [contact,      setContact]      = useState('');
// // //   const [images,       setImages]       = useState([]);
// // //   const [videos,       setVideos]       = useState([]);

// // //   const LF_CATS = [
// // //     'Electronics','Documents & ID','Pets','Bags & Luggage',
// // //     'Clothing & Accessories','Keys','Wallets & Purses','Jewellery',
// // //     'Vehicles','Books & Stationery','Other',
// // //   ];

// // //   const handleLFVideos = (e) => {
// // //     if (maxVideos === 0) return alert('Video upload requires Business plan');
// // //     const all = Array.from(e.target.files);
// // //     const valid = all.filter(f => f.size <= 50*1024*1024);
// // //     const oversized = all.length - valid.length;
// // //     const overCount = valid.length - maxVideos;
// // //     if (oversized > 0) alert(`${oversized} video${oversized > 1 ? 's were' : ' was'} removed (exceeds 50MB limit).`);
// // //     if (overCount > 0) alert(`Only ${maxVideos} video${maxVideos > 1 ? 's' : ''} allowed. The rest will be ignored.`);
// // //     setVideos(valid.slice(0, maxVideos));
// // //   };

// // //   const handleSubmit = (e) => {
// // //     e.preventDefault();
// // //     if (!itemName)     return alert('Please enter item name');
// // //     if (!itemCategory) return alert('Please select a category');
// // //     if (!location)     return alert('Please enter a location');
// // //     if (!dateLost)     return alert('Please select date lost/found');
// // //     if (!description)  return alert('Please add a description');
// // //     if (!contact)      return alert('Please enter contact info');

// // //     const fd = new FormData();
// // //     fd.append('title',         `[${itemStatus.toUpperCase()}] ${itemName}`);
// // //     fd.append('description',   `Status: ${itemStatus}\nCategory: ${itemCategory}\nDate: ${dateLost}\n\n${description}`);
// // //     fd.append('price',         '0');
// // //     fd.append('category_name', LOST_FOUND_CAT);
// // //     fd.append('condition',     'used');
// // //     fd.append('price_type',    'fixed');
// // //     fd.append('contact',       contact);
// // //     fd.append('city',          location);
// // //     fd.append('plan_name',     planName);
// // //     images.forEach((img, i) => fd.append(`image_${i}`, img));
// // //     videos.forEach((vid, i) => fd.append(`video_${i}`, vid));
// // //     onSubmit(fd);
// // //   };

// // //   return (
// // //     <form onSubmit={handleSubmit} className={styles.postAdForm}>
// // //       <Field label="Status" required>
// // //         <select className={styles.inputField} value={itemStatus} onChange={e => setItemStatus(e.target.value)}>
// // //           <option value="lost">🔍 Lost</option>
// // //           <option value="found">✅ Found</option>
// // //         </select>
// // //       </Field>
// // //       <Field label={`Item Name (${itemName.length}/25)`} required>
// // //         <input className={styles.inputField}
// // //           placeholder="e.g., BLACK BACKPACK, CAR KEY"
// // //           value={itemName} maxLength={25}
// // //           onChange={e => setItemName(e.target.value.toUpperCase())} />
// // //       </Field>
// // //       <Field label="Category" required>
// // //         <select className={styles.inputField} value={itemCategory} onChange={e => setItemCategory(e.target.value)}>
// // //           <option value="" disabled>Select category...</option>
// // //           {LF_CATS.map(c => <option key={c} value={c}>{c}</option>)}
// // //         </select>
// // //       </Field>
// // //       <Field label={itemStatus === 'lost' ? 'Where Was It Lost?' : 'Where Was It Found?'} required>
// // //         <input className={styles.inputField}
// // //           placeholder="e.g., Central Mall, Nairobi CBD, Uhuru Park"
// // //           value={location} onChange={e => setLocation(e.target.value)} />
// // //       </Field>
// // //       <Field label="Date Lost/Found" required>
// // //         <input type="date" className={styles.inputField}
// // //           value={dateLost} onChange={e => setDateLost(e.target.value)}
// // //           max={new Date().toISOString().split('T')[0]} />
// // //       </Field>
// // //       <Field label="Description" required>
// // //         <textarea className={`${styles.inputField} ${styles.textareaField}`}
// // //           placeholder="Describe the item, its appearance, and how to contact you..."
// // //           rows={4} value={description} onChange={e => setDescription(e.target.value)} />
// // //       </Field>
// // //       <Field label="Contact (Email or Phone)" required>
// // //         <input className={styles.inputField}
// // //           placeholder="e.g., +254712345678 or email@example.com"
// // //           value={contact} onChange={e => setContact(e.target.value)} />
// // //       </Field>
// // //       {maxVideos > 0 && (
// // //         <Field label="Upload Videos (Optional)">
// // //           <input type="file" className={styles.fileInput}
// // //             accept="video/mp4,video/webm,video/ogg,video/quicktime" multiple onChange={handleLFVideos} />
// // //           <p className={styles.helperText}>🎥 Max {maxVideos} videos, 50MB each</p>
// // //           {videos.length > 0 && <p className={styles.selectedFiles}>✅ {videos.length}/{maxVideos} selected</p>}
// // //         </Field>
// // //       )}
// // //       <Field label="Upload Images (Optional)">
// // //         <div className={styles.uploadBox}>
// // //           <label className={styles.uploadLabel} htmlFor="lf-images">
// // //             <span>📷</span> Upload Photos <span className={styles.uploadChevron}>▾</span>
// // //           </label>
// // //           <input id="lf-images" type="file" className={styles.hiddenInput}
// // //             accept="image/*" multiple onChange={e => {
// // //               const files = Array.from(e.target.files);
// // //               if (files.length > 6) alert('Only the first 6 photos will be uploaded. The rest will be ignored.');
// // //               setImages(files.slice(0, 6));
// // //             }} />
// // //           <p className={styles.helperText}>🖼️ Upload up to 6 photos: JPG, PNG (Max 10 MB each)</p>
// // //           {images.length > 0 && <p className={styles.selectedFiles}>✅ {images.length}/6 selected</p>}
// // //         </div>
// // //       </Field>
// // //       {maxVideos === 0 && (
// // //         <div className={styles.upgradePrompt}>
// // //           <p>🎥 Want to add videos? Upgrade to <strong>Business Plan</strong> for up to 2 videos!</p>
// // //         </div>
// // //       )}
// // //       <button type="submit" className={styles.submitAdBtn}>Post Listing</button>
// // //     </form>
// // //   );
// // // };

// // // // ══════════════════════════════════════════════════════════════════════════════
// // // // STANDARD FORM
// // // // ══════════════════════════════════════════════════════════════════════════════
// // // const StandardForm = ({ initialCategory, maxImages, maxVideos, planName, onSubmit }) => {
// // //   const [title,        setTitle]        = useState('');
// // //   const [category,     setCategory]     = useState(initialCategory || 'Electronics');
// // //   const [subCategory,  setSubCategory]  = useState('');
// // //   const [condition,    setCondition]    = useState('');
// // //   const [price,        setPrice]        = useState('');
// // //   const [priceDisplay, setPriceDisplay] = useState('');
// // //   const [negotiable,   setNegotiable]   = useState('');
// // //   const [city,         setCity]         = useState('');
// // //   const [description,  setDescription]  = useState('');
// // //   const [contact,      setContact]      = useState('');
// // //   const [images,       setImages]       = useState([]);
// // //   const [videos,       setVideos]       = useState([]);

// // //   const handlePriceChange = (e) => {
// // //     const raw = e.target.value.replace(/,/g, '').replace(/[^\d]/g, '');
// // //     setPrice(raw);
// // //     setPriceDisplay(raw ? Number(raw).toLocaleString('en-KE') : '');
// // //   };

// // //   const handleImages = (e) => {
// // //     const files = Array.from(e.target.files);
// // //     if (files.length > maxImages)
// // //       alert(`Only the first ${maxImages} photo${maxImages > 1 ? 's' : ''} will be uploaded. The rest will be ignored.`);
// // //     setImages(files.slice(0, maxImages));
// // //   };

// // //   const handleVideos = (e) => {
// // //     if (maxVideos === 0) return alert('Video upload requires Business plan');
// // //     const all = Array.from(e.target.files);
// // //     const valid = all.filter(f => f.size <= 50*1024*1024);
// // //     const oversized = all.length - valid.length;
// // //     const overCount = valid.length - maxVideos;
// // //     if (oversized > 0) alert(`${oversized} video${oversized > 1 ? 's were' : ' was'} removed (exceeds 50MB limit).`);
// // //     if (overCount > 0) alert(`Only ${maxVideos} video${maxVideos > 1 ? 's' : ''} allowed. The rest will be ignored.`);
// // //     setVideos(valid.slice(0, maxVideos));
// // //   };

// // //   const handleSubmit = (e) => {
// // //     e.preventDefault();
// // //     if (title.length < 5)        return alert('Title must be at least 5 characters');
// // //     if (title.length > 25)       return alert('Title must be 25 characters or less');
// // //     if (description.length < 10) return alert('Description must be at least 10 characters');
// // //     if (!city)                   return alert('Please enter a city/town');
// // //     if (!condition)              return alert('Please select product condition');
// // //     if (!negotiable)             return alert('Please select price type');
// // //     if (!contact)                return alert('Please enter contact info');
// // //     if (!images.length && !videos.length) return alert('Please upload at least one image or video');

// // //     const fd = new FormData();
// // //     fd.append('title',         title);
// // //     fd.append('description',   description);
// // //     fd.append('price',         price);
// // //     fd.append('category_name', category);
// // //     if (subCategory) fd.append('sub_category', subCategory);
// // //     fd.append('condition',     condition);
// // //     fd.append('price_type',    negotiable);
// // //     fd.append('contact',       contact);
// // //     fd.append('city',          city);
// // //     fd.append('plan_name',     planName);
// // //     images.forEach((img, i) => fd.append(`image_${i}`, img));
// // //     videos.forEach((vid, i) => fd.append(`video_${i}`, vid));
// // //     onSubmit(fd);
// // //   };

// // //   return (
// // //     <form onSubmit={handleSubmit} className={styles.postAdForm}>
// // //       <Field label={`Title (${title.length}/25)`} required>
// // //         <input className={styles.inputField} placeholder="e.g., IPHONE 13 PRO MAX"
// // //           value={title} maxLength={25}
// // //           onChange={e => setTitle(e.target.value.toUpperCase())} />
// // //       </Field>

// // //       <Field label="Category" required>
// // //         <select className={styles.inputField} value={category}
// // //           onChange={e => { setCategory(e.target.value); setSubCategory(''); }}>
// // //           {CATEGORIES.filter(c => c !== 'All' && c !== ROOMS_CAT && c !== LOST_FOUND_CAT)
// // //             .map(c => <option key={c} value={c}>{c}</option>)}
// // //         </select>
// // //       </Field>

// // //       {/* ── Sub-category — optional, shown when category has defined sub-options ── */}
// // //       {SUB_CATEGORIES[category] && (
// // //         <Field label="Sub-Category (Optional)">
// // //           <select className={`${styles.inputField} ${styles.subCategorySelect}`}
// // //             value={subCategory}
// // //             onChange={e => setSubCategory(e.target.value)}>
// // //             <option value="">— Select sub-category —</option>
// // //             {SUB_CATEGORIES[category].map(sub => (
// // //               <option key={sub} value={sub}>{sub}</option>
// // //             ))}
// // //           </select>
// // //           {subCategory && (
// // //             <p className={styles.subCategoryChosen}>🏷️ {subCategory}</p>
// // //           )}
// // //         </Field>
// // //       )}

// // //       <Field label="Is Product Used?" required>
// // //         <select className={styles.inputField} value={condition} onChange={e => setCondition(e.target.value)}>
// // //           <option value="" disabled>Select condition...</option>
// // //           <option value="new">✨ New</option>
// // //           <option value="slightly_used">👌 Slightly Used</option>
// // //           <option value="used">🔄 Used</option>
// // //         </select>
// // //       </Field>

// // //       <Field label="Price (KES)" required>
// // //         <input type="text" inputMode="numeric" className={styles.inputField}
// // //           placeholder="e.g., 50,000" value={priceDisplay} onChange={handlePriceChange} />
// // //       </Field>

// // //       <Field label="Is Price Negotiable?" required>
// // //         <select className={styles.inputField} value={negotiable} onChange={e => setNegotiable(e.target.value)}>
// // //           <option value="" disabled>Select price type...</option>
// // //           <option value="fixed">🔒 Fixed Price</option>
// // //           <option value="negotiable">🤝 Slightly Negotiable</option>
// // //         </select>
// // //       </Field>

// // //       <Field label="City/Town" required>
// // //         <CityPicker value={city} onChange={setCity} placeholder="Search city or town..." />
// // //       </Field>

// // //       <Field label="Description" required>
// // //         <textarea className={`${styles.inputField} ${styles.textareaField}`}
// // //           placeholder="Describe your item in detail..." rows={4}
// // //           value={description} onChange={e => setDescription(e.target.value)} />
// // //       </Field>

// // //       <Field label="Contact (Email or Phone)" required>
// // //         <input className={styles.inputField}
// // //           placeholder="e.g., +254712345678 or email@example.com"
// // //           value={contact} onChange={e => setContact(e.target.value)} />
// // //       </Field>

// // //       {maxVideos > 0 && (
// // //         <Field label="Upload Videos (Optional)">
// // //           <input type="file" className={styles.fileInput}
// // //             accept="video/mp4,video/webm,video/ogg,video/quicktime" multiple onChange={handleVideos} />
// // //           <p className={styles.helperText}>🎥 Max {maxVideos} videos, 50MB each</p>
// // //           {videos.length > 0 && <p className={styles.selectedFiles}>✅ {videos.length}/{maxVideos} selected</p>}
// // //         </Field>
// // //       )}

// // //       <Field label="Upload Images *">
// // //         <input type="file" className={styles.fileInput} accept="image/*" multiple onChange={handleImages} />
// // //         <p className={styles.helperText}>📷 Max {maxImages} images ({planName} plan)</p>
// // //         {images.length > 0 && <p className={styles.selectedFiles}>✅ {images.length}/{maxImages} selected</p>}
// // //       </Field>

// // //       {maxVideos === 0 && (
// // //         <div className={styles.upgradePrompt}>
// // //           <p>🎥 Want to add videos? Upgrade to <strong>Business Plan</strong> for up to 2 videos!</p>
// // //         </div>
// // //       )}

// // //       <button type="submit" className={styles.submitAdBtn}>Post Ad</button>
// // //     </form>
// // //   );
// // // };

// // // // ══════════════════════════════════════════════════════════════════════════════
// // // // MAIN MODAL
// // // // ══════════════════════════════════════════════════════════════════════════════
// // // const PostAdModal = ({ isOpen, onClose, onAdPosted, selectedPlan, onBackToPlan }) => {
// // //   const { isAuthenticated, getToken } = useAuthContext();
// // //   const [chosenCategory, setChosenCategory] = useState(null);
  
// // //   // ✅ NEW: Promotion state
// // //   const [promotion, setPromotion] = useState(null);
// // //   const [promotionLoading, setPromotionLoading] = useState(false);
// // //   const [finalPrice, setFinalPrice] = useState(null);

// // //   // ✅ Payment state — holds form data + ad_id while waiting for M-Pesa confirmation
// // //   const [showPayment,     setShowPayment]     = useState(false);
// // //   const [pendingFormData, setPendingFormData] = useState(null);
// // //   const [pendingAdId,     setPendingAdId]     = useState(null);

// // //   // ✅ NEW: Check for promotion when modal opens or plan changes
// // //   useEffect(() => {
// // //     if (isOpen && selectedPlan && isAuthenticated) {
// // //       checkPromotion();
// // //     }
// // //   }, [isOpen, selectedPlan, isAuthenticated]);

// // //   // ✅ NEW: Check user promotion
// // //   const checkPromotion = async () => {
// // //     if (!selectedPlan) return;
    
// // //     setPromotionLoading(true);
// // //     try {
// // //       const planMap = {
// // //         'free': '',
// // //         'basic': 'basic',
// // //         'business': 'business'
// // //       };
// // //       const planParam = planMap[selectedPlan.planId] || '';
      
// // //       const promo = await checkUserPromotion(planParam);
      
// // //       if (promo.has_promotion && promo.promotion) {
// // //         setPromotion(promo.promotion);
        
// // //         // Calculate final price
// // //         const originalPrice = selectedPlan.price || 0;
// // //         const pricingInfo = calculatePromotionPrice(
// // //           originalPrice, 
// // //           promo.promotion, 
// // //           planParam
// // //         );
        
// // //         setFinalPrice(pricingInfo);
        
// // //         console.log('✅ Promotion applied:', pricingInfo);
// // //       } else {
// // //         setPromotion(null);
// // //         setFinalPrice(null);
// // //       }
// // //     } catch (error) {
// // //       console.error('Promotion check failed:', error);
// // //       setPromotion(null);
// // //       setFinalPrice(null);
// // //     } finally {
// // //       setPromotionLoading(false);
// // //     }
// // //   };

// // //   if (!isOpen) return null;

// // //   const maxImages  = selectedPlan?.maxImages || 2;
// // //   const maxVideos  = selectedPlan?.maxVideos || 0;
// // //   const planName   = selectedPlan?.planName  || 'Free';
// // //   const isBusiness = selectedPlan?.planId === 'business';
  
// // //   // ✅ NEW: Use promotional price if available
// // //   const displayPrice = finalPrice?.isFree 
// // //     ? 0 
// // //     : finalPrice?.finalPrice ?? selectedPlan?.price ?? 0;

// // //   const handleClose = () => {
// // //     setChosenCategory(null);
// // //     setPromotion(null);
// // //     setFinalPrice(null);
// // //     setShowPayment(false);
// // //     setPendingFormData(null);
// // //     setPendingAdId(null);
// // //     onClose();
// // //   };

// // //   // ── Determine if this plan needs payment ─────────────────────────────────
// // //   const needsPayment = () => {
// // //     if (!selectedPlan || !selectedPlan.price || selectedPlan.price === 0) return false;
// // //     if (selectedPlan.planId === 'free') return false;
// // //     // If user has an active promo that makes it free — skip payment
// // //     if (finalPrice?.isFree) return false;
// // //     return true;
// // //   };

// // //   // ── Called by sub-forms (Rooms / LostFound / Standard) on submit ──────────
// // //   const handleSubmit = async (formData) => {
// // //     if (!isAuthenticated) return alert('Please log in to post an ad');
// // //     if (selectedPlan) {
// // //       formData.append('plan_id',   selectedPlan.planId   || '');
// // //       formData.append('plan_name', selectedPlan.planName || 'Free');
// // //     }

// // //     // ── If paid plan and no promo → create ad as pending, then show payment ─
// // //     if (needsPayment()) {
// // //       // Step 1: POST the ad to backend with status pending
// // //       // This gives us an ad_id which M-Pesa callback needs to activate the plan
// // //       let adId = null;
// // //       try {
// // //         const res = await apiService.postAd(formData, getToken());
// // //         let data = {};
// // //         try { data = await res.json(); } catch (_) {}
// // //         if (!res.ok) {
// // //           alert(data?.error || data?.detail || 'Failed to save ad. Please try again.');
// // //           return;
// // //         }
// // //         adId = data?.id || data?.ad_id || null;
// // //         console.log('✅ Ad created (pending payment), id=', adId);
// // //       } catch (err) {
// // //         console.error(err);
// // //         alert('Network error. Please try again.');
// // //         return;
// // //       }

// // //       // Step 2: Show payment modal with the real ad_id
// // //       setPendingAdId(adId);
// // //       setPendingFormData(formData);
// // //       setShowPayment(true);
// // //       return;   // wait for payment confirmation
// // //     }

// // //     // ── Free / promo → post immediately ──────────────────────────────────
// // //     await postAdToBackend(formData);
// // //   };

// // //   // ── Actually POST the ad (called after payment OR directly for free) ──────
// // //   const postAdToBackend = async (formData) => {
// // //     try {
// // //       const response = await apiService.postAd(formData, getToken());
// // //       let data = {};
// // //       try { data = await response.json(); } catch (_) {}

// // //       if (response.ok) {
// // //         // Use promotion slot after successful post
// // //         if (promotion && selectedPlan) {
// // //           const planMap = { 'free': '', 'basic': 'basic', 'pro': 'pro', 'business': 'business' };
// // //           const planParam = planMap[selectedPlan.planId] || '';
// // //           if (planParam) {
// // //             try { await consumePromotionSlot(planParam); } catch (_) {}
// // //           }
// // //         }
// // //         alert('Ad posted successfully!');
// // //         handleClose();
// // //         if (typeof onAdPosted === 'function') onAdPosted();
// // //       } else {
// // //         alert(data?.error || data?.detail || 'Failed to post ad. Please try again.');
// // //       }
// // //     } catch (err) {
// // //       console.error(err);
// // //       alert('Network error. Please try again.');
// // //     }
// // //   };

// // //   // ── Payment confirmed → ad already exists, just notify parent ──────────────
// // //   // The Django callback already activated AdPlan when payment was confirmed.
// // //   const handlePaymentSuccess = async (paymentData) => {
// // //     setShowPayment(false);
// // //     setPendingFormData(null);
// // //     setPendingAdId(null);
// // //     alert('Ad posted successfully! Your plan is now active.');
// // //     handleClose();
// // //     if (typeof onAdPosted === 'function') onAdPosted();
// // //   };

// // //   const titles = {
// // //     [ROOMS_CAT]:      { title: 'Post Your Listing',  sub: 'Fill in the details to list your room' },
// // //     [LOST_FOUND_CAT]: { title: 'Post Lost & Found',  sub: 'Fill in the details to report the item' },
// // //     default:          { title: 'Post Your Ad',        sub: 'Fill in the details to list your item' },
// // //   };
// // //   const { title, sub } = titles[chosenCategory] || titles.default;

// // //   return (
// // //     <>
// // //     <div className={styles.modalOverlay} onClick={handleClose}>
// // //       <div className={styles.postAdModal} onClick={e => e.stopPropagation()}>
// // //         <button className={styles.closeBtn} onClick={handleClose}>×</button>
// // //         <h2 className={styles.modalTitle}>{title}</h2>
// // //         <p className={styles.modalSubtitle}>{sub}</p>

// // //         {/* ── Plan banner ── */}
// // //         {selectedPlan && (
// // //           <div className={`${styles.selectedPlanBanner} ${isBusiness ? styles.selectedPlanBannerBusiness : ''}`}>
// // //             <div className={styles.planInfo}>
// // //               <span className={`${styles.planBadge} ${isBusiness ? styles.planBadgeBusiness : ''}`}>
// // //                 {isBusiness ? '⭐ ' : ''}{planName}
// // //               </span>
// // //               <div className={styles.planLimits}>
// // //                 <span>📷 Up to {maxImages} images</span>
// // //                 {maxVideos > 0 && <span>🎥 Up to {maxVideos} videos</span>}
// // //                 {selectedPlan.displayDays && <span>⏱️ {selectedPlan.displayDays} days</span>}
// // //               </div>
// // //             </div>
            
// // //             {/* ← Change Plan button */}
// // //             {onBackToPlan && (
// // //               <button
// // //                 onClick={onBackToPlan}
// // //                 style={{
// // //                   background: 'none', border: '1.5px solid rgba(255,255,255,0.5)',
// // //                   borderRadius: 8, color: 'inherit', fontSize: 12, fontWeight: 600,
// // //                   padding: '4px 12px', cursor: 'pointer', whiteSpace: 'nowrap',
// // //                   opacity: 0.85, transition: 'opacity .15s',
// // //                 }}
// // //                 onMouseEnter={e => e.target.style.opacity = 1}
// // //                 onMouseLeave={e => e.target.style.opacity = 0.85}
// // //               >
// // //                 ← Change Plan
// // //               </button>
// // //             )}

// // //           {/* ✅ NEW: Show promotional or regular price */}
// // //             {displayPrice > 0 ? (
// // //               <div className={`${styles.planPrice} ${isBusiness ? styles.planPriceBusiness : ''}`}>
// // //                 {finalPrice && finalPrice.discountAmount > 0 && (
// // //                   <div className={styles.originalPrice}>
// // //                     <del>KES {selectedPlan.price}</del>
// // //                   </div>
// // //                 )}
// // //                 <div>KES {displayPrice}</div>
// // //               </div>
// // //             ) : displayPrice === 0 && finalPrice?.isFree ? (
// // //               <div className={`${styles.planPrice} ${styles.planPriceFree}`}>
// // //                 FREE
// // //               </div>
// // //             ) : null}
// // //           </div>
// // //         )}

// // //         {/* ✅ NEW: Promotion label */}
// // //         {finalPrice && finalPrice.label && (
// // //           <div className={styles.promotionBanner}>
// // //             {finalPrice.label}
// // //           </div>
// // //         )}

// // //         {/* ── Business slot notice ── */}
// // //         {isBusiness && (
// // //           <div className={styles.slotBanner}>
// // //             🔄 <strong>Slot-based plan:</strong> Pay once &amp; post freely — delete and re-post
// // //             your ad any time within the 3-month window at no extra charge.
// // //           </div>
// // //         )}

// // //         {/* ── Category picker ── */}
// // //         {chosenCategory === null && (
// // //           <div className={styles.categoryPicker}>
// // //             <div className={styles.formGroup}>
// // //               <label>Category <span className={styles.req}>*</span></label>
// // //               <select
// // //                 className={styles.inputField}
// // //                 defaultValue=""
// // //                 onChange={e => { if (e.target.value) setChosenCategory(e.target.value); }}
// // //               >
// // //                 <option value="" disabled>Select a category...</option>
// // //                 {CATEGORIES.filter(c => c !== 'All').map(c => (
// // //                   <option key={c} value={c}>{c}</option>
// // //                 ))}
// // //               </select>
// // //             </div>
// // //           </div>
// // //         )}

// // //         {chosenCategory === ROOMS_CAT && (
// // //           <RoomsForm maxImages={maxImages} maxVideos={maxVideos} planName={planName} onSubmit={handleSubmit} />
// // //         )}
// // //         {chosenCategory === LOST_FOUND_CAT && (
// // //           <LostFoundForm maxVideos={maxVideos} planName={planName} onSubmit={handleSubmit} />
// // //         )}
// // //         {chosenCategory !== null && chosenCategory !== ROOMS_CAT && chosenCategory !== LOST_FOUND_CAT && (
// // //           <StandardForm initialCategory={chosenCategory} maxImages={maxImages}
// // //             maxVideos={maxVideos} planName={planName} onSubmit={handleSubmit} />
// // //         )}

// // //         {chosenCategory !== null && (
// // //           <button className={styles.backBtn} onClick={() => setChosenCategory(null)}>
// // //             ← Change category
// // //           </button>
// // //         )}
// // //       </div>
// // //     </div>

// // //     {/* ── M-Pesa Payment Modal — shown after user fills form and clicks submit ── */}
// // //     <PaymentModal
// // //       isOpen={showPayment}
// // //       plan={selectedPlan}
// // //       adId={pendingAdId}
// // //       onSuccess={handlePaymentSuccess}
// // //       onClose={() => { setShowPayment(false); setPendingFormData(null); setPendingAdId(null); }}
// // //     />
// // //     </>
// // //   );
// // // };

// // // export default PostAdModal;
























// // // // import React, { useState, useRef, useEffect } from 'react';
// // // // import { useAuthContext } from '../../../context/AuthContext';
// // // // import apiService from '../../../services/api';
// // // // import { CATEGORIES, SUB_CATEGORIES } from '../../../utils/constants';
// // // // import { checkUserPromotion, consumePromotionSlot, calculatePromotionPrice } from '../../../utils/promotionApi'; // ✅ NEW
// // // // import styles from './PostAdModal.module.css';

// // // // const ROOMS_CAT      = 'Rooms to Let';
// // // // const LOST_FOUND_CAT = 'Lost & Found';

// // // // // ── Location search via OpenStreetMap Nominatim ───────────────────────────
// // // // const searchKenyaPlaces = async (query) => {
// // // //   if (!query || query.trim().length < 2) return [];
// // // //   try {
// // // //     const url =
// // // //       `https://nominatim.openstreetmap.org/search` +
// // // //       `?q=${encodeURIComponent(query + ', Kenya')}` +
// // // //       `&countrycodes=ke&limit=12&format=json&addressdetails=1&accept-language=en`;
// // // //     const res = await fetch(url, {
// // // //       headers: { 'User-Agent': 'ImSellingKE/1.0 (marketplace)' }
// // // //     });
// // // //     const data = await res.json();
// // // //     return data.map(item => {
// // // //       const a = item.address;
// // // //       const label =
// // // //         a.suburb || a.neighbourhood || a.quarter || a.village ||
// // // //         a.hamlet || a.town || a.city_district || a.city ||
// // // //         item.display_name.split(',')[0];
// // // //       const parent =
// // // //         a.suburb        ? (a.town || a.city || a.county) :
// // // //         a.village       ? (a.town || a.city || a.county) :
// // // //         a.hamlet        ? (a.town || a.city || a.county) :
// // // //         a.neighbourhood ? (a.city || a.county)           : null;
// // // //       const display = parent ? `${label.trim()}, ${parent.trim()}` : label.trim();
// // // //       return { label: label.trim(), display, osm_id: item.osm_id };
// // // //     }).filter((v, i, arr) => arr.findIndex(x => x.display === v.display) === i);
// // // //   } catch (e) {
// // // //     console.error('Location search error:', e);
// // // //     return [];
// // // //   }
// // // // };

// // // // const Field = ({ label, required, children }) => (
// // // //   <div className={styles.formGroup}>
// // // //     <label>{label} {required && <span className={styles.req}>*</span>}</label>
// // // //     {children}
// // // //   </div>
// // // // );

// // // // // ── CityPicker ────────────────────────────────────────────────────────────
// // // // const CityPicker = ({ value, onChange, placeholder = 'Search village, estate, street, town...' }) => {
// // // //   const [query,     setQuery]     = useState(value || '');
// // // //   const [results,   setResults]   = useState([]);
// // // //   const [open,      setOpen]      = useState(false);
// // // //   const [active,    setActive]    = useState(-1);
// // // //   const [loading,   setLoading]   = useState(false);
// // // //   const [confirmed, setConfirmed] = useState(!!value);
// // // //   const wrapRef  = useRef(null);
// // // //   const inputRef = useRef(null);
// // // //   const timerRef = useRef(null);

// // // //   useEffect(() => {
// // // //     const fn = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
// // // //     document.addEventListener('mousedown', fn);
// // // //     return () => document.removeEventListener('mousedown', fn);
// // // //   }, []);

// // // //   const runSearch = (q) => {
// // // //     clearTimeout(timerRef.current);
// // // //     if (!q || q.trim().length < 2) { setResults([]); setLoading(false); return; }
// // // //     setLoading(true);
// // // //     timerRef.current = setTimeout(async () => {
// // // //       const found = await searchKenyaPlaces(q);
// // // //       setResults(found);
// // // //       setOpen(found.length > 0);
// // // //       setActive(0);
// // // //       setLoading(false);
// // // //     }, 400);
// // // //   };

// // // //   const select = (item) => {
// // // //     setQuery(item.display);
// // // //     onChange(item.display);
// // // //     setConfirmed(true);
// // // //     setOpen(false);
// // // //     setResults([]);
// // // //   };

// // // //   const clear = () => {
// // // //     setQuery(''); onChange('');
// // // //     setConfirmed(false); setResults([]); setOpen(false);
// // // //     clearTimeout(timerRef.current);
// // // //     inputRef.current?.focus();
// // // //   };

// // // //   const handleInput = (e) => {
// // // //     const v = e.target.value;
// // // //     setQuery(v);
// // // //     setConfirmed(false);
// // // //     onChange('');
// // // //     runSearch(v);
// // // //   };

// // // //   const handleKey = (e) => {
// // // //     if (e.key === 'ArrowDown') { e.preventDefault(); setActive(i => Math.min(i + 1, results.length - 1)); }
// // // //     if (e.key === 'ArrowUp')   { e.preventDefault(); setActive(i => Math.max(i - 1, 0)); }
// // // //     if (e.key === 'Enter')     { e.preventDefault(); if (results[active]) select(results[active]); }
// // // //     if (e.key === 'Escape')    { setOpen(false); }
// // // //   };

// // // //   return (
// // // //     <div ref={wrapRef} className={styles.cityWrap}>
// // // //       <div className={styles.cityInputWrap}>
// // // //         <span className={styles.citySearchIcon}>🔍</span>
// // // //         <input
// // // //           ref={inputRef}
// // // //           type="text"
// // // //           className={`${styles.inputField} ${styles.cityInput} ${confirmed ? styles.cityInputConfirmed : ''}`}
// // // //           placeholder={placeholder}
// // // //           value={query}
// // // //           onChange={handleInput}
// // // //           onFocus={() => results.length > 0 && setOpen(true)}
// // // //           onKeyDown={handleKey}
// // // //           autoComplete="off"
// // // //           spellCheck="false"
// // // //         />
// // // //         {loading && <span className={styles.citySpinner} title="Searching...">⏳</span>}
// // // //         {!loading && query && (
// // // //           <button type="button" className={styles.cityClear} onClick={clear}>×</button>
// // // //         )}
// // // //       </div>
// // // //       {confirmed && <p className={styles.cityConfirmedMsg}>✅ Location set</p>}
// // // //       {!confirmed && query.length === 1 && (
// // // //         <p className={styles.cityHint}>Type one more character to search…</p>
// // // //       )}
// // // //       {open && results.length > 0 && (
// // // //         <ul className={styles.cityDropdown}>
// // // //           {results.map((item, i) => (
// // // //             <li
// // // //               key={`${item.osm_id}-${i}`}
// // // //               className={`${styles.cityOption} ${i === active ? styles.cityOptionActive : ''}`}
// // // //               onMouseDown={() => select(item)}
// // // //               onMouseEnter={() => setActive(i)}
// // // //             >
// // // //               <span className={styles.cityPin}>📍</span>
// // // //               <span>
// // // //                 <span className={styles.cityOptionLabel}>{item.label}</span>
// // // //                 {item.display !== item.label && (
// // // //                   <span className={styles.cityOptionSub}>
// // // //                     {', ' + item.display.replace(item.label + ', ', '')}
// // // //                   </span>
// // // //                 )}
// // // //               </span>
// // // //             </li>
// // // //           ))}
// // // //         </ul>
// // // //       )}
// // // //       {!loading && open && results.length === 0 && query.length >= 2 && (
// // // //         <div className={styles.cityNoResult}>
// // // //           No results for "<strong>{query}</strong>" — try a nearby street or area name
// // // //         </div>
// // // //       )}
// // // //     </div>
// // // //   );
// // // // };

// // // // // ══════════════════════════════════════════════════════════════════════════════
// // // // // ROOMS TO LET FORM
// // // // // ══════════════════════════════════════════════════════════════════════════════
// // // // const RoomsForm = ({ maxImages, maxVideos, planName, onSubmit }) => {
// // // //   const [title,        setTitle]        = useState('');
// // // //   const [location,     setLocation]     = useState('');
// // // //   const [price,        setPrice]        = useState('');
// // // //   const [priceDisplay, setPriceDisplay] = useState('');
// // // //   const [negotiable,   setNegotiable]   = useState('');
// // // //   const [propType,     setPropType]     = useState('');
// // // //   const [furnished,    setFurnished]    = useState('');
// // // //   const [availFrom,    setAvailFrom]    = useState('');
// // // //   const [description,  setDescription]  = useState('');
// // // //   const [contact,      setContact]      = useState('');
// // // //   const [images,       setImages]       = useState([]);
// // // //   const [videos,       setVideos]       = useState([]);

// // // //   const handleRoomPrice = (e) => {
// // // //     const raw = e.target.value.replace(/,/g, '').replace(/[^\d]/g, '');
// // // //     setPrice(raw);
// // // //     setPriceDisplay(raw ? Number(raw).toLocaleString('en-KE') : '');
// // // //   };

// // // //   const handleImages = (e) => {
// // // //     const files = Array.from(e.target.files);
// // // //     if (files.length > maxImages)
// // // //       alert(`Only the first ${maxImages} photo${maxImages > 1 ? 's' : ''} will be uploaded. The rest will be ignored.`);
// // // //     setImages(files.slice(0, maxImages));
// // // //   };

// // // //   const handleVideos = (e) => {
// // // //     if (maxVideos === 0) return alert('Video upload requires Business plan');
// // // //     const all = Array.from(e.target.files);
// // // //     const valid = all.filter(f => f.size <= 50*1024*1024);
// // // //     const oversized = all.length - valid.length;
// // // //     const overCount = valid.length - maxVideos;
// // // //     if (oversized > 0) alert(`${oversized} video${oversized > 1 ? 's were' : ' was'} removed (exceeds 50MB limit).`);
// // // //     if (overCount > 0) alert(`Only ${maxVideos} video${maxVideos > 1 ? 's' : ''} allowed. The rest will be ignored.`);
// // // //     setVideos(valid.slice(0, maxVideos));
// // // //   };

// // // //   const handleSubmit = (e) => {
// // // //     e.preventDefault();
// // // //     if (!title)            return alert('Please enter a title');
// // // //     if (title.length > 25) return alert('Title must be 25 characters or less');
// // // //     if (!location)         return alert('Please select a location');
// // // //     if (!price)            return alert('Please enter rental price');
// // // //     if (!negotiable)       return alert('Please select price type');
// // // //     if (!propType)         return alert('Please select property type');
// // // //     if (!furnished)        return alert('Please select furnished option');
// // // //     if (!availFrom)        return alert('Please select availability date');
// // // //     if (!description)      return alert('Please add a description');
// // // //     if (!contact)          return alert('Please enter contact info');

// // // //     const fd = new FormData();
// // // //     fd.append('title',         title);
// // // //     fd.append('description',   `${description}\n\nProperty Type: ${propType}\nFurnished: ${furnished}\nAvailable From: ${availFrom}`);
// // // //     fd.append('price',         price);
// // // //     fd.append('category_name', ROOMS_CAT);
// // // //     fd.append('condition',     'new');
// // // //     fd.append('price_type',    negotiable);
// // // //     fd.append('contact',       contact);
// // // //     fd.append('city',          location);
// // // //     fd.append('plan_name',     planName);
// // // //     images.forEach((img, i) => fd.append(`image_${i}`, img));
// // // //     videos.forEach((vid, i) => fd.append(`video_${i}`, vid));
// // // //     onSubmit(fd);
// // // //   };

// // // //   return (
// // // //     <form onSubmit={handleSubmit} className={styles.postAdForm}>
// // // //       <Field label={`Title (${title.length}/25)`} required>
// // // //         <input className={styles.inputField} placeholder="e.g., SPACIOUS 2 BEDROOM"
// // // //           value={title} maxLength={25}
// // // //           onChange={e => setTitle(e.target.value.toUpperCase())} />
// // // //       </Field>
// // // //       <Field label="Category" required>
// // // //         <div className={styles.lockedCategory}>🏠 Rooms to Let</div>
// // // //       </Field>
// // // //       <Field label="Location" required>
// // // //         <CityPicker value={location} onChange={setLocation} placeholder="Search city or town..." />
// // // //       </Field>
// // // //       <Field label="Rental Price (KES)" required>
// // // //         <input type="text" inputMode="numeric" className={styles.inputField}
// // // //           placeholder="e.g., 10,000" value={priceDisplay} onChange={handleRoomPrice} />
// // // //       </Field>
// // // //       <div className={styles.twoCol}>
// // // //         <Field label="Is Rent Negotiable?" required>
// // // //           <select className={styles.inputField} value={negotiable} onChange={e => setNegotiable(e.target.value)}>
// // // //             <option value="" disabled>Select price type...</option>
// // // //             <option value="fixed">🔒 Fixed Price</option>
// // // //             <option value="negotiable">🤝 Negotiable</option>
// // // //           </select>
// // // //         </Field>
// // // //         <Field label="Property Type" required>
// // // //           <select className={styles.inputField} value={propType} onChange={e => setPropType(e.target.value)}>
// // // //             <option value="" disabled>Select type...</option>
// // // //             <option value="bedsitter">Bedsitter</option>
// // // //             <option value="single_room">Single Room</option>
// // // //             <option value="1_bedroom">1 Bedroom</option>
// // // //             <option value="2_bedroom">2 Bedroom</option>
// // // //             <option value="3_bedroom">3 Bedroom</option>
// // // //             <option value="studio">Studio</option>
// // // //             <option value="mansion">Mansion</option>
// // // //             <option value="airbnb">AirBnB</option>
// // // //             <option value="warehouse">Warehouse</option>
// // // //             <option value="office">Office Space</option>
// // // //             <option value="shop">Shop / Stall</option>
// // // //           </select>
// // // //         </Field>
// // // //       </div>
// // // //       <div className={styles.twoCol}>
// // // //         <Field label="Furnished?" required>
// // // //           <select className={styles.inputField} value={furnished} onChange={e => setFurnished(e.target.value)}>
// // // //             <option value="" disabled>Select option...</option>
// // // //             <option value="fully_furnished">Fully Furnished</option>
// // // //             <option value="semi_furnished">Semi-Furnished</option>
// // // //             <option value="unfurnished">Unfurnished</option>
// // // //           </select>
// // // //         </Field>
// // // //         <Field label="Available From" required>
// // // //           <input type="date" className={styles.inputField}
// // // //             value={availFrom} onChange={e => setAvailFrom(e.target.value)}
// // // //             min={new Date().toISOString().split('T')[0]} />
// // // //         </Field>
// // // //       </div>
// // // //       <Field label="Description" required>
// // // //         <textarea className={`${styles.inputField} ${styles.textareaField}`}
// // // //           placeholder="Describe your room and amenities..." rows={4}
// // // //           value={description} onChange={e => setDescription(e.target.value)} />
// // // //       </Field>
// // // //       <Field label="Contact (Email or Phone)" required>
// // // //         <input className={styles.inputField}
// // // //           placeholder="e.g., +254712345678 or email@example.com"
// // // //           value={contact} onChange={e => setContact(e.target.value)} />
// // // //       </Field>
// // // //       {maxVideos > 0 && (
// // // //         <Field label="Upload Videos (Optional)">
// // // //           <input type="file" className={styles.fileInput}
// // // //             accept="video/mp4,video/webm,video/ogg,video/quicktime" multiple onChange={handleVideos} />
// // // //           <p className={styles.helperText}>🎥 Max {maxVideos} videos, 50MB each</p>
// // // //           {videos.length > 0 && <p className={styles.selectedFiles}>✅ {videos.length}/{maxVideos} selected</p>}
// // // //         </Field>
// // // //       )}
// // // //       <Field label="Upload Images (Optional)">
// // // //         <div className={styles.uploadBox}>
// // // //           <label className={styles.uploadLabel} htmlFor="rooms-images">
// // // //             <span>📷</span> Choose Files
// // // //           </label>
// // // //           <input id="rooms-images" type="file" className={styles.hiddenInput}
// // // //             accept="image/*" multiple onChange={handleImages} />
// // // //           <p className={styles.helperText}>Upload up to {maxImages} photos (Max 10 MB each)</p>
// // // //           {images.length > 0 && <p className={styles.selectedFiles}>✅ {images.length}/{maxImages} selected</p>}
// // // //         </div>
// // // //       </Field>
// // // //       {maxVideos === 0 && (
// // // //         <div className={styles.upgradePrompt}>
// // // //           <p>🎥 Want to add videos? Upgrade to <strong>Business Plan</strong> for up to 2 videos!</p>
// // // //         </div>
// // // //       )}
// // // //       <button type="submit" className={styles.submitAdBtn}>Post Listing</button>
// // // //     </form>
// // // //   );
// // // // };

// // // // // ══════════════════════════════════════════════════════════════════════════════
// // // // // LOST & FOUND FORM
// // // // // ══════════════════════════════════════════════════════════════════════════════
// // // // const LostFoundForm = ({ maxVideos, planName, onSubmit }) => {
// // // //   const [itemStatus,   setItemStatus]   = useState('lost');
// // // //   const [itemName,     setItemName]     = useState('');
// // // //   const [itemCategory, setItemCategory] = useState('');
// // // //   const [location,     setLocation]     = useState('');
// // // //   const [dateLost,     setDateLost]     = useState('');
// // // //   const [description,  setDescription]  = useState('');
// // // //   const [contact,      setContact]      = useState('');
// // // //   const [images,       setImages]       = useState([]);
// // // //   const [videos,       setVideos]       = useState([]);

// // // //   const LF_CATS = [
// // // //     'Electronics','Documents & ID','Pets','Bags & Luggage',
// // // //     'Clothing & Accessories','Keys','Wallets & Purses','Jewellery',
// // // //     'Vehicles','Books & Stationery','Other',
// // // //   ];

// // // //   const handleLFVideos = (e) => {
// // // //     if (maxVideos === 0) return alert('Video upload requires Business plan');
// // // //     const all = Array.from(e.target.files);
// // // //     const valid = all.filter(f => f.size <= 50*1024*1024);
// // // //     const oversized = all.length - valid.length;
// // // //     const overCount = valid.length - maxVideos;
// // // //     if (oversized > 0) alert(`${oversized} video${oversized > 1 ? 's were' : ' was'} removed (exceeds 50MB limit).`);
// // // //     if (overCount > 0) alert(`Only ${maxVideos} video${maxVideos > 1 ? 's' : ''} allowed. The rest will be ignored.`);
// // // //     setVideos(valid.slice(0, maxVideos));
// // // //   };

// // // //   const handleSubmit = (e) => {
// // // //     e.preventDefault();
// // // //     if (!itemName)     return alert('Please enter item name');
// // // //     if (!itemCategory) return alert('Please select a category');
// // // //     if (!location)     return alert('Please enter a location');
// // // //     if (!dateLost)     return alert('Please select date lost/found');
// // // //     if (!description)  return alert('Please add a description');
// // // //     if (!contact)      return alert('Please enter contact info');

// // // //     const fd = new FormData();
// // // //     fd.append('title',         `[${itemStatus.toUpperCase()}] ${itemName}`);
// // // //     fd.append('description',   `Status: ${itemStatus}\nCategory: ${itemCategory}\nDate: ${dateLost}\n\n${description}`);
// // // //     fd.append('price',         '0');
// // // //     fd.append('category_name', LOST_FOUND_CAT);
// // // //     fd.append('condition',     'used');
// // // //     fd.append('price_type',    'fixed');
// // // //     fd.append('contact',       contact);
// // // //     fd.append('city',          location);
// // // //     fd.append('plan_name',     planName);
// // // //     images.forEach((img, i) => fd.append(`image_${i}`, img));
// // // //     videos.forEach((vid, i) => fd.append(`video_${i}`, vid));
// // // //     onSubmit(fd);
// // // //   };

// // // //   return (
// // // //     <form onSubmit={handleSubmit} className={styles.postAdForm}>
// // // //       <Field label="Status" required>
// // // //         <select className={styles.inputField} value={itemStatus} onChange={e => setItemStatus(e.target.value)}>
// // // //           <option value="lost">🔍 Lost</option>
// // // //           <option value="found">✅ Found</option>
// // // //         </select>
// // // //       </Field>
// // // //       <Field label={`Item Name (${itemName.length}/25)`} required>
// // // //         <input className={styles.inputField}
// // // //           placeholder="e.g., BLACK BACKPACK, CAR KEY"
// // // //           value={itemName} maxLength={25}
// // // //           onChange={e => setItemName(e.target.value.toUpperCase())} />
// // // //       </Field>
// // // //       <Field label="Category" required>
// // // //         <select className={styles.inputField} value={itemCategory} onChange={e => setItemCategory(e.target.value)}>
// // // //           <option value="" disabled>Select category...</option>
// // // //           {LF_CATS.map(c => <option key={c} value={c}>{c}</option>)}
// // // //         </select>
// // // //       </Field>
// // // //       <Field label={itemStatus === 'lost' ? 'Where Was It Lost?' : 'Where Was It Found?'} required>
// // // //         <input className={styles.inputField}
// // // //           placeholder="e.g., Central Mall, Nairobi CBD, Uhuru Park"
// // // //           value={location} onChange={e => setLocation(e.target.value)} />
// // // //       </Field>
// // // //       <Field label="Date Lost/Found" required>
// // // //         <input type="date" className={styles.inputField}
// // // //           value={dateLost} onChange={e => setDateLost(e.target.value)}
// // // //           max={new Date().toISOString().split('T')[0]} />
// // // //       </Field>
// // // //       <Field label="Description" required>
// // // //         <textarea className={`${styles.inputField} ${styles.textareaField}`}
// // // //           placeholder="Describe the item, its appearance, and how to contact you..."
// // // //           rows={4} value={description} onChange={e => setDescription(e.target.value)} />
// // // //       </Field>
// // // //       <Field label="Contact (Email or Phone)" required>
// // // //         <input className={styles.inputField}
// // // //           placeholder="e.g., +254712345678 or email@example.com"
// // // //           value={contact} onChange={e => setContact(e.target.value)} />
// // // //       </Field>
// // // //       {maxVideos > 0 && (
// // // //         <Field label="Upload Videos (Optional)">
// // // //           <input type="file" className={styles.fileInput}
// // // //             accept="video/mp4,video/webm,video/ogg,video/quicktime" multiple onChange={handleLFVideos} />
// // // //           <p className={styles.helperText}>🎥 Max {maxVideos} videos, 50MB each</p>
// // // //           {videos.length > 0 && <p className={styles.selectedFiles}>✅ {videos.length}/{maxVideos} selected</p>}
// // // //         </Field>
// // // //       )}
// // // //       <Field label="Upload Images (Optional)">
// // // //         <div className={styles.uploadBox}>
// // // //           <label className={styles.uploadLabel} htmlFor="lf-images">
// // // //             <span>📷</span> Upload Photos <span className={styles.uploadChevron}>▾</span>
// // // //           </label>
// // // //           <input id="lf-images" type="file" className={styles.hiddenInput}
// // // //             accept="image/*" multiple onChange={e => {
// // // //               const files = Array.from(e.target.files);
// // // //               if (files.length > 6) alert('Only the first 6 photos will be uploaded. The rest will be ignored.');
// // // //               setImages(files.slice(0, 6));
// // // //             }} />
// // // //           <p className={styles.helperText}>🖼️ Upload up to 6 photos: JPG, PNG (Max 10 MB each)</p>
// // // //           {images.length > 0 && <p className={styles.selectedFiles}>✅ {images.length}/6 selected</p>}
// // // //         </div>
// // // //       </Field>
// // // //       {maxVideos === 0 && (
// // // //         <div className={styles.upgradePrompt}>
// // // //           <p>🎥 Want to add videos? Upgrade to <strong>Business Plan</strong> for up to 2 videos!</p>
// // // //         </div>
// // // //       )}
// // // //       <button type="submit" className={styles.submitAdBtn}>Post Listing</button>
// // // //     </form>
// // // //   );
// // // // };

// // // // // ══════════════════════════════════════════════════════════════════════════════
// // // // // STANDARD FORM
// // // // // ══════════════════════════════════════════════════════════════════════════════
// // // // const StandardForm = ({ initialCategory, maxImages, maxVideos, planName, onSubmit }) => {
// // // //   const [title,        setTitle]        = useState('');
// // // //   const [category,     setCategory]     = useState(initialCategory || 'Electronics');
// // // //   const [subCategory,  setSubCategory]  = useState('');
// // // //   const [condition,    setCondition]    = useState('');
// // // //   const [price,        setPrice]        = useState('');
// // // //   const [priceDisplay, setPriceDisplay] = useState('');
// // // //   const [negotiable,   setNegotiable]   = useState('');
// // // //   const [city,         setCity]         = useState('');
// // // //   const [description,  setDescription]  = useState('');
// // // //   const [contact,      setContact]      = useState('');
// // // //   const [images,       setImages]       = useState([]);
// // // //   const [videos,       setVideos]       = useState([]);

// // // //   const handlePriceChange = (e) => {
// // // //     const raw = e.target.value.replace(/,/g, '').replace(/[^\d]/g, '');
// // // //     setPrice(raw);
// // // //     setPriceDisplay(raw ? Number(raw).toLocaleString('en-KE') : '');
// // // //   };

// // // //   const handleImages = (e) => {
// // // //     const files = Array.from(e.target.files);
// // // //     if (files.length > maxImages)
// // // //       alert(`Only the first ${maxImages} photo${maxImages > 1 ? 's' : ''} will be uploaded. The rest will be ignored.`);
// // // //     setImages(files.slice(0, maxImages));
// // // //   };

// // // //   const handleVideos = (e) => {
// // // //     if (maxVideos === 0) return alert('Video upload requires Business plan');
// // // //     const all = Array.from(e.target.files);
// // // //     const valid = all.filter(f => f.size <= 50*1024*1024);
// // // //     const oversized = all.length - valid.length;
// // // //     const overCount = valid.length - maxVideos;
// // // //     if (oversized > 0) alert(`${oversized} video${oversized > 1 ? 's were' : ' was'} removed (exceeds 50MB limit).`);
// // // //     if (overCount > 0) alert(`Only ${maxVideos} video${maxVideos > 1 ? 's' : ''} allowed. The rest will be ignored.`);
// // // //     setVideos(valid.slice(0, maxVideos));
// // // //   };

// // // //   const handleSubmit = (e) => {
// // // //     e.preventDefault();
// // // //     if (title.length < 5)        return alert('Title must be at least 5 characters');
// // // //     if (title.length > 25)       return alert('Title must be 25 characters or less');
// // // //     if (description.length < 10) return alert('Description must be at least 10 characters');
// // // //     if (!city)                   return alert('Please enter a city/town');
// // // //     if (!condition)              return alert('Please select product condition');
// // // //     if (!negotiable)             return alert('Please select price type');
// // // //     if (!contact)                return alert('Please enter contact info');
// // // //     if (!images.length && !videos.length) return alert('Please upload at least one image or video');

// // // //     const fd = new FormData();
// // // //     fd.append('title',         title);
// // // //     fd.append('description',   description);
// // // //     fd.append('price',         price);
// // // //     fd.append('category_name', category);
// // // //     if (subCategory) fd.append('sub_category', subCategory);
// // // //     fd.append('condition',     condition);
// // // //     fd.append('price_type',    negotiable);
// // // //     fd.append('contact',       contact);
// // // //     fd.append('city',          city);
// // // //     fd.append('plan_name',     planName);
// // // //     images.forEach((img, i) => fd.append(`image_${i}`, img));
// // // //     videos.forEach((vid, i) => fd.append(`video_${i}`, vid));
// // // //     onSubmit(fd);
// // // //   };

// // // //   return (
// // // //     <form onSubmit={handleSubmit} className={styles.postAdForm}>
// // // //       <Field label={`Title (${title.length}/25)`} required>
// // // //         <input className={styles.inputField} placeholder="e.g., IPHONE 13 PRO MAX"
// // // //           value={title} maxLength={25}
// // // //           onChange={e => setTitle(e.target.value.toUpperCase())} />
// // // //       </Field>

// // // //       <Field label="Category" required>
// // // //         <select className={styles.inputField} value={category}
// // // //           onChange={e => { setCategory(e.target.value); setSubCategory(''); }}>
// // // //           {CATEGORIES.filter(c => c !== 'All' && c !== ROOMS_CAT && c !== LOST_FOUND_CAT)
// // // //             .map(c => <option key={c} value={c}>{c}</option>)}
// // // //         </select>
// // // //       </Field>

// // // //       {/* ── Sub-category — optional, shown when category has defined sub-options ── */}
// // // //       {SUB_CATEGORIES[category] && (
// // // //         <Field label="Sub-Category (Optional)">
// // // //           <select className={`${styles.inputField} ${styles.subCategorySelect}`}
// // // //             value={subCategory}
// // // //             onChange={e => setSubCategory(e.target.value)}>
// // // //             <option value="">— Select sub-category —</option>
// // // //             {SUB_CATEGORIES[category].map(sub => (
// // // //               <option key={sub} value={sub}>{sub}</option>
// // // //             ))}
// // // //           </select>
// // // //           {subCategory && (
// // // //             <p className={styles.subCategoryChosen}>🏷️ {subCategory}</p>
// // // //           )}
// // // //         </Field>
// // // //       )}

// // // //       <Field label="Is Product Used?" required>
// // // //         <select className={styles.inputField} value={condition} onChange={e => setCondition(e.target.value)}>
// // // //           <option value="" disabled>Select condition...</option>
// // // //           <option value="new">✨ New</option>
// // // //           <option value="slightly_used">👌 Slightly Used</option>
// // // //           <option value="used">🔄 Used</option>
// // // //         </select>
// // // //       </Field>

// // // //       <Field label="Price (KES)" required>
// // // //         <input type="text" inputMode="numeric" className={styles.inputField}
// // // //           placeholder="e.g., 50,000" value={priceDisplay} onChange={handlePriceChange} />
// // // //       </Field>

// // // //       <Field label="Is Price Negotiable?" required>
// // // //         <select className={styles.inputField} value={negotiable} onChange={e => setNegotiable(e.target.value)}>
// // // //           <option value="" disabled>Select price type...</option>
// // // //           <option value="fixed">🔒 Fixed Price</option>
// // // //           <option value="negotiable">🤝 Slightly Negotiable</option>
// // // //         </select>
// // // //       </Field>

// // // //       <Field label="City/Town" required>
// // // //         <CityPicker value={city} onChange={setCity} placeholder="Search city or town..." />
// // // //       </Field>

// // // //       <Field label="Description" required>
// // // //         <textarea className={`${styles.inputField} ${styles.textareaField}`}
// // // //           placeholder="Describe your item in detail..." rows={4}
// // // //           value={description} onChange={e => setDescription(e.target.value)} />
// // // //       </Field>

// // // //       <Field label="Contact (Email or Phone)" required>
// // // //         <input className={styles.inputField}
// // // //           placeholder="e.g., +254712345678 or email@example.com"
// // // //           value={contact} onChange={e => setContact(e.target.value)} />
// // // //       </Field>

// // // //       {maxVideos > 0 && (
// // // //         <Field label="Upload Videos (Optional)">
// // // //           <input type="file" className={styles.fileInput}
// // // //             accept="video/mp4,video/webm,video/ogg,video/quicktime" multiple onChange={handleVideos} />
// // // //           <p className={styles.helperText}>🎥 Max {maxVideos} videos, 50MB each</p>
// // // //           {videos.length > 0 && <p className={styles.selectedFiles}>✅ {videos.length}/{maxVideos} selected</p>}
// // // //         </Field>
// // // //       )}

// // // //       <Field label="Upload Images *">
// // // //         <input type="file" className={styles.fileInput} accept="image/*" multiple onChange={handleImages} />
// // // //         <p className={styles.helperText}>📷 Max {maxImages} images ({planName} plan)</p>
// // // //         {images.length > 0 && <p className={styles.selectedFiles}>✅ {images.length}/{maxImages} selected</p>}
// // // //       </Field>

// // // //       {maxVideos === 0 && (
// // // //         <div className={styles.upgradePrompt}>
// // // //           <p>🎥 Want to add videos? Upgrade to <strong>Business Plan</strong> for up to 2 videos!</p>
// // // //         </div>
// // // //       )}

// // // //       <button type="submit" className={styles.submitAdBtn}>Post Ad</button>
// // // //     </form>
// // // //   );
// // // // };

// // // // // ══════════════════════════════════════════════════════════════════════════════
// // // // // MAIN MODAL
// // // // // ══════════════════════════════════════════════════════════════════════════════
// // // // const PostAdModal = ({ isOpen, onClose, onAdPosted, selectedPlan }) => {
// // // //   const { isAuthenticated, getToken } = useAuthContext();
// // // //   const [chosenCategory, setChosenCategory] = useState(null);
  
// // // //   // ✅ NEW: Promotion state
// // // //   const [promotion, setPromotion] = useState(null);
// // // //   const [promotionLoading, setPromotionLoading] = useState(false);
// // // //   const [finalPrice, setFinalPrice] = useState(null);

// // // //   // ✅ NEW: Check for promotion when modal opens or plan changes
// // // //   useEffect(() => {
// // // //     if (isOpen && selectedPlan && isAuthenticated) {
// // // //       checkPromotion();
// // // //     }
// // // //   }, [isOpen, selectedPlan, isAuthenticated]);

// // // //   // ✅ NEW: Check user promotion
// // // //   const checkPromotion = async () => {
// // // //     if (!selectedPlan) return;
    
// // // //     setPromotionLoading(true);
// // // //     try {
// // // //       const planMap = {
// // // //         'free': '',
// // // //         'basic': 'basic',
// // // //         'business': 'business'
// // // //       };
// // // //       const planParam = planMap[selectedPlan.planId] || '';
      
// // // //       const promo = await checkUserPromotion(planParam);
      
// // // //       if (promo.has_promotion && promo.promotion) {
// // // //         setPromotion(promo.promotion);
        
// // // //         // Calculate final price
// // // //         const originalPrice = selectedPlan.price || 0;
// // // //         const pricingInfo = calculatePromotionPrice(
// // // //           originalPrice, 
// // // //           promo.promotion, 
// // // //           planParam
// // // //         );
        
// // // //         setFinalPrice(pricingInfo);
        
// // // //         console.log('✅ Promotion applied:', pricingInfo);
// // // //       } else {
// // // //         setPromotion(null);
// // // //         setFinalPrice(null);
// // // //       }
// // // //     } catch (error) {
// // // //       console.error('Promotion check failed:', error);
// // // //       setPromotion(null);
// // // //       setFinalPrice(null);
// // // //     } finally {
// // // //       setPromotionLoading(false);
// // // //     }
// // // //   };

// // // //   if (!isOpen) return null;

// // // //   const maxImages  = selectedPlan?.maxImages || 2;
// // // //   const maxVideos  = selectedPlan?.maxVideos || 0;
// // // //   const planName   = selectedPlan?.planName  || 'Free';
// // // //   const isBusiness = selectedPlan?.planId === 'business';
  
// // // //   // ✅ NEW: Use promotional price if available
// // // //   const displayPrice = finalPrice?.isFree 
// // // //     ? 0 
// // // //     : finalPrice?.finalPrice ?? selectedPlan?.price ?? 0;

// // // //   const handleClose = () => { 
// // // //     setChosenCategory(null); 
// // // //     setPromotion(null);
// // // //     setFinalPrice(null);
// // // //     onClose(); 
// // // //   };

// // // //   const handleSubmit = async (formData) => {
// // // //     if (!isAuthenticated) return alert('Please log in to post an ad');
// // // //     if (selectedPlan) {
// // // //       formData.append('plan_id',   selectedPlan.planId   || '');
// // // //       formData.append('plan_name', selectedPlan.planName || 'Free');
// // // //     }
// // // //     try {
// // // //       const response = await apiService.postAd(formData, getToken());
// // // //       if (response.ok) {
// // // //         // ✅ NEW: Use promotion slot after successful post
// // // //         if (promotion && selectedPlan) {
// // // //           const planMap = {
// // // //             'free': '',
// // // //             'basic': 'basic',
// // // //             'business': 'business'
// // // //           };
// // // //           const planParam = planMap[selectedPlan.planId] || '';
// // // //           if (planParam) {
// // // //             await consumePromotionSlot(planParam);
// // // //             console.log('✅ Promotion slot used');
// // // //           }
// // // //         }
        
// // // //         alert('Ad posted successfully!');
// // // //         handleClose();
// // // //         onAdPosted();
// // // //       } else {
// // // //         const err = await response.json();
// // // //         alert(err.error || 'Failed to post ad. Please try again.');
// // // //       }
// // // //     } catch (err) {
// // // //       console.error(err);
// // // //       alert('Network error. Please try again.');
// // // //     }
// // // //   };

// // // //   const titles = {
// // // //     [ROOMS_CAT]:      { title: 'Post Your Listing',  sub: 'Fill in the details to list your room' },
// // // //     [LOST_FOUND_CAT]: { title: 'Post Lost & Found',  sub: 'Fill in the details to report the item' },
// // // //     default:          { title: 'Post Your Ad',        sub: 'Fill in the details to list your item' },
// // // //   };
// // // //   const { title, sub } = titles[chosenCategory] || titles.default;

// // // //   return (
// // // //     <div className={styles.modalOverlay} onClick={handleClose}>
// // // //       <div className={styles.postAdModal} onClick={e => e.stopPropagation()}>
// // // //         <button className={styles.closeBtn} onClick={handleClose}>×</button>
// // // //         <h2 className={styles.modalTitle}>{title}</h2>
// // // //         <p className={styles.modalSubtitle}>{sub}</p>

// // // //         {/* ── Plan banner ── */}
// // // //         {selectedPlan && (
// // // //           <div className={`${styles.selectedPlanBanner} ${isBusiness ? styles.selectedPlanBannerBusiness : ''}`}>
// // // //             <div className={styles.planInfo}>
// // // //               <span className={`${styles.planBadge} ${isBusiness ? styles.planBadgeBusiness : ''}`}>
// // // //                 {isBusiness ? '⭐ ' : ''}{planName}
// // // //               </span>
// // // //               <div className={styles.planLimits}>
// // // //                 <span>📷 Up to {maxImages} images</span>
// // // //                 {maxVideos > 0 && <span>🎥 Up to {maxVideos} videos</span>}
// // // //                 {selectedPlan.displayDays && <span>⏱️ {selectedPlan.displayDays} days</span>}
// // // //               </div>
// // // //             </div>
            
// // // //             {/* ✅ NEW: Show promotional or regular price */}
// // // //             {displayPrice > 0 ? (
// // // //               <div className={`${styles.planPrice} ${isBusiness ? styles.planPriceBusiness : ''}`}>
// // // //                 {finalPrice && finalPrice.discountAmount > 0 && (
// // // //                   <div className={styles.originalPrice}>
// // // //                     <del>KES {selectedPlan.price}</del>
// // // //                   </div>
// // // //                 )}
// // // //                 <div>KES {displayPrice}</div>
// // // //               </div>
// // // //             ) : displayPrice === 0 && finalPrice?.isFree ? (
// // // //               <div className={`${styles.planPrice} ${styles.planPriceFree}`}>
// // // //                 FREE
// // // //               </div>
// // // //             ) : null}
// // // //           </div>
// // // //         )}

// // // //         {/* ✅ NEW: Promotion label */}
// // // //         {finalPrice && finalPrice.label && (
// // // //           <div className={styles.promotionBanner}>
// // // //             {finalPrice.label}
// // // //           </div>
// // // //         )}

// // // //         {/* ── Business slot notice ── */}
// // // //         {isBusiness && (
// // // //           <div className={styles.slotBanner}>
// // // //             🔄 <strong>Slot-based plan:</strong> Pay once &amp; post freely — delete and re-post
// // // //             your ad any time within the 3-month window at no extra charge.
// // // //           </div>
// // // //         )}

// // // //         {/* ── Category picker ── */}
// // // //         {chosenCategory === null && (
// // // //           <div className={styles.categoryPicker}>
// // // //             <div className={styles.formGroup}>
// // // //               <label>Category <span className={styles.req}>*</span></label>
// // // //               <select
// // // //                 className={styles.inputField}
// // // //                 defaultValue=""
// // // //                 onChange={e => { if (e.target.value) setChosenCategory(e.target.value); }}
// // // //               >
// // // //                 <option value="" disabled>Select a category...</option>
// // // //                 {CATEGORIES.filter(c => c !== 'All').map(c => (
// // // //                   <option key={c} value={c}>{c}</option>
// // // //                 ))}
// // // //               </select>
// // // //             </div>
// // // //           </div>
// // // //         )}

// // // //         {chosenCategory === ROOMS_CAT && (
// // // //           <RoomsForm maxImages={maxImages} maxVideos={maxVideos} planName={planName} onSubmit={handleSubmit} />
// // // //         )}
// // // //         {chosenCategory === LOST_FOUND_CAT && (
// // // //           <LostFoundForm maxVideos={maxVideos} planName={planName} onSubmit={handleSubmit} />
// // // //         )}
// // // //         {chosenCategory !== null && chosenCategory !== ROOMS_CAT && chosenCategory !== LOST_FOUND_CAT && (
// // // //           <StandardForm initialCategory={chosenCategory} maxImages={maxImages}
// // // //             maxVideos={maxVideos} planName={planName} onSubmit={handleSubmit} />
// // // //         )}

// // // //         {chosenCategory !== null && (
// // // //           <button className={styles.backBtn} onClick={() => setChosenCategory(null)}>
// // // //             ← Change category
// // // //           </button>
// // // //         )}
// // // //       </div>
// // // //     </div>
// // // //   );
// // // // };

// // // // export default PostAdModal;


